import DataFaturaFaturado from '../components/DataFaturaFaturado';
import { carregarSugestoesPendentes as carregarEfsImportacao } from '../utils/atualizacaoSugestoesEf';
import ResultadosDigitalizacao from '../components/ResultadosDigitalizacao';
import { idsLeituraPendente, carregarSugestoesPendentes, fundirSugestoes } from '../utils/atualizacaoSugestoesEf';
import AppIcon from '../components/AppIcon';
import { useEncomendasCliente, EncomendaCliente } from '../components/EncomendasCliente';
import SugestoesEf from '../components/SugestoesEf';
import FaturacaoModulo from '../components/FaturacaoModulo';
import { EditorRascunho } from '../components/FaturacaoRascunhos';
import LiquidText from '../components/LiquidText';
import DeleteIcon from '../components/DeleteIcon';
import { OrcamentoIcon } from '../components/OrcamentoUI';
import { LiquidDialog, useLiquidFeedback } from '../components/LiquidUI';
import React, { useState, useEffect, useCallback, useRef, useMemo, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { fmtMoney } from '../utils/moeda';
import './GestaoFinanceira.css';

/* Exceção documentada de pronto pagamento: o histórico conserva o dinheiro pago. */
function descontoLiquidacao(f) {
    const ajuste = f.metadata?.liquidacao_pronto_pagamento;
    if (!ajuste || !f.pago || f.valor_pago != null) return 0;
    const pagoReal = (f.pagamentos_historico || []).reduce((s, p) => s + (parseFloat(p.valor) || 0), 0);
    const desconto = Number(ajuste.valor_desconto) || 0;
    return desconto > 0 && Math.abs(pagoReal - Number(ajuste.valor_pago_real)) < .005
        && Math.abs(Number(f.valor_total) - Number(ajuste.valor_documento)) < .005
        && Math.abs(pagoReal + desconto - Number(f.valor_total)) < .005 ? desconto : 0;
}

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-PT') : '';

// Constrói URL para ficheiro de fatura via API (sempre proxied, não vai para React Router)
const buildDocUrl = (faturaId) => {
    if (!faturaId) return null;
    const token = localStorage.getItem('token') || '';
    const base = api.defaults.baseURL || '/api';
    return `${base}/gestao-financeira/documento/${faturaId}?token=${encodeURIComponent(token)}`;
};

// Mantido para compatibilidade com outros usos estáticos
const buildFileUrl = (caminho) => {
    if (!caminho) return null;
    if (caminho.startsWith('http')) return caminho;
    const base = api.defaults.baseURL || '';
    if (base.startsWith('http')) return base.replace(/\/api\/?$/, '') + caminho;
    return window.location.origin + caminho;
};

const TABS_GF_LEFT = [
    { key: 'scanner',    label: 'Ler Fatura' },
    { key: 'enc_forn',   label: 'Encomendas Fornecedor' },
    { key: 'fat_forn',   label: 'Faturas Fornecedor' },
];
const TABS_GF_RIGHT = [
    { key: 'faturar',    label: 'Para Faturar' },
    { key: 'faturados',  label: 'Faturados' },
    { key: 'faturacao', label: 'Faturação' },
];

/* ============================================================
   EXCEL-STYLE COLUMN FILTER (partilhado)
   ============================================================ */
function ColFilter({ label, field, data, colFilters, setColFilters, sortCol, setSortCol, sortDir, setSortDir }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const uniqueVals = useMemo(() => {
        const vals = new Set(data.map(o => {
            const v = field === 'margem'
                ? String((parseFloat(o.total_venda||0) - parseFloat(o.total_compra||0)).toFixed(2))
                : String(o[field] || '');
            return v;
        }));
        return [...vals].filter(Boolean).sort((a, b) => a.localeCompare(b, 'pt'));
    }, [data, field]);

    const selected = colFilters[field] || new Set();
    const allSelected = selected.size === 0;
    const filteredVals = uniqueVals.filter(v => v.toLowerCase().includes(search.toLowerCase()));

    const toggle = (val) => {
        setColFilters(prev => {
            const cur = new Set(prev[field] || []);
            if (cur.has(val)) cur.delete(val); else cur.add(val);
            if (cur.size === 0 || cur.size === uniqueVals.length) {
                const next = { ...prev }; delete next[field]; return next;
            }
            return { ...prev, [field]: cur };
        });
        // Fica aberto: escolher clientes é escolher vários, e fechar a cada
        // clique obrigava a reabrir a lista para cada nome. A pesquisa também
        // se mantém, para não perder o filtro a meio de uma escolha.
    };

    const clearFilter = () => {
        setColFilters(prev => { const next = { ...prev }; delete next[field]; return next; });
        setSearch('');
    };

    const handleSort = (dir) => { setSortCol(field); setSortDir(dir); setOpen(false); };

    const isFiltered = !allSelected;
    const isSorted = sortCol === field;

    return (
        <div className="col-filter-wrap" ref={ref}>
            <button
                className={`col-filter-btn${isFiltered ? ' filtered' : ''}${isSorted ? ' sorted' : ''}`}
                onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
                title={isFiltered ? 'Filtro activo' : 'Filtrar'}
            >
                <LiquidText>{label}</LiquidText>
                <span className="col-filter-arrow">{isFiltered ? '▼' : isSorted ? (sortDir === 'asc' ? '↑' : '↓') : '⌄'}</span>
            </button>
            {open && (
                <div className="col-filter-dropdown" onClick={e => e.stopPropagation()}>
                    <div className="col-filter-sort">
                        <button onClick={() => handleSort('asc')} className={`col-filter-sort-btn${sortCol===field&&sortDir==='asc'?' active':''}`}>↑ Crescente</button>
                        <button onClick={() => handleSort('desc')} className={`col-filter-sort-btn${sortCol===field&&sortDir==='desc'?' active':''}`}>↓ Decrescente</button>
                    </div>
                    <input className="col-filter-search" autoFocus placeholder="Pesquisar..." value={search} onChange={e => setSearch(e.target.value)} />
                    <div className="col-filter-list">
                        <label className="col-filter-item">
                            <input type="checkbox" checked={allSelected} onChange={clearFilter} />
                            <span>(Todos)</span>
                        </label>
                        {filteredVals.map(v => (
                            <label key={v} className="col-filter-item">
                                <input type="checkbox" checked={selected.has(v)} onChange={() => toggle(v)} />
                                <span>{v || '(vazio)'}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ============================================================
   TAB: SCANNER / LEITURA DE FATURAS
   ============================================================ */
/* O aviso de duplicação aparece em dois sítios: ao ler uma fatura e ao gravá-la
   na listagem. Estava escrito à mão só num deles, e o outro chamava um
   setAvisoDup que não existia no seu âmbito — rebentava com "setAvisoDup is not
   defined" exatamente quando havia uma duplicação para assinalar. */
/* Nome curto do fornecedor: o bastante para o reconhecer, sem a designação
   social atrás. "Transgrua - Transportes, Representações e Aluguer..." fica
   "Transgrua". Corri isto contra os 107 fornecedores em sistema antes de o
   fixar; o único cuidado é a primeira palavra não chegar (o "2800" de "2800
   Print"), e aí leva a seguinte atrás. */
const nomeCurtoFornecedor = (nome) => {
    const inteiro = String(nome || '').trim();
    const base = inteiro.split(/\s[-\u2013\u2014]\s|,/)[0].trim();
    if (!base) return inteiro;
    const limpar = (t) => t.replace(/[\s,;:\-\u2013\u2014]+$/, '');
    const palavras = base.split(/\s+/);
    if (palavras.length > 1 && (/^\d+$/.test(palavras[0]) || palavras[0].length <= 3)) {
        return limpar(`${palavras[0]} ${palavras[1]}`);
    }
    return limpar(palavras[0]) || inteiro;
};

/* Duplicação exata: a decisão é do utilizador e NADA vai para a base de dados
   enquanto ele não responder. Antes gravava-se primeiro e avisava-se depois, o
   que o obrigava a ir apagar à mão o registo que ele nunca quis criar. */
function ConfirmarDuplicacao({ aviso, onLancar, onApagar }) {
    const numero = aviso?.existente?.numero || '—';
    // "fatura" ou "nota de crédito" — ambas femininas, por isso a frase concorda
    // nos dois casos sem ter de mudar mais nada.
    const especie = aviso?.existente?.especie || 'fatura';
    // A abreviatura da ficha do fornecedor manda; a regra automática só entra
    // quando a fatura não chega a nenhuma ficha (13 em 1264, à data).
    const fornecedor = aviso?.existente?.abreviatura
        || nomeCurtoFornecedor(aviso?.existente?.fornecedor)
        || 'este fornecedor';
    return (
        <LiquidDialog className="gf-dup-overlay" role="alertdialog" aria-modal="true"
            aria-labelledby="gf-dup-titulo" aria-describedby="gf-dup-texto">
            <div className="gf-dup-caixa" onClick={e => e.stopPropagation()}>
                <div className="gf-dup-titulo" id="gf-dup-titulo">
                    <span className="gf-dup-sinal" aria-hidden="true"><LiquidText>⛔</LiquidText></span>
                    <span className="gf-dup-titulo-texto">DUPLICAÇÃO DETETADA</span>
                    <span className="gf-dup-sinal" aria-hidden="true"><LiquidText>⛔</LiquidText></span>
                </div>
                <p className="gf-dup-texto" id="gf-dup-texto">
                    A {especie} n.º <strong>{numero}</strong> da "<strong>{fornecedor}</strong>" já está lançada em sistema.
                </p>
                <div className="gf-dup-botoes">
                    <button type="button" className="gf-dup-btn gf-dup-apagar" onClick={onApagar}><DeleteIcon /> Apagar</button>
                    <button type="button" className="gf-dup-btn gf-dup-lancar" onClick={onLancar}>
                        Lançar mesmo assim
                    </button>
                </div>
            </div>
        </LiquidDialog>
    );
}

function AvisoDuplicacao({ aviso, onFechar }) {
    if (!aviso) return null;
    const titulo = aviso.nivel === 'duplicado' ? 'Duplicação detetada'
        : aviso.nivel === 'provavel' ? 'Provável duplicação'
        : 'Vale a pena confirmar';
    const icone = aviso.nivel === 'duplicado' ? '⛔' : aviso.nivel === 'provavel' ? '⚠️' : '❓';
    return (
        <div className={`gf-aviso-dup nivel-${aviso.nivel}`}>
            <span className="gf-aviso-dup-icone"><LiquidText>{icone}</LiquidText></span>
            <div className="gf-aviso-dup-texto">
                <strong><LiquidText>{titulo}</LiquidText></strong>
                <div>{aviso.mensagem}</div>
                <div className="gf-aviso-dup-nota">
                    A fatura <strong>foi gravada</strong>. Se for mesmo repetida, apague-a; se for
                    legítima — por exemplo a mesma fatura lançada em linhas de IVA separadas — ignore este aviso.
                </div>
            </div>
            <button className="gf-aviso-dup-x" onClick={onFechar} title="Fechar"><OrcamentoIcon name="close" fallback="×" /></button>
        </div>
    );
}

const consultarEfsImportacao = ids => carregarEfsImportacao(api, ids);

function TabScanner({ onAbrirFaturas }) {
    const liquidFeedback = useLiquidFeedback();
    const navigate = useNavigate();
    const atcudRef = useRef();
    const [atcudRaw, setAtcudRaw] = useState('');
    const [valorManual, setValorManual] = useState('');
    const [nifManual, setNifManual] = useState('');
    const [loading, setLoading] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [erro, setErro] = useState('');
    const [infoMsg, setInfoMsg] = useState('');
    // Aviso de possível duplicação. Não impede o lançamento — há faturas do
    // mesmo fornecedor com o mesmo número que são legítimas (as de várias
    // taxas de IVA lançadas em linhas separadas). Fica visível até ser fechado
    // à mão, para não passar despercebido entre gravações.
    const [avisoDup, setAvisoDup] = useState(null);
    const [ocrText, setOcrText] = useState('');
    const [modoEntrada, setModoEntrada] = useState('bulk'); // 'pistola' | 'bulk'
    const [modalNovaFatura, setModalNovaFatura] = useState(false);
    const [bulkFiles, setBulkFiles] = useState([]);
    const [bulkResults, setBulkResults] = useState([]);
    const [bulkProcessing, setBulkProcessing] = useState(false);
    const [lastBulkImport, setLastBulkImport] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [modalEFScanner, setModalEFScanner] = useState(null); // { ef, atcud, numDoc, dataDoc }
    const [queryEFScanner, setQueryEFScanner] = useState('');
    const [resultadosEFScanner, setResultadosEFScanner] = useState([]);
    const [efSelecionadaScanner, setEfSelecionadaScanner] = useState(null);
    const [efsSelectedScanner, setEfsSelectedScanner] = useState([]); // multi-select manual
    const [showCriarEFScanner, setShowCriarEFScanner] = useState(false);
    const [criarScannerOrc, setCriarScannerOrc] = useState('');
    const [criarScannerForn, setCriarScannerForn] = useState('');
    const [criarScannerValor, setCriarScannerValor] = useState('');
    const [criarScannerDesc, setCriarScannerDesc] = useState('');
    const [criarScannerFatNum, setCriarScannerFatNum] = useState('');
    const [criarScannerFatData, setCriarScannerFatData] = useState('');
    const [criarScannerTaxaIva, setCriarScannerTaxaIva] = useState(23);
    const [criandoEFScanner, setCriandoEFScanner] = useState(false);

    // Foco automático no campo ATCUD ao carregar (para pistola)
    useEffect(() => {
        if (modoEntrada === 'pistola' && atcudRef.current) atcudRef.current.focus();
    }, [modoEntrada]);

    // Carregar último bulk import + pendentes ao entrar no modo bulk
    const [bulkPendentes, setBulkPendentes] = useState([]);
    const [loadingPendentes, setLoadingPendentes] = useState(false);
    useEffect(() => {
        if (modoEntrada !== 'bulk') return;
        api.get('/gestao-financeira/bulk-last-import')
            .then(r => { if (r.data) setLastBulkImport(r.data); })
            .catch(() => {});
        setLoadingPendentes(true);
        api.get('/gestao-financeira/bulk-pendentes')
            .then(r => setBulkPendentes(r.data || []))
            .catch(() => setBulkPendentes([]))
            .finally(() => setLoadingPendentes(false));
    }, [modoEntrada]);

    // Detecção automática: quando pistola lê e carrega Enter, disparar pesquisa
    const handleAtcudKeyDown = (e) => {
        if ((e.key === 'Enter' || e.keyCode === 13) && atcudRaw.trim()) {
            e.preventDefault();
            handlePesquisar(atcudRaw);
            if (modoEntrada === 'pistola') setAtcudRaw('');
        }
    };

    // Auto-pesquisa se detectar padrão de QR Code completo (vários * e :)
    useEffect(() => {
        if (modoEntrada === 'pistola' && atcudRaw.includes('*') && atcudRaw.includes(':') && atcudRaw.length > 30) {
            const currentVal = atcudRaw;
            // Pequeno delay para garantir que o scanner terminou de escrever tudo
            const timer = setTimeout(() => {
                handlePesquisar(currentVal);
                setAtcudRaw('');
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [atcudRaw, modoEntrada]);

    // EF search no scanner (debounced)
    useEffect(() => {
        if (!queryEFScanner || queryEFScanner.trim().length < 2) { setResultadosEFScanner([]); return; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get(`/gestao-financeira/search-ef?q=${encodeURIComponent(queryEFScanner.trim())}`);
                setResultadosEFScanner(r.data);
            } catch { setResultadosEFScanner([]); }
        }, 300);
        return () => clearTimeout(t);
    }, [queryEFScanner]);

    // O digitalizador parte faturas de várias páginas em ficheiros soltos
    // ("... (página 2).jpg", "..._pagina03.jpg"). As páginas além da primeira
    // não têm QR: processadas à parte viravam registos vazios para limpar à
    // mão. Aqui juntam-se pelo nome — mesmo prefixo, só o número da página a
    // variar — e cada grupo segue numa única chamada, de onde sai UM documento.
    // O separador antes de "página" é obrigatório para "Campagina" não contar.
    const RE_PAGINA_NOME = /^(.*?)(?:[\s_.-]+\(?|\()\s*p[áa]g(?:ina)?[\s_.-]*(\d{1,3})\s*\)?$/i;
    const partirNomePagina = (nome) => {
        const ponto = nome.lastIndexOf('.');
        const base = ponto > 0 ? nome.slice(0, ponto) : nome;
        const m = base.match(RE_PAGINA_NOME);
        if (!m || !m[1].trim()) return null;
        const ext = ponto > 0 ? nome.slice(ponto).toLowerCase() : '';
        return { chave: `${m[1].trim().toLowerCase()}|${ext}`, pagina: parseInt(m[2], 10), prefixo: m[1].trim() };
    };

    const agruparBulk = (files) => {
        const grupos = [], porChave = new Map();
        for (const f of files) {
            const p = partirNomePagina(f.name);
            if (!p) { grupos.push({ rotulo: f.name, files: [f] }); continue; }
            let g = porChave.get(p.chave);
            if (!g) { g = { rotulo: p.prefixo, files: [], paginas: [] }; porChave.set(p.chave, g); grupos.push(g); }
            g.files.push(f); g.paginas.push(p.pagina);
        }
        for (const g of grupos) {
            if (!g.paginas) continue;
            if (g.files.length === 1) { g.rotulo = g.files[0].name; continue; }
            const ordenado = g.files.map((f, i) => [g.paginas[i], f]).sort((a, b) => a[0] - b[0]);
            g.files = ordenado.map(x => x[1]);
            g.rotulo = `${g.rotulo} (${g.files.length} páginas)`;
        }
        return grupos;
    };

    const receberBulk = (files) => {
        if (files.length === 0 || bulkProcessing || undoBulkLock.current) return;
        setModoEntrada('bulk');
        const grupos = agruparBulk(files);
        setBulkFiles(grupos);
        setBulkResults(grupos.map(g => ({ filename: g.rotulo, status: 'pending', message: 'Aguarda processamento' })));
    };

    // Manipuladores de Drag & Drop para Bulk
    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = () => { setIsDragging(false); };
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        receberBulk(Array.from(e.dataTransfer.files));
    };

    const handleBulkUpload = (e) => {
        receberBulk(Array.from(e.target.files));
    };

    const processBulk = async () => {
        if (bulkFiles.length === 0 || bulkProcessing || undoBulkLock.current) return;
        setBulkProcessing(true);
        const criadoEm = new Date().toISOString();
        const allResults = [];

        for (let i = 0; i < bulkFiles.length; i++) {
            const grupo = bulkFiles[i];
            // Marcar documento atual como "a processar"
            setBulkResults(prev => prev.map((item, idx) =>
                idx === i ? { ...item, status: 'processing', message: 'A processar...' } : item
            ));
            try {
                const formData = new FormData();
                grupo.files.forEach(f => formData.append('faturas', f));
                if (grupo.files.length > 1) {
                    // páginas da mesma fatura: o backend lê o QR na que o tem
                    // e junta-as num único PDF, em vez de N registos soltos
                    formData.append('grupo_paginas', '1');
                    formData.append('grupo_nome', grupo.rotulo);
                }
                formData.append('criado_em_cliente', criadoEm);
                const r = await api.post('/gestao-financeira/bulk-process', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    timeout: 90000,
                });
                const result = r.data.results?.[0] || { filename: grupo.rotulo, status: 'erro', message: 'Sem resultado' };
                allResults.push(result);
                setBulkResults(prev => prev.map((item, idx) => idx === i ? result : item));
            } catch (err) {
                const detail = err.response?.data?.error || err.message || 'Erro desconhecido';
                const errResult = { filename: grupo.rotulo, status: 'erro', message: 'Erro: ' + detail };
                allResults.push(errResult);
                setBulkResults(prev => prev.map((item, idx) => idx === i ? errResult : item));
            }
        }

        setBulkFiles([]);
        // Só disponibilizar Desfazer depois de identificar o lote efetivamente guardado.
        try {
            const {data} = await api.post('/gestao-financeira/bulk-save-log', { results: allResults });
            setLastBulkImport({id:data.id, timestamp:data.timestamp, results:allResults});
        } catch {
            setLastBulkImport(null);
            liquidFeedback.notify('Os documentos foram processados, mas não foi possível guardar o resumo. Atualize a página antes de desfazer a importação.');
        } finally {setBulkProcessing(false);}
    };

    const [undoingBulk, setUndoingBulk] = useState(false);
    const undoBulkLock = useRef(false);
    const handleUndoBulk = async (importData) => {
        if (!importData?.id || undoBulkLock.current || bulkProcessing) return;
        undoBulkLock.current = true; setUndoingBulk(true);
        try {
            if (!await liquidFeedback.confirm('Desfazer exclusivamente a última importação? As faturas novas desse lote serão eliminadas. As faturas já existentes e as respetivas digitalizações ficam intactas. Esta ação não pode ser revertida.')) return;
            const {data} = await api.post('/gestao-financeira/bulk-last-import/' + importData.id + '/desfazer');
            setLastBulkImport(null); setBulkResults([]);
            liquidFeedback.notify('Importação desfeita: ' + data.eliminados + ' registo(s) eliminado(s).');
        } catch (e) {
            liquidFeedback.notify(e.response?.data?.error || 'Não foi possível confirmar a reversão. Atualize o resumo antes de tentar novamente.');
        } finally {undoBulkLock.current=false;setUndoingBulk(false);}
    };

    const handlePesquisar = async (valOverride) => {
        const finalAtcud = valOverride || atcudRaw;
        setLoading(true); setErro(''); setInfoMsg(''); setResultado(null);
        try {
            const payload = {};
            if (finalAtcud.trim()) payload.atcud_raw = finalAtcud.trim();
            if (valorManual) payload.valor_total = parseFloat(valorManual.replace(',', '.'));
            if (nifManual) payload.nif_fornecedor = nifManual.trim();

            if (!payload.valor_total && !payload.atcud_raw) {
                setErro('Leia o código QR/ATCUD ou indique o valor total');
                setLoading(false);
                return;
            }

            const r = await api.post('/gestao-financeira/match-atcud', payload);
            // Guardar o atcud_raw no parsed para uso posterior (Dar Entrada)
            r.data.parsed.atcud_raw = payload.atcud_raw || '';
            // Mostrar apenas linhas que têm EF criada (ef_id != null)
            r.data.resultados = (r.data.resultados || []).filter(x => x.ef_id);
            setResultado(r.data);

            // SEGURANÇA: fatura já existe no sistema? → alerta e abortar fluxo
            if (r.data.duplicate) {
                const dup = r.data.duplicate;
                const efTxt = dup.ef_numero ? ` (associada a ${dup.ef_numero})` : '';
                setErro(`⚠️ Esta fatura já existe no sistema${efTxt}. Não será registada novamente.`);
                return;
            }

            // Verificar se algum resultado já tem fatura associada à EF
            const efComFatura = r.data.resultados?.find(x => x.ef_tem_fatura && x.ef_id);
            if (efComFatura) {
                setInfoMsg(`Esta fatura já está associada à ${efComFatura.ef_numero || 'EF'}`);
            }

            // NOTA: a fatura NÃO é registada automaticamente.
            // Só é criada quando o utilizador associa a uma EF no modal
            // (via /associar-ef-scanner). Se não houver associação, a fatura é descartada.
        } catch (e) {
            setErro(e.response?.data?.error || 'Erro na pesquisa');
        } finally { setLoading(false); }
    };

    const handleDarEntrada = async (r) => {
        try {
            const efRes = await api.get(`/gestao-financeira/encomendas-fornecedor/${r.ef_id}`);
            const parsed = resultado?.parsed || {};
            const hoje = new Date().toISOString().split('T')[0];
            setModalEFScanner({
                ef: efRes.data,
                atcud: parsed.atcud_raw || atcudRaw || '',
                numDoc: parsed.numDoc || '',
                dataDoc: parsed.dataDoc || hoje,
            });
        } catch (e) {
            liquidFeedback.notify('Erro ao carregar EF');
        }
    };

    const handleLimpar = () => {
        setAtcudRaw(''); setValorManual(''); setNifManual('');
        setResultado(null); setErro(''); setOcrText(''); setInfoMsg('');
        setQueryEFScanner(''); setResultadosEFScanner([]); setEfSelecionadaScanner(null); setEfsSelectedScanner([]);
        if (atcudRef.current) atcudRef.current.focus();
    };

    const handleAssociarEFScanner = async (ef) => {
        try {
            const efRes = await api.get(`/gestao-financeira/encomendas-fornecedor/${ef.id}`);
            const parsed = resultado?.parsed || {};
            const hoje = new Date().toISOString().split('T')[0];
            // Associação manual — sem atcud e sem pré-preenchimento do scan anterior
            // (o resultado foi limpo ao fazer foco no campo de pesquisa)
            setModalEFScanner({
                ef: efRes.data,
                atcud: '',
                numDoc: '',
                dataDoc: '',
            });
            setQueryEFScanner(''); setResultadosEFScanner([]); setEfSelecionadaScanner(null);
        } catch (e) {
            liquidFeedback.notify('Erro ao carregar EF');
        }
    };

    const handleDesassociarEFScanner = async (ef, e) => {
        e.stopPropagation();
        if (!await liquidFeedback.confirm(`Desassociar fatura da EF ${ef.numero}?`)) return;
        try {
            await api.delete(`/gestao-financeira/encomendas-fornecedor/${ef.id}/registar-fatura`);
            setResultadosEFScanner(prev => prev.map(x => x.id === ef.id ? { ...x, tem_fatura: false } : x));
            setEfSelecionadaScanner(prev => prev?.id === ef.id ? { ...prev, tem_fatura: false } : prev);
        } catch (err) {
            liquidFeedback.notify('Erro ao desassociar fatura');
        }
    };

    return (
        <div className="gf-scanner">
            {/* Modo de entrada */}
            <div className="gf-modo-tabs" style={{ display:'flex', alignItems:'center', gap:8 }}>
                {[
                    { key: 'pistola', label: 'Pistola / QR' },
                    { key: 'bulk',    label: 'Digitalização em Massa' },
                ].map(m => (
                    <button key={m.key} className={`gf-modo-btn ${modoEntrada === m.key ? 'active' : ''}`}
                        onClick={() => setModoEntrada(m.key)}>
                        <LiquidText>{m.label}</LiquidText>
                    </button>
                ))}
                {modoEntrada !== 'bulk' && <button
                    className="btn-gf-search"
                    style={{ marginLeft:8, whiteSpace:'nowrap' }}
                    onClick={() => setModalNovaFatura(true)}>
                    + Entrada Manual
                </button>}
            </div>

            {modalNovaFatura && (
                <ModalEditFatura
                    fatura={{ id: null, fornecedor: efsSelectedScanner[0]?.fornecedor || '', nif_emissor: efsSelectedScanner[0]?.nif_fornecedor || '', valor_total:'', numero_documento_completo:'', data_documento:'', taxa_iva:23, subtotal:'', valor_iva:'', estado_fatura:'pendente', caminho_foto:null, associated_efs:[], atcud:null }}
                    listaFaturas={[]}
                    onClose={() => setModalNovaFatura(false)}
                    onSaved={() => { setModalNovaFatura(false); setEfsSelectedScanner([]); setQueryEFScanner(''); setResultadosEFScanner([]); }}
                    isNew
                    initialPreEfs={efsSelectedScanner}
                />
            )}

            <div className="gf-input-area">
                {/* Modo Pistola / QR */}
                {modoEntrada === 'pistola' && (
                    <div className="gf-pistola">
                        <p className="gf-pistola-hint">
                            Aponte a pistola para o QR Code da fatura.<br />
                            O código será lido automaticamente e a pesquisa iniciará.
                        </p>
                        <div className="gf-pistola-row">
                            <button className="btn-gf-clear" onClick={handleLimpar}>Limpar</button>
                            <input
                                ref={atcudRef}
                                type="text"
                                className="gf-atcud-input"
                                value={atcudRaw}
                                onChange={e => setAtcudRaw(e.target.value)}
                                onKeyDown={handleAtcudKeyDown}
                                onFocus={e => e.target.select()}
                                placeholder="Aguardando leitura do código..."
                                autoFocus
                            />
                        </div>
                        <p className="gf-pistola-tip">O cursor deve estar neste campo durante a leitura</p>

                        {/* Pesquisa manual de EF */}
                        <div className="gf-scanner-ef-search">
                            <label className="gf-scanner-ef-label">Pesquisar EF para associar manualmente</label>
                            <input
                                type="text"
                                className="gef-ef-input"
                                placeholder="N.º EF, fornecedor, projeto ou NIF..."
                                value={queryEFScanner}
                                onChange={e => { setQueryEFScanner(e.target.value); setEfSelecionadaScanner(null); }}
                                onFocus={() => {
                                    // Limpar resultado do scan anterior ao entrar em modo manual
                                    if (resultado) {
                                        setResultado(null);
                                        setErro('');
                                        setInfoMsg('');
                                        setShowCriarEFScanner(false);
                                    }
                                    // Limpar seleção pistola ao usar manual
                                    setEfSelecionadaScanner(null);
                                }}
                            />
                            {/* Resultados da pesquisa — SEMPRE imediatamente abaixo do input para posição estável */}
                            {resultadosEFScanner.length > 0 && (
                                <div className="gef-ef-results">
                                    {resultadosEFScanner.map(ef => {
                                        const isSel = efsSelectedScanner.some(x => x.id === ef.id);
                                        return (
                                            <div key={ef.id}
                                                className={`gef-ef-item${isSel ? ' selected' : ''}${ef.tem_fatura ? ' gef-ef-item-associada' : ''}`}
                                                style={{cursor: ef.tem_fatura ? 'default' : 'pointer'}}
                                                onClick={() => {
                                                    if (ef.tem_fatura) return;
                                                    setEfsSelectedScanner(prev =>
                                                        isSel ? prev.filter(x => x.id !== ef.id) : [...prev, ef]
                                                    );
                                                }}>
                                                <input type="checkbox" checked={isSel}
                                                    style={{accentColor:'#2563eb', marginRight:6, flexShrink:0,
                                                        cursor: ef.tem_fatura ? 'default' : 'pointer'}}
                                                    onChange={() => {
                                                        if (ef.tem_fatura) return;
                                                        setEfsSelectedScanner(prev =>
                                                            isSel ? prev.filter(x => x.id !== ef.id) : [...prev, ef]
                                                        );
                                                    }}
                                                    onClick={e => e.stopPropagation()} />
                                                <span className="gef-ef-num">{ef.numero}</span>
                                                <span className="gef-ef-info">
                                                    {ef.fornecedor}
                                                    {ef.projeto && <span className="gef-ef-proj"> · {ef.projeto}</span>}
                                                    <span className="gef-ef-val"> · {fmtMoney(ef.valor_total)}</span>
                                                </span>
                                                {ef.tem_fatura && (
                                                    <span className="gf-badge badge-yellow"><OrcamentoIcon name="check" fallback="✓" /> ASSOCIADA</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {queryEFScanner.trim().length >= 2 && resultadosEFScanner.length === 0 && (
                                <p className="gef-ef-vazio">Nenhuma EF encontrada.</p>
                            )}
                            {/* EFs selecionadas + botão de registo — ABAIXO dos resultados */}
                            {efsSelectedScanner.length > 0 && (
                                <div className="gef-pre-ef-list" style={{marginTop:8}}>
                                    {efsSelectedScanner.map(ef => (
                                        <div key={ef.id} className="gef-pre-ef-item">
                                            <span className="gef-ef-num">{ef.numero}</span>
                                            <span className="gef-ef-info" style={{flex:1}}>
                                                {ef.fornecedor}
                                                {ef.projeto && <span className="gef-ef-proj"> · {ef.projeto}</span>}
                                                <span className="gef-ef-val"> · {fmtMoney(ef.valor_total)}</span>
                                            </span>
                                            <button type="button" className="gef-ef-assoc-remove"
                                                onClick={e => { e.stopPropagation(); setEfsSelectedScanner(prev => prev.filter(x => x.id !== ef.id)); }} aria-label="Apagar" title="Apagar"><DeleteIcon /></button>
                                        </div>
                                    ))}
                                    {efsSelectedScanner.length > 1 && (
                                        <div style={{textAlign:'right', fontSize:12, color:'#1e40af', fontWeight:700, padding:'2px 6px'}}>
                                            Total s/ IVA: {fmtMoney(efsSelectedScanner.reduce((s,e) => s + (parseFloat(e.valor_total)||0), 0))}
                                        </div>
                                    )}
                                    <button className="btn-gf-search" style={{width:'100%', marginTop:4}}
                                        onClick={() => setModalNovaFatura(true)}>
                                        <OrcamentoIcon name="check" fallback="✓" /> Registar fatura para {efsSelectedScanner.length} EF{efsSelectedScanner.length > 1 ? 's' : ''}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Modo Manual */}
                {modoEntrada === 'manual' && (
                    <div className="gf-manual-grid">
                        <div className="gf-field">
                            <label>ATCUD / Código QR (opcional)</label>
                            <input type="text" value={atcudRaw}
                                onChange={e => setAtcudRaw(e.target.value)}
                                placeholder="A:NIF*B:NIF*O:valor*..." />
                        </div>
                        <div className="gf-field">
                            <label>NIF do Fornecedor (opcional)</label>
                            <input type="text" value={nifManual}
                                onChange={e => setNifManual(e.target.value)}
                                placeholder="500000000" />
                        </div>
                        <div className="gf-field">
                            <label>Valor Total da Fatura (€)</label>
                            <input type="number" value={valorManual}
                                onChange={e => setValorManual(e.target.value)}
                                placeholder="0.00" step="0.01" min="0" />
                        </div>
                    </div>
                )}

                {/* Modo Bulk */}
                {modoEntrada === 'bulk' && (
                    <div className="gf-bulk">
                        <div
                            className={`gf-bulk-dropzone ${isDragging ? 'dragging' : ''}`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                        >
                            <input type="file" id="bulk-input" disabled={bulkProcessing} multiple accept="image/*,application/pdf"
                                onChange={handleBulkUpload} hidden />
                            <label htmlFor="bulk-input" className="gf-bulk-label">
                                <div className="gf-bulk-icon"><LiquidText>📂</LiquidText></div>
                                <strong>Clique ou arraste PDF/imagens do digitalizador</strong>
                                <p>{bulkProcessing ? 'Aguarde o fim do processamento para selecionar outro lote.' : 'Lê ATCUD automaticamente e cruza com faturas pendentes de digitalização'}</p>
                            </label>
                        </div>

                        {/* Última importação guardada (mostra quando não há resultados da sessão atual) */}
                        {bulkResults.length === 0 && lastBulkImport && (
                            <div className="bulk-last-import">
                                <div className="bulk-last-import-header">
                                    <button className="btn-bulk-undo" disabled={undoingBulk || bulkProcessing} onClick={() => handleUndoBulk(lastBulkImport)}><LiquidText>
                                        ↩ Desfazer importação
                                    </LiquidText></button>
                                    <div>
                                        <span className="bulk-last-import-title">Última importação</span>
                                        <span className="bulk-last-import-date">{new Date(lastBulkImport.timestamp).toLocaleString('pt-PT')}</span>
                                        <span className="bulk-last-import-count">{lastBulkImport.results.length} ficheiro(s)</span>
                                        {lastBulkImport.utilizador && <span className="bulk-last-import-user">por {lastBulkImport.utilizador}</span>}
                                    </div>
                                    <button className="btn-bulk-dismiss" onClick={async () => { if (lastBulkImport?.id) { try { await api.delete(`/gestao-financeira/bulk-last-import/${lastBulkImport.id}`); } catch {} } setLastBulkImport(null); }}>
                                        <OrcamentoIcon name="clear" fallback="✕" />
                                    </button>
                                </div>
                                <ResultadosDigitalizacao carregarEfs={consultarEfsImportacao} resultados={lastBulkImport.results} onAbrirFaturas={onAbrirFaturas} historico />
                            </div>
                        )}

                        {/* Faturas com digitalização pendente (de sessões anteriores) */}
                        {bulkPendentes.length > 0 && bulkResults.length === 0 && (
                            <div className="bulk-pendentes-wrap">
                                <div className="bulk-pendentes-header">
                                    <span className="bulk-pendentes-title">Digitalizações Pendentes</span><button type="button" className="btn-ver-orc" onClick={onAbrirFaturas}>Ir para Faturas Fornecedor</button>
                                    <span className="bulk-pendentes-count">{loadingPendentes ? '…' : `${bulkPendentes.length} fatura(s)`}</span>
                                </div>
                                <table className="gf-table standard-table">
                                    <thead>
                                        <tr>
                                            <th>Fornecedor</th><th>NIF</th><th>Valor</th><th>Nº Fatura</th><th>Criado em</th><th>EF</th><th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bulkPendentes.map(p => (
                                            <tr key={p.id} className="gf-row">
                                                <td>{p.fornecedor || '—'}</td>
                                                <td style={{fontSize:11,color:'#666'}}>{p.nif_emissor || '—'}</td>
                                                <td className="tr">{fmtMoney(p.valor_total)}</td>
                                                <td style={{fontSize:11}}>{p.numero_documento_completo || '—'}</td>
                                                <td style={{fontSize:11}}>{fmtDate(p.criado_em)}</td>
                                                <td style={{fontSize:11,color:'#555'}}>{p.ef_numero || '—'}</td>
                                                <td>
                                                    <span>Rever em Faturas Fornecedor</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {bulkResults.length > 0 && bulkFiles.length === 0 && !bulkProcessing && lastBulkImport?.id && <button type="button" className="btn-bulk-undo" disabled={undoingBulk} onClick={() => handleUndoBulk(lastBulkImport)}>{undoingBulk ? 'A desfazer…' : '↩ Desfazer importação'}</button>}
                        {bulkResults.length > 0 && <ResultadosDigitalizacao carregarEfs={consultarEfsImportacao} resultados={bulkResults} aProcessar={bulkProcessing} podeProcessar={bulkFiles.length > 0} onProcessar={processBulk} onAbrirFaturas={onAbrirFaturas} />}
                    </div>
                )}

                {modoEntrada === 'manual' && (
                    <div className="gf-actions-form">
                        <button className="btn-gf-search" onClick={handlePesquisar} disabled={loading}>
                            {loading ? 'A pesquisar...' : 'Associar Fatura a Orçamento'}
                        </button>
                        <button className="btn-gf-clear" onClick={handleLimpar}>Limpar</button>
                    </div>
                )}
            </div>

            {erro && <div className="gf-erro">{erro}</div>}
            {infoMsg && <div className="gf-info">{infoMsg}</div>}
            <AvisoDuplicacao aviso={avisoDup} onFechar={() => setAvisoDup(null)} />

            {modalEFScanner && (
                <ModalEF
                    ef={modalEFScanner.ef}
                    atcud={modalEFScanner.atcud}
                    initialNumero={modalEFScanner.numDoc}
                    initialData={modalEFScanner.dataDoc}
                    onClose={() => setModalEFScanner(null)}
                    onSaved={() => { setModalEFScanner(null); handleLimpar(); }}
                />
            )}

            {/* Resultados */}
            {resultado && (
                <div className="gf-resultados">
                    {/* Info da fatura analisada */}
                    <div className="gf-fatura-info" style={{display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8}}>
                        <div style={{display:'flex', gap:16, flexWrap:'wrap', alignItems:'center'}}>
                            <span><strong>NIF Emitente:</strong> {resultado.parsed.nif || '—'} {resultado.parsed.cleanNif && resultado.parsed.cleanNif !== resultado.parsed.nif && <small>({resultado.parsed.cleanNif})</small>}</span>
                            <span><strong>Fornecedor:</strong> {resultado.parsed.nomeFornecedor || <span className="txt-warn"><OrcamentoIcon name="warning" fallback="⚠️" /> Não reconhecido (pesquisa por valor)</span>}</span>
                            <span><strong>Valor:</strong> {resultado.parsed.valor ? fmtMoney(resultado.parsed.valor) : '—'}</span>
                            {resultado.parsed.numDoc && <span><strong>N.º Doc:</strong> {resultado.parsed.numDoc}</span>}
                        </div>
                        <button
                            title="Criar nova Encomenda Fornecedor com estes dados"
                            className="btn-gf-search"
                            style={{padding:'3px 12px', fontSize:18, fontWeight:700, lineHeight:1.2, flexShrink:0}}
                            onClick={() => {
                                const parsed = resultado.parsed;
                                // Tentar detectar taxa IVA e base a partir dos dados do QR
                                const taxaDetectada = parsed.taxaIva ?? 23;
                                const valorBruto = parseFloat(parsed.valor || 0);
                                // Se QR trouxe base (subtotal) usa-a; senão calcula a partir da taxa
                                const valorBase = parsed.subtotal
                                    ? parseFloat(parsed.subtotal)
                                    : (valorBruto > 0 && taxaDetectada > 0
                                        ? parseFloat((valorBruto / (1 + taxaDetectada / 100)).toFixed(2))
                                        : valorBruto);
                                setCriarScannerForn(parsed.nomeFornecedor || '');
                                setCriarScannerValor(valorBase > 0 ? valorBase.toFixed(2) : '');
                                setCriarScannerTaxaIva(taxaDetectada);
                                setCriarScannerFatNum(parsed.numDoc || '');
                                setCriarScannerFatData(parsed.dataDoc || '');
                                setCriarScannerOrc('');
                                setCriarScannerDesc('');
                                setShowCriarEFScanner(true);
                            }}>
                            +
                        </button>
                    </div>

                    {/* Modal criar EF a partir de scan */}
                    {showCriarEFScanner && (
                        <div style={{background:'#f0f9ff',border:'1px solid #bae6fd',borderRadius:8,padding:'14px',marginTop:10,display:'flex',flexDirection:'column',gap:10}}>
                            <strong style={{fontSize:13,color:'#0369a1'}}>Nova Encomenda Fornecedor (a partir do scan)</strong>
                            <div style={{display:'flex',gap:8,alignItems:'flex-end',flexWrap:'wrap'}}>
                                <div style={{display:'flex',flexDirection:'column',gap:3,flex:'0 0 110px'}}>
                                    <label style={{fontSize:11,fontWeight:700,color:'#555'}}>N.º Orçamento *</label>
                                    <input type="number" value={criarScannerOrc}
                                        onChange={e => setCriarScannerOrc(e.target.value)}
                                        placeholder="ex: 16491"
                                        autoFocus
                                        style={{padding:'5px 8px',border:'1px solid #cbd5e1',borderRadius:6,fontSize:13,width:'100%'}} />
                                </div>
                                <div style={{display:'flex',flexDirection:'column',gap:3,flex:'1 1 160px'}}>
                                    <label style={{fontSize:11,fontWeight:700,color:'#555'}}>Fornecedor *</label>
                                    <input type="text" value={criarScannerForn}
                                        onChange={e => setCriarScannerForn(e.target.value)}
                                        style={{padding:'5px 8px',border:'1px solid #cbd5e1',borderRadius:6,fontSize:13,width:'100%'}} />
                                </div>
                                <div style={{display:'flex',flexDirection:'column',gap:3,flex:'0 0 100px'}}>
                                    <label style={{fontSize:11,fontWeight:700,color:'#555'}}>Valor s/ IVA *</label>
                                    <input type="number" value={criarScannerValor} step="0.01"
                                        onChange={e => setCriarScannerValor(e.target.value)}
                                        style={{padding:'5px 8px',border:'1px solid #cbd5e1',borderRadius:6,fontSize:13,width:'100%'}} />
                                </div>
                                <div style={{display:'flex',flexDirection:'column',gap:3,flex:'1 1 140px'}}>
                                    <label style={{fontSize:11,fontWeight:700,color:'#555'}}>Descrição</label>
                                    <input type="text" value={criarScannerDesc}
                                        onChange={e => setCriarScannerDesc(e.target.value)}
                                        placeholder="Opcional"
                                        style={{padding:'5px 8px',border:'1px solid #cbd5e1',borderRadius:6,fontSize:13,width:'100%'}} />
                                </div>
                                <div style={{display:'flex',flexDirection:'column',gap:3,flex:'0 0 140px'}}>
                                    <label style={{fontSize:11,fontWeight:700,color:'#555'}}>N.º Fatura Forn.</label>
                                    <input type="text" value={criarScannerFatNum}
                                        onChange={e => setCriarScannerFatNum(e.target.value)}
                                        style={{padding:'5px 8px',border:'1px solid #cbd5e1',borderRadius:6,fontSize:13,width:'100%'}} />
                                </div>
                                <div style={{display:'flex',flexDirection:'column',gap:3,flex:'0 0 130px'}}>
                                    <label style={{fontSize:11,fontWeight:700,color:'#555'}}>Data Fatura</label>
                                    <input type="date" value={criarScannerFatData}
                                        onChange={e => setCriarScannerFatData(e.target.value)}
                                        style={{padding:'5px 8px',border:'1px solid #cbd5e1',borderRadius:6,fontSize:13,width:'100%'}} />
                                </div>
                                <div style={{display:'flex',flexDirection:'column',gap:3,flex:'0 0 80px'}}>
                                    <label style={{fontSize:11,fontWeight:700,color:'#555'}}>IVA %</label>
                                    <select value={criarScannerTaxaIva}
                                        onChange={e => setCriarScannerTaxaIva(parseInt(e.target.value))}
                                        style={{padding:'5px 8px',border:'1px solid #cbd5e1',borderRadius:6,fontSize:13,width:'100%'}}>
                                        <option value={0}>0%</option>
                                        <option value={6}>6%</option>
                                        <option value={4}>4%</option>
                                        <option value={13}>13%</option>
                                        <option value={16}>16%</option>
                                        <option value={23}>23%</option>
                                    </select>
                                </div>
                            </div>
                            <div data-liquid-actions="true" style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                                <button className="btn-gf-clear" onClick={() => { setShowCriarEFScanner(false); }}>Cancelar</button>
                                <button className="btn-gf-search"
                                    onClick={async () => {
                                        if (!criarScannerOrc || !criarScannerForn || !criarScannerValor) return;
                                        setCriandoEFScanner(true);
                                        try {
                                            // Usar endpoint único que cria EF + fatura verificada (aguarda digitalização)
                                            await api.post('/gestao-financeira/criar-ef-com-fatura', {
                                                orcamento_id: parseInt(criarScannerOrc),
                                                fornecedor: criarScannerForn.trim(),
                                                valor_total: parseFloat(criarScannerValor),
                                                descricao: criarScannerDesc.trim() || null,
                                                taxa_iva: criarScannerTaxaIva,
                                                atcud_raw: resultado?.parsed?.atcud_raw || null,
                                                nif_emissor: resultado?.parsed?.nif || null,
                                                numero_fatura_fornecedor: criarScannerFatNum.trim() || null,
                                                data_fatura_fornecedor: criarScannerFatData || null,
                                            });
                                            setShowCriarEFScanner(false);
                                            setInfoMsg('✓ Encomenda criada e fatura registada — aguarda digitalização.');
                                        } catch (e) {
                                            setErro(e.response?.data?.error || e.message);
                                        } finally { setCriandoEFScanner(false); }
                                    }}
                                    disabled={criandoEFScanner || !criarScannerOrc || !criarScannerForn || !criarScannerValor}>
                                    {criandoEFScanner ? 'A criar...' : 'Criar Encomenda'}
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="gf-resultados-header-bulk">
                        <h3>Orçamentos correspondentes ({resultado.resultados.length})</h3>
                        <p className="gf-result-hint">orçamentos correspondentes similares</p>
                    </div>

                    {resultado.resultados.length === 0 ? (
                        <div className="gf-no-result">Nenhum orçamento encontrado com esses critérios</div>
                    ) : (
                        <table className="gf-table standard-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>ID Orç.</th>
                                    <th>Data</th>
                                    <th>Encomenda Fornecedor</th>
                                    <th>Fornecedor</th>
                                    <th>Projeto</th>
                                    <th>Linha</th>
                                    <th>Descrição</th>
                                    <th>Cliente</th>
                                    <th>Qtd</th>
                                    <th>V. Unit.</th>
                                    <th>Total</th>
                                    <th>Estado</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {resultado.resultados.map((r, i) => (
                                    <tr key={i} className={`gf-row gf-${r.match_tipo}`}>
                                        <td>
                                            <span className={`match-badge match-${r.match_tipo}`}>
                                                <LiquidText>{r.match_tipo === 'exato' ? '✓' : '~'}</LiquidText>
                                            </span>
                                        </td>
                                        <td className="gf-orc-id">#{r.orcamento_id}</td>
                                        <td>{fmtDate(r.data)}</td>
                                        <td>
                                            {r.ef_numero ? (
                                                <span className="gf-ef-tag" title="Correspondência via Encomenda"><LiquidText>
                                                    📦 </LiquidText>{r.ef_numero}
                                                </span>
                                            ) : (
                                                <span className="gf-line-tag">Linha {r.linha_num}</span>
                                            )}
                                            {r.ef_estado && (
                                                <span className={`gf-ef-estado ef-estado-${r.ef_estado.toLowerCase()}`}>
                                                    {r.ef_estado}
                                                </span>
                                            )}
                                        </td>
                                        <td>{r.fornecedor}</td>
                                        <td className="gf-proj">{r.projeto}</td>
                                        <td className="tc">{r.linha_num}</td>
                                        <td>{r.descricao}</td>
                                        <td>{r.cliente}</td>
                                        <td className="tc">{r.qtd}</td>
                                        <td className="tr">{r.valor_unitario ? fmtMoney(r.valor_unitario) : '—'}</td>
                                        <td className="tr"><strong>{fmtMoney(r.valor_total)}</strong></td>
                                        <td>{r.estado}</td>
                                        <td>
                                            <div className="gf-actions">
                                            {r.ef_id ? (
                                                r.ef_tem_fatura ? (
                                                    <span className="gf-badge badge-yellow" title="Fatura já registada nesta EF"><OrcamentoIcon name="check" fallback="✓" /> ASSOCIADA</span>
                                                ) : (
                                                    <button className="btn-assoc-ef"
                                                        onClick={() => handleDarEntrada(r)}>
                                                        Dar Entrada
                                                    </button>
                                                )
                                            ) : null}
                                            <button className="btn-ver-orc"
                                                onClick={() => navigate(`/orcamentos/${r.orcamento_id}`)}>
                                                Ver
                                            </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
}

/* ============================================================
   TAB: PARA FATURAR
   ============================================================ */
function TabParaFaturar({ onCountChange, onRascunho }) {
    const liquidFeedback = useLiquidFeedback();
    const navigate = useNavigate();
    const [lista, setLista] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortCol, setSortCol] = useState('id');
    const [sortDir, setSortDir] = useState('desc');
    const [colFilters, setColFilters] = useState({});
    const [procuraId, setProcuraId] = useState('');
    const [editando, setEditando] = useState({});   // { [id]: { numero_fatura } }
    const [criando, setCriando] = useState(null);
    const encomendas = useEncomendasCliente(setLista);
    const [gravacao, setGravacao] = useState('');
    const pendentes = useRef(new Map());
    const aGravar = useRef(null);
    const saida = useRef({ registos: [], enviados: false });
    saida.current.registos = lista.map(o => ({
        id: o.id,
        numero_fatura: editando[o.id]?.numero_fatura ?? o.numero_fatura_preparacao ?? o.numero_fatura ?? '',
        data_fatura: editando[o.id]?.data_fatura ?? o.data_fatura?.slice(0,10) ?? ''
    })).filter(r => r.numero_fatura.trim() || Object.prototype.hasOwnProperty.call(editando, r.id));
    const concluirAoSair = useCallback(() => {
        if (saida.current.enviados || !saida.current.registos.length) return;
        saida.current.enviados = true;
        const registos = saida.current.registos;
        const base = (api.defaults.baseURL || '/api').replace(/\/$/, '');
        const enviar = async () => {
            for (let i = 0; i < registos.length; i += 100) {
                const r = await window.fetch(base + '/gestao-financeira/para-faturar/concluir', {
                    method: 'POST', keepalive: true,
                    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (localStorage.getItem('token') || '') },
                    body: JSON.stringify({ registos: registos.slice(i, i + 100) })
                });
                if (!r.ok) throw new Error('Não foi possível concluir a gravação.');
            }
            window.dispatchEvent(new Event('gf-faturados-atualizados'));
        };
        enviar().catch(() => {
            saida.current.enviados = false;
            window.alert('Não foi possível concluir a gravação de saída. Volte a Para Faturar para verificar os dados.');
        });
    }, []);
    const gravarPendentes = useCallback(() => {
        if (aGravar.current) return aGravar.current;
        if (!pendentes.current.size) return Promise.resolve(true);
        setGravacao('A guardar…');
        const tarefa = (async () => {
            try {
                while (pendentes.current.size) {
                    const [id, dados] = pendentes.current.entries().next().value;
                    await api.patch('/gestao-financeira/para-faturar/' + id + '/rascunho', dados);
                    setLista(prev => prev.map(o => o.id === id ? { ...o, numero_fatura_preparacao: dados.numero_fatura, data_fatura: dados.data_fatura } : o));
                    if (pendentes.current.get(id) === dados) pendentes.current.delete(id);
                }
                setGravacao('Todos os dados guardados');
                return true;
            } catch {
                setGravacao('Não foi possível guardar. Tente novamente antes de sair.');
                return false;
            } finally { aGravar.current = null; }
        })();
        aGravar.current = tarefa;
        return tarefa;
    }, []);
    const alterarFatura = (id, campo, valor) => {
        const original = lista.find(o => o.id === id);
        const atual = saida.current.registos.find(r => r.id === id) || {
            id, numero_fatura: original?.numero_fatura_preparacao ?? original?.numero_fatura ?? '',
            data_fatura: original?.data_fatura?.slice(0,10) ?? ''
        };
        const dados = { ...atual, [campo]: valor };
        setEditando(prev => ({ ...prev, [id]: { numero_fatura: dados.numero_fatura, data_fatura: dados.data_fatura } }));
        saida.current.registos = [...saida.current.registos.filter(r => r.id !== id), dados];
        pendentes.current.set(id, dados);
        gravarPendentes();
    };
    const alterarNumero = (id, numero) => alterarFatura(id, 'numero_fatura', numero);
    useEffect(() => {
        window.addEventListener('pagehide', concluirAoSair);
        const regressar = () => { saida.current.enviados = false; };
        window.addEventListener('pageshow', regressar);
        return () => {
            window.removeEventListener('pagehide', concluirAoSair);
            window.removeEventListener('pageshow', regressar);
            concluirAoSair();
        };
    }, [concluirAoSair]);
    const criarRascunho = async id => {
        if (criando !== null) return;
        setCriando(id);
        try {
            const r = await api.post(`/faturacao/rascunhos/de-orcamento/${id}`);
            onRascunho(r.data.id);
        } catch (e) { liquidFeedback.notify(e.response?.data?.error || 'Não foi possível criar o rascunho.'); }
        finally { setCriando(null); }
    };

    const fetchParaFaturar = useCallback((silent = false) => {
        if (!silent) setLoading(true);
        api.get('/gestao-financeira/para-faturar')
            .then(r => { const rows = Array.isArray(r.data) ? r.data : []; setLista(rows); onCountChange?.(rows.length); })
            .catch(() => {})
            .finally(() => { if (!silent) setLoading(false); });
    }, [onCountChange]);

    useEffect(() => { fetchParaFaturar(); }, [fetchParaFaturar]);

    // Auto-refresh quando o tab volta a ficar visível
    useEffect(() => {
        const onVisible = () => { if (document.visibilityState === 'visible') fetchParaFaturar(true); };
        const onFocus = () => fetchParaFaturar(true);
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onFocus);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onFocus);
        };
    }, [fetchParaFaturar]);

    const dadosFiltrados = useMemo(() => {
        let rows = lista.map(o => ({
            ...o,
            margem: String((parseFloat(o.total_venda||0) - parseFloat(o.total_compra||0)).toFixed(2)),
        }));

        const idProcurado = procuraId.trim().replace(/^#/, '');
        if (idProcurado) rows = rows.filter(o => String(o.id).includes(idProcurado));

        for (const [field, vals] of Object.entries(colFilters)) {
            if (!vals || vals.size === 0) continue;
            rows = rows.filter(o => {
                const v = field === 'id' ? String(o.id) : String(o[field] || '');
                return vals.has(v);
            });
        }

        rows.sort((a, b) => {
            let va = a[sortCol], vb = b[sortCol];
            if (sortCol === 'id') { va = parseInt(va||0); vb = parseInt(vb||0); }
            else if (sortCol === 'total_compra' || sortCol === 'total_venda' || sortCol === 'margem') { va = parseFloat(va||0); vb = parseFloat(vb||0); }
            else { va = String(va||''); vb = String(vb||''); return sortDir === 'asc' ? va.localeCompare(vb, 'pt') : vb.localeCompare(va, 'pt'); }
            return sortDir === 'asc' ? va - vb : vb - va;
        });

        return rows;
    }, [lista, colFilters, sortCol, sortDir, procuraId]);

    const totalCompra = useMemo(() => dadosFiltrados.reduce((acc, o) => acc + parseFloat(o.total_compra||0), 0), [dadosFiltrados]);
    const totalVenda  = useMemo(() => dadosFiltrados.reduce((acc, o) => acc + parseFloat(o.total_venda||0), 0), [dadosFiltrados]);
    const totalMargem = totalVenda - totalCompra;

    const cfProps = { data: lista.map(o => ({ ...o, margem: String((parseFloat(o.total_venda||0)-parseFloat(o.total_compra||0)).toFixed(2)), id: String(o.id) })), colFilters, setColFilters, sortCol, setSortCol, sortDir, setSortDir };

    return (
        <div>
            {encomendas.dialogo}
            <div className="gf-dashboard-mini">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <h3 style={{ margin: 0, fontSize: 15, color: '#1a1a2e' }}>Orçamentos Para Faturar</h3>
                    <span className="gf-count">{dadosFiltrados.length} registos</span>
                </div>
                <div className="gf-summary-cards">
                    <div className="gf-card-mini">
                        <span className="gf-card-label">TOTAL COMPRA</span>
                        <span className="gf-card-val">{fmtMoney(totalCompra)}</span>
                    </div>
                    <div className="gf-card-mini">
                        <span className="gf-card-label">TOTAL VENDA</span>
                        <span className="gf-card-val">{fmtMoney(totalVenda)}</span>
                    </div>
                    <div className="gf-card-mini">
                        <span className="gf-card-label">MARGEM</span>
                        <span className={`gf-card-val ${totalMargem >= 0 ? 'pos' : 'neg'}`}>{fmtMoney(totalMargem)}</span>
                    </div>
                    <div className="gf-card-mini">
                        <span className="gf-card-label">TOTAL REGISTOS</span>
                        <span className="gf-card-val">{dadosFiltrados.length}</span>
                    </div>
                </div>
            </div>
            <div className="gf-procura-id">
                <label htmlFor="gf-para-faturar-id">Procurar por ID</label>
                <input id="gf-para-faturar-id" type="search" inputMode="numeric" className="gf-inline-input" placeholder="" value={procuraId} onChange={e => setProcuraId(e.target.value)} />
                {procuraId && <button type="button" className="btn-ver-orc" onClick={() => setProcuraId('')}>Limpar</button>}
                <span role="status" className="gf-autogravacao">{gravacao}</span>
                {gravacao.startsWith('Não foi') && <button type="button" className="btn-ver-orc" onClick={gravarPendentes}>Tentar novamente</button>}
            </div>
            <div className="gf-table-wrap">
                {loading ? <div className="gf-loading">A carregar...</div> : (
                    <table className="gf-table standard-table gf-table-para-faturar">
                        <thead>
                            <tr>
                                <th><ColFilter label="ID" field="id" {...cfProps} /></th>
                                <th><ColFilter label="Data" field="data" {...cfProps} /></th>
                                <th className="gf-col-cliente"><ColFilter label="Cliente" field="cliente" {...cfProps} /></th>
                                <th><ColFilter label="Projeto" field="projeto" {...cfProps} /></th>
                                <th><ColFilter label="NE Cliente" field="ne_cliente" {...cfProps} /></th>
                                <th><ColFilter label="T. Compra" field="total_compra" {...cfProps} /></th>
                                <th><ColFilter label="T. Venda" field="total_venda" {...cfProps} /></th>
                                <th><ColFilter label="Margem" field="margem" {...cfProps} /></th>
                                <th className="gf-pf-numero">Nº Fatura</th>
                                <th className="gf-data-fatura-cliente">Data Fatura</th>
                                <th className="gf-encomenda-cliente"><span className="gf-encomenda-titulo">ENCOMENDA<br />CLIENTE</span></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {dadosFiltrados.length === 0 ? (
                                <tr><td colSpan={12} className="gf-empty">{procuraId.trim() ? 'Nenhum orçamento encontrado para este ID' : 'Nenhum orçamento para faturar'}</td></tr>
                            ) : dadosFiltrados.map(o => {
                                const margem = parseFloat(o.total_venda || 0) - parseFloat(o.total_compra || 0);
                                const ed = editando[o.id] || {};
                                const numVal  = ed.numero_fatura !== undefined ? ed.numero_fatura : (o.numero_fatura_preparacao ?? o.numero_fatura ?? '');
                                return (
                                    <tr key={o.id} className="gf-row-click" onClick={() => navigate(`/orcamentos/${o.id}`)}>
                                        <td className="gf-orc-id">#{o.id}</td>
                                        <td>{fmtDate(o.data)}</td>
                                        <td>{o.cliente}</td>
                                        <td>{o.projeto}</td>
                                        <td>{o.ne_cliente}</td>
                                        <td className="tr">{fmtMoney(o.total_compra)}</td>
                                        <td className="tr">{fmtMoney(o.total_venda)}</td>
                                        <td className={`tr ${margem >= 0 ? 'pos' : 'neg'}`}>{fmtMoney(margem)}</td>
                                        <td className="gf-pf-numero" onClick={e => e.stopPropagation()}>
                                            <input
                                                type="text"
                                                className="gf-inline-input"
                                                value={numVal}
                                                onChange={e => alterarNumero(o.id, e.target.value)}
                                                onBlur={gravarPendentes}
                                            />
                                        </td>
                                        <td className="gf-data-fatura-cliente" onClick={e => e.stopPropagation()}>
                                            <input type="date" className="gf-inline-input" aria-label={'Data da fatura do orçamento '+o.id}
                                                value={ed.data_fatura ?? o.data_fatura?.slice(0,10) ?? ''}
                                                onChange={e => alterarFatura(o.id, 'data_fatura', e.target.value)} onBlur={gravarPendentes} />
                                        </td>
                                        <td className="gf-encomenda-cliente" onClick={e => e.stopPropagation()}>
                                            <EncomendaCliente o={o} {...encomendas} />
                                        </td>
                                        <td className="gf-pf-acoes" onClick={e => e.stopPropagation()}><div className="gf-pf-acoes-conteudo">
                                            {(Array.isArray(o.ne_documentos) ? o.ne_documentos.length > 0 : !!o.ne_ficheiro) && <button className="btn-ver-orc" type="button" aria-label="Ver documentos da encomenda cliente" title="Ver documentos da encomenda cliente" onClick={e => { e.stopPropagation(); encomendas.abrirEncomenda(o); }}><AppIcon name="eye" size={16} /></button>}
                                            {' '}<button className="fr-btn" disabled={criando !== null} onClick={() => criarRascunho(o.id)} title="Criar ou abrir o rascunho de fatura deste orçamento"><OrcamentoIcon name="document" fallback="▤" />{criando === o.id ? 'A preparar…' : 'RASCUNHO FATURA'}</button>
                                            {' '}

                                        </div></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

        </div>
    );
}

/* ============================================================
   TAB: FATURADOS
   ============================================================ */
function TabFaturados() {
    const navigate = useNavigate();

    // Persistência do filtro em localStorage (recorda a última seleção)
    const LS = 'gf_fatd_';
    const readLS = (key, fallback) => {
        try {
            const v = localStorage.getItem(LS + key);
            return v == null ? fallback : JSON.parse(v);
        } catch { return fallback; }
    };
    const writeLS = (key, value) => {
        try { localStorage.setItem(LS + key, JSON.stringify(value)); } catch {}
    };

    const [lista, setLista] = useState([]);
    const encomendas = useEncomendasCliente(setLista);
    const [loading, setLoading] = useState(true);
    const [sortCol, setSortCol] = useState('id');
    const [sortDir, setSortDir] = useState('desc');
    const [colFilters, setColFilters] = useState({});
    const [procuraFaturados, setProcuraFaturados] = useState('');
    // Pesquisa dentro da lista de clientes (para chegar a um nome no meio de
    // muitos). Não filtra a tabela — só encurta a lista de nomes a escolher.
    const [procuraCliente, setProcuraCliente] = useState('');
    // A lista abre só quando é pedida: aberta de raiz ocupava um terço do ecrã
    // com nomes que na maior parte do tempo não se está a escolher.
    const [listaClientesAberta, setListaClientesAberta] = useState(false);
    const refClientes = useRef(null);
    useEffect(() => {
        if (!listaClientesAberta) return;
        const fora = (e) => { if (refClientes.current && !refClientes.current.contains(e.target)) setListaClientesAberta(false); };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [listaClientesAberta]);

    // Filtro ano/mês — por defeito o ano corrente; 'todos' = sem filtro de ano
    const [anosDisponiveis, setAnosDisponiveis] = useState([]);
    const [ano, setAno] = useState(() => readLS('ano', new Date().getFullYear()));
    const [meses, setMeses] = useState(() => new Set(readLS('meses', []))); // 0-based; vazio = todos

    useEffect(() => { writeLS('ano', ano); }, [ano]);
    useEffect(() => { writeLS('meses', Array.from(meses)); }, [meses]);

    useEffect(() => {
        api.get('/gestao-financeira/faturados/anos')
            .then(r => setAnosDisponiveis(Array.isArray(r.data) ? r.data : []))
            .catch(() => {});
    }, []);

    const fetchFaturados = useCallback((silent = false) => {
        if (!silent) setLoading(true);
        const params = {};
        if (ano !== 'todos') params.ano = ano;
        if (meses.size > 0 && meses.size < 12) params.meses = Array.from(meses).map(m => m + 1).join(',');
        api.get('/gestao-financeira/faturados', { params })
            .then(r => setLista(r.data))
            .catch(() => {})
            .finally(() => { if (!silent) setLoading(false); });
    }, [ano, meses]);

    useEffect(() => { fetchFaturados(); }, [fetchFaturados]);
    useEffect(() => {
        const atualizar = () => fetchFaturados(true);
        window.addEventListener('gf-faturados-atualizados', atualizar);
        return () => window.removeEventListener('gf-faturados-atualizados', atualizar);
    }, [fetchFaturados]);

    useEffect(() => {
        const onVisible = () => { if (document.visibilityState === 'visible') fetchFaturados(true); };
        const onFocus = () => fetchFaturados(true);
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onFocus);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onFocus);
        };
    }, [fetchFaturados]);

    const dadosFiltrados = useMemo(() => {
        let rows = lista.map(o => ({
            ...o,
            margem: String((parseFloat(o.total_venda||0) - parseFloat(o.total_compra||0)).toFixed(2)),
            // Margem sobre a COMPRA (marcação): comprar por 100 e vender por 200
            // é 100%. Vazio quando não há compra — dividir por zero não diz nada.
            margem_pct: parseFloat(o.total_compra||0) > 0
                ? String((((parseFloat(o.total_venda||0) - parseFloat(o.total_compra||0)) / parseFloat(o.total_compra)) * 100).toFixed(1))
                : '',
        }));

        const procura = procuraFaturados.trim().toLocaleLowerCase('pt-PT');
        const idProcurado = procura.replace(/^#/, '');
        if (procura) rows = rows.filter(o =>
            (idProcurado && String(o.id).includes(idProcurado)) ||
            String(o.numero_fatura || '').toLocaleLowerCase('pt-PT').includes(procura)
        );

        for (const [field, vals] of Object.entries(colFilters)) {
            if (!vals || vals.size === 0) continue;
            rows = rows.filter(o => {
                const v = field === 'id' ? String(o.id) : String(o[field] || '');
                return vals.has(v);
            });
        }

        rows.sort((a, b) => {
            let va = a[sortCol], vb = b[sortCol];
            if (sortCol === 'id') { va = parseInt(va||0); vb = parseInt(vb||0); }
            else if (['total_compra','total_venda','margem','margem_pct'].includes(sortCol)) { va = parseFloat(va||0); vb = parseFloat(vb||0); }
            else { va = String(va||''); vb = String(vb||''); return sortDir === 'asc' ? va.localeCompare(vb, 'pt') : vb.localeCompare(va, 'pt'); }
            return sortDir === 'asc' ? va - vb : vb - va;
        });

        return rows;
    }, [lista, colFilters, sortCol, sortDir, procuraFaturados]);

    const totalCompra = useMemo(() => dadosFiltrados.reduce((acc, o) => acc + parseFloat(o.total_compra||0), 0), [dadosFiltrados]);
    const totalVenda  = useMemo(() => dadosFiltrados.reduce((acc, o) => acc + parseFloat(o.total_venda||0), 0), [dadosFiltrados]);
    const totalMargem = totalVenda - totalCompra;

    const cfProps = { data: lista.map(o => ({ ...o, margem: String((parseFloat(o.total_venda||0)-parseFloat(o.total_compra||0)).toFixed(2)), id: String(o.id), margem_pct: parseFloat(o.total_compra||0) > 0 ? String(((( parseFloat(o.total_venda||0) - parseFloat(o.total_compra||0)) / parseFloat(o.total_compra)) * 100).toFixed(1)) : '' })), colFilters, setColFilters, sortCol, setSortCol, sortDir, setSortDir };

    /* ---- Painel de clientes ----------------------------------------
       Os nomes saem do que está carregado (o período escolhido), com a
       contagem de orçamentos de cada um — é ela que diz onde vale a pena
       carregar. Escreve no mesmo colFilters.cliente que o filtro do cabeçalho
       da coluna, para os dois nunca discordarem. */
    const clientesDisponiveis = useMemo(() => {
        const conta = new Map();
        for (const o of lista) {
            const nome = (o.cliente || '').trim();
            if (!nome) continue;
            conta.set(nome, (conta.get(nome) || 0) + 1);
        }
        return [...conta.entries()]
            .map(([nome, n]) => ({ nome, n }))
            .sort((a, b) => a.nome.localeCompare(b.nome, 'pt'));
    }, [lista]);

    const clientesSel = colFilters.cliente || new Set();
    const clientesVisiveis = useMemo(() => {
        const limpar = (t) => (t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const alvo = limpar(procuraCliente.trim());
        return alvo ? clientesDisponiveis.filter(c => limpar(c.nome).includes(alvo)) : clientesDisponiveis;
    }, [clientesDisponiveis, procuraCliente]);

    const alternarCliente = (nome) => {
        setColFilters(prev => {
            const cur = new Set(prev.cliente || []);
            if (cur.has(nome)) cur.delete(nome); else cur.add(nome);
            if (cur.size === 0) { const next = { ...prev }; delete next.cliente; return next; }
            return { ...prev, cliente: cur };
        });
    };
    const limparClientes = () => setColFilters(prev => { const next = { ...prev }; delete next.cliente; return next; });


    const MONTHS = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
    const curYear = new Date().getFullYear();
    const toggleMonth = (idx) => {
        setMeses(prev => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx); else next.add(idx);
            return next;
        });
    };

    return (
        <div>
            {encomendas.dialogo}
            <div className="gf-quick-filter-container">
                <div className="gf-quick-row">
                    <button
                        className={`orc-ano-btn ${ano === 'todos' ? 'active' : ''}`}
                        onClick={() => { setAno('todos'); setMeses(new Set()); }}
                    >
                        Todos
                    </button>
                    <button
                        className={`orc-ano-btn ${ano === curYear && meses.size === 0 ? 'active' : ''}`}
                        onClick={() => { setAno(curYear); setMeses(new Set()); }}
                    >
                        Ano Corrente ({curYear})
                    </button>
                    <select
                        value={ano}
                        onChange={e => setAno(e.target.value === 'todos' ? 'todos' : parseInt(e.target.value, 10))}
                        style={{ padding: '6px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 13, background: '#fff' }}
                    >
                        <option value="todos">Todos os anos</option>
                        {anosDisponiveis.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                </div>
                <div className="gf-quick-row gf-months-grid">
                    {MONTHS.map((m, idx) => (
                        <button
                            key={m}
                            className={`gf-month-btn ${meses.has(idx) ? 'active' : ''}`}
                            onClick={() => toggleMonth(idx)}
                        >
                            {m}
                        </button>
                    ))}
                </div>
            </div>
            <div className="gf-dashboard-mini">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <h3 style={{ margin: 0, fontSize: 15, color: '#1a1a2e' }}>Orçamentos Faturados</h3>
                    <span className="gf-count">{dadosFiltrados.length} registos</span>
                </div>
                <div className="gf-summary-cards">
                    <div className="gf-card-mini">
                        <span className="gf-card-label">TOTAL COMPRA</span>
                        <span className="gf-card-val">{fmtMoney(totalCompra)}</span>
                    </div>
                    <div className="gf-card-mini">
                        <span className="gf-card-label">TOTAL VENDA</span>
                        <span className="gf-card-val">{fmtMoney(totalVenda)}</span>
                    </div>
                    <div className="gf-card-mini">
                        <span className="gf-card-label">MARGEM</span>
                        <span className={`gf-card-val ${totalMargem >= 0 ? 'pos' : 'neg'}`}>{fmtMoney(totalMargem)}</span>
                    </div>
                    {/* Margem do conjunto, na mesma regra da coluna da tabela:
                        sobre a compra. Não é a média das percentagens de cada
                        linha — é o total ganho sobre o total gasto, que é o que
                        responde a "quanto rendeu este período". */}
                    <div className="gf-card-mini">
                        <span className="gf-card-label">MARGEM %</span>
                        <span className={`gf-card-val ${totalMargem >= 0 ? 'pos' : 'neg'}`}>
                            {totalCompra > 0
                                ? `${(totalMargem / totalCompra * 100).toFixed(1).replace('.', ',')}%`
                                : '—'}
                        </span>
                    </div>
                    <div className="gf-card-mini">
                        <span className="gf-card-label">TOTAL REGISTOS</span>
                        <span className="gf-card-val">{dadosFiltrados.length}</span>
                    </div>
                </div>
            </div>
            {/* Botão que abre a lista de clientes. Fechado ocupa uma linha; só
                se abre quando se vai escolher. Aberto de raiz, a lista tomava um
                terço do ecrã com nomes que na maior parte do tempo não interessam. */}
            <div className="gf-clientes" ref={refClientes}>
                <button type="button"
                    className={`gf-clientes-btn${clientesSel.size > 0 ? ' ativo' : ''}`}
                    onClick={() => setListaClientesAberta(o => !o)}>
                    {clientesSel.size === 0
                        ? `Escolher clientes (${clientesDisponiveis.length})`
                        : `${clientesSel.size} cliente${clientesSel.size > 1 ? 's' : ''} escolhido${clientesSel.size > 1 ? 's' : ''}`}
                    <span className="gf-clientes-seta">⌄</span>
                </button>
                {clientesSel.size > 0 && (
                    <button type="button" className="gf-clientes-limpar" onClick={limparClientes}
                        title="Mostrar todos os clientes outra vez"><OrcamentoIcon name="close" fallback="×" /> limpar</button>
                )}
                {listaClientesAberta && (
                    <div className="gf-clientes-lista">
                        <input type="text" className="gf-clientes-procura" autoFocus
                            placeholder="Procurar nome..." value={procuraCliente}
                            onChange={e => setProcuraCliente(e.target.value)} />
                        <div className="gf-clientes-itens">
                            {clientesVisiveis.map(c => (
                                <label key={c.nome}
                                    className={`gf-clientes-item${clientesSel.has(c.nome) ? ' escolhido' : ''}`}>
                                    <input type="checkbox" checked={clientesSel.has(c.nome)}
                                        onChange={() => alternarCliente(c.nome)} />
                                    <span className="gf-clientes-nome" title={c.nome}>{c.nome}</span>
                                    <span className="gf-clientes-n">{c.n}</span>
                                </label>
                            ))}
                            {clientesVisiveis.length === 0 && (
                                <span className="gf-clientes-vazio">Nenhum cliente com esse nome.</span>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <div className="gf-procura-id">
                <label htmlFor="gf-faturados-procura">Procurar por orçamento ou n.º fatura</label>
                <input id="gf-faturados-procura" type="search" className="gf-inline-input" placeholder="Orçamento ou fatura" value={procuraFaturados} onChange={e => setProcuraFaturados(e.target.value)} />
                {procuraFaturados && <button type="button" className="btn-ver-orc" onClick={() => setProcuraFaturados('')}>Limpar</button>}
            </div>
            <div className="gf-table-wrap">
                {loading ? <div className="gf-loading">A carregar...</div> : (
                    <table className="gf-table standard-table">
                        <thead>
                            <tr>
                                <th><ColFilter label="ID" field="id" {...cfProps} /></th>
                                <th><ColFilter label="Data Orc." field="data" {...cfProps} /></th>
                                <th className="gf-col-cliente"><ColFilter label="Cliente" field="cliente" {...cfProps} /></th>
                                <th><ColFilter label="Projeto" field="projeto" {...cfProps} /></th>
                                <th><ColFilter label="T. Compra" field="total_compra" {...cfProps} /></th>
                                <th><ColFilter label="T. Venda" field="total_venda" {...cfProps} /></th>
                                <th className="gf-col-margem"><ColFilter label="Margem" field="margem" {...cfProps} /></th>
                                <th className="gf-col-margem-pct"><ColFilter label="Margem %" field="margem_pct" {...cfProps} /></th>
                                <th className="gf-numero-fatura-centro"><ColFilter label="Nº Fatura" field="numero_fatura" {...cfProps} /></th>
                                <th className="gf-data-fatura-cliente"><ColFilter label="Data Fatura" field="data_fatura" {...cfProps} /></th>
                                <th className="gf-encomenda-cliente"><span className="gf-encomenda-titulo">ENCOMENDA<br />CLIENTE</span></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {dadosFiltrados.length === 0 ? (
                                <tr><td colSpan={12} className="gf-empty">{procuraFaturados.trim() ? 'Nenhum registo encontrado para esta pesquisa' : 'Nenhum orçamento faturado'}</td></tr>
                            ) : dadosFiltrados.map(o => {
                                const margem = parseFloat(o.total_venda || 0) - parseFloat(o.total_compra || 0);
                                return (
                                    <tr key={o.id} className="gf-row-click" onClick={() => navigate(`/orcamentos/${o.id}`)}>
                                        <td className="gf-orc-id">#{o.id}</td>
                                        <td>{fmtDate(o.data)}</td>
                                        <td className="gf-col-cliente">{o.cliente}</td>
                                        <td>{o.projeto}</td>
                                        <td className="tr">{fmtMoney(o.total_compra)}</td>
                                        <td className="tr">{fmtMoney(o.total_venda)}</td>
                                        <td className={`tr gf-col-margem ${margem >= 0 ? 'pos' : 'neg'}`}>{fmtMoney(margem)}</td>
                                        {/* Percentagem sobre a compra, alinhada com a coluna
                                            do valor: as duas leem-se em conjunto. */}
                                        <td className={`tr gf-col-margem-pct ${margem >= 0 ? 'pos' : 'neg'}`}>
                                            {parseFloat(o.total_compra || 0) > 0
                                                ? `${(margem / parseFloat(o.total_compra) * 100).toFixed(1).replace('.', ',')}%`
                                                : '—'}
                                        </td>
                                        <td className="gf-numero-fatura-centro">{o.numero_fatura || '—'}</td>
                                        <td className="gf-data-fatura-cliente" onClick={e => e.stopPropagation()}>
                                            <DataFaturaFaturado id={o.id} data={o.data_fatura}
                                                onGuardada={data => setLista(prev => prev.map(item => item.id === o.id ? { ...item, data_fatura: data } : item))} />
                                        </td>
                                        <td className="gf-encomenda-cliente" onClick={e => e.stopPropagation()}><EncomendaCliente o={o} {...encomendas} /></td>
                                        <td>{(Array.isArray(o.ne_documentos) ? o.ne_documentos.length > 0 : !!o.ne_ficheiro) && <button className="btn-ver-orc gf-ver-encomenda-faturado" type="button" aria-label="Ver documentos da encomenda cliente" title="Ver documentos da encomenda cliente" onClick={e => { e.stopPropagation(); encomendas.abrirEncomenda(o); }}><AppIcon name="eye" size={16} /></button>}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

/* ============================================================
   MODAL PREENCHIMENTO MANUAL BULK
   ============================================================ */
function ModalEF({ ef, onClose, onSaved, initialNumero, initialData, atcud }) {
    const liquidFeedback = useLiquidFeedback();
    const navigate = useNavigate();

    // Prioridade: prop inicial (pistola) > campo derivado da lista > campo direto EF > despesa ligada
    const numInicial = initialNumero || ef.numero_fatura_fornecedor_ef || ef.numero_fatura_fornecedor || ef.despesa_numero || '';
    const dataInicial = initialData
        || (ef.data_fatura_fornecedor ? ef.data_fatura_fornecedor.substring(0, 10) : '')
        || (ef.despesa_data ? ef.despesa_data.substring(0, 10) : '')
        || '';

    const [fatNumero, setFatNumero] = useState(numInicial);
    const [fatData, setFatData] = useState(dataInicial);
    const [fatTaxaIva, setFatTaxaIva] = useState(
        ef.despesa_taxa_iva != null ? parseFloat(ef.despesa_taxa_iva) : 23
    );
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState('');
    const jaTemFatura = !initialNumero && !!(ef.numero_fatura_fornecedor_ef || ef.numero_fatura_fornecedor || ef.despesa_id || ef.tem_fatura_ligada || ef.estado === 'RECEBIDA');

    // --- Edição da EF ---
    const fmtQtd = (v) => { if (v === null || v === undefined || v === '') return ''; const n = parseFloat(v); return isNaN(n) ? '' : (n % 1 === 0 ? String(Math.round(n)) : n.toFixed(1)); };
    const [efForm, setEfForm] = useState({
        fornecedor: ef.fornecedor || '',
        descricao: ef.descricao || '',
        qtd: fmtQtd(ef.qtd),
        valor_unitario: ef.valor_unitario != null ? parseFloat(ef.valor_unitario).toFixed(2) : '',
        valor_total: ef.valor_total != null ? parseFloat(ef.valor_total).toFixed(2) : '',
        notas: ef.notas || '',
    });
    const [fornecedores, setFornecedores] = useState([]);
    const [savingEF, setSavingEF] = useState(false);
    const [saveEFMsg, setSaveEFMsg] = useState('');

    useEffect(() => {
        api.get('/fornecedores').then(r => setFornecedores(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }, []);

    const recalcEF = (field, value) => {
        const next = { ...efForm, [field]: value };
        const q = parseFloat(String(field === 'qtd' ? value : next.qtd).replace(',', '.')) || 0;
        const vu = parseFloat(String(field === 'valor_unitario' ? value : next.valor_unitario).replace(',', '.')) || 0;
        if ((field === 'qtd' || field === 'valor_unitario') && q && vu) {
            next.valor_total = (q * vu).toFixed(2);
        }
        setEfForm(next);
    };

    const handleSaveEF = async () => {
        setSavingEF(true); setSaveEFMsg('');
        try {
            await api.put(`/encomendas-fornecedor/${ef.id}`, {
                ...efForm,
                qtd: parseFloat(String(efForm.qtd).replace(',', '.')) || 0,
                valor_unitario: parseFloat(String(efForm.valor_unitario).replace(',', '.')) || 0,
                valor_total: parseFloat(String(efForm.valor_total).replace(',', '.')) || 0,
            });
            setSaveEFMsg('ok');
            if (onSaved) onSaved();
        } catch (e) {
            setSaveEFMsg('erro: ' + (e.response?.data?.error || e.message));
        } finally { setSavingEF(false); }
    };
    // Preview do total da futura fatura com base na taxa escolhida
    // Usa efForm.valor_total (pode ter sido editado) em vez do valor original da EF
    const efBaseNum = parseFloat(efForm.valor_total) || parseFloat(ef.valor_total) || 0;
    const efTotalComIva = parseFloat((efBaseNum * (1 + (parseFloat(fatTaxaIva) || 0) / 100)).toFixed(2));

    const handleDesassociar = async () => {
        if (!await liquidFeedback.confirm('Remover os dados da fatura desta encomenda?')) return;
        setSaving(true);
        try {
            await api.delete(`/gestao-financeira/encomendas-fornecedor/${ef.id}/registar-fatura`);
            if (onSaved) onSaved();
            onClose();
        } catch (e) {
            setSaveMsg('erro: ' + (e.response?.data?.error || e.message));
        } finally { setSaving(false); }
    };

    const handleSave = async () => {
        if (!fatNumero.trim()) return;
        setSaving(true);
        setSaveMsg('');
        try {
            await api.patch(`/gestao-financeira/encomendas-fornecedor/${ef.id}/registar-fatura`, {
                numero_fatura_fornecedor: fatNumero.trim(),
                data_fatura_fornecedor: fatData || null,
                taxa_iva: fatTaxaIva,
                // Data/hora local do PC do utilizador (ISO com offset) — o servidor usa isto como criado_em
                criado_em_cliente: new Date().toISOString(),
                ...(atcud ? { atcud } : {}),
            });
            setSaveMsg('ok');
            if (onSaved) onSaved();
            setTimeout(() => onClose(), 1200);
        } catch (e) {
            setSaveMsg('erro: ' + (e.response?.data?.error || e.message));
        } finally { setSaving(false); }
    };

    return (
        <LiquidDialog onClose={onClose} className="gf-modal-overlay" onClick={onClose}>
            <div className="gf-modal" onClick={e => e.stopPropagation()}>
                <div className="gf-modal-header">
                    <h3>Encomenda {ef.numero}</h3>
                    <button className="gf-modal-close" onClick={onClose}><OrcamentoIcon name="close" fallback="×" /></button>
                </div>
                <div className="gf-modal-body">
                    <div className="gf-modal-info-grid" style={{marginBottom:4}}>
                        <div><span className="gf-modal-label">Orçamento</span><span>{ef.orcamento_id ? `#${ef.orcamento_id}` : '—'}</span></div>
                        <div><span className="gf-modal-label">Projeto</span><span>{ef.projeto || '—'}</span></div>
                        <div><span className="gf-modal-label">Estado</span><span className={`ef-estado ${(ef.tem_fatura_ligada || ef.despesa_id) ? 'ef-estado-associada' : 'ef-estado-aguarda'}`}>{(ef.tem_fatura_ligada || ef.despesa_id) ? 'Associada' : 'Aguarda Fatura'}</span></div>
                        <div><span className="gf-modal-label">Criada em</span><span>{fmtDate(ef.criado_em) || '—'}</span></div>
                    </div>
                    <div className="gf-modal-fatura-form" style={{marginBottom:6}}>
                        <div className="gf-field" style={{gridColumn:'1/-1'}}>
                            <label>Fornecedor</label>
                            <select value={efForm.fornecedor} onChange={e => setEfForm({...efForm, fornecedor: e.target.value})}>
                                <option value="">-- Selecionar --</option>
                                {fornecedores.map(f => <option key={f.id} value={f.nome}>{f.nome}</option>)}
                            </select>
                        </div>
                        <div className="gf-field" style={{gridColumn:'1/-1'}}>
                            <label>Descrição</label>
                            <input value={efForm.descricao} onChange={e => setEfForm({...efForm, descricao: e.target.value})} />
                        </div>
                        <div className="gf-field">
                            <label>Qtd</label>
                            <input type="number" step="0.1" min="0" value={efForm.qtd}
                                onChange={e => recalcEF('qtd', e.target.value)}
                                onBlur={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) recalcEF('qtd', v % 1 === 0 ? String(Math.round(v)) : v.toFixed(1)); }} />
                        </div>
                        <div className="gf-field">
                            <label>V. Unitário (s/ IVA)</label>
                            <input type="number" step="0.01" min="0" value={efForm.valor_unitario}
                                onChange={e => recalcEF('valor_unitario', e.target.value)} />
                        </div>
                        <div className="gf-field">
                            <label>Total (s/ IVA)</label>
                            <input type="number" step="0.01" min="0" value={efForm.valor_total}
                                onChange={e => setEfForm({...efForm, valor_total: e.target.value})} />
                        </div>
                        <div className="gf-field" style={{gridColumn:'1/-1'}}>
                            <label>Notas</label>
                            <input value={efForm.notas} onChange={e => setEfForm({...efForm, notas: e.target.value})} />
                        </div>
                    </div>
                    {saveEFMsg === 'ok' && <div className="gf-save-ok" style={{marginBottom:4}}>Encomenda guardada.</div>}
                    {saveEFMsg && saveEFMsg !== 'ok' && <div className="gf-save-err" style={{marginBottom:4}}>{saveEFMsg}</div>}
                    {ef.despesa_id && (
                        <div className="gf-modal-despesa-status">
                            <span className="gf-modal-label">Fatura registada</span>
                            <span className={`gf-badge ${ef.despesa_estado_fatura === 'verificada' ? 'badge-green' : 'badge-yellow'}`}>
                                {ef.despesa_estado_fatura === 'verificada' ? 'Verificada' : 'Pendente'}
                            </span>
                            <span className={`gf-badge ${ef.despesa_estado_dig === 'concluida' ? 'badge-yellow' : 'badge-gray'}`}>
                                Dig.: {ef.despesa_estado_dig === 'concluida' ? 'Concluída' : 'Pendente'}
                            </span>
                        </div>
                    )}
                    <hr className="gf-modal-sep" />
                    <h4 className="gf-modal-section-title">
                        {ef.despesa_id ? 'Atualizar Fatura do Fornecedor' : 'Registar Fatura do Fornecedor'}
                    </h4>
                    <div className="gf-modal-fatura-form">
                        <div className="gf-field">
                            <label>Nº Fatura Fornecedor *</label>
                            <input type="text" value={fatNumero} onChange={e => setFatNumero(e.target.value)}
                                placeholder="ex: FT 2026/123" autoFocus />
                        </div>
                        <div className="gf-field">
                            <label>Data da Fatura</label>
                            <input type="date" value={fatData} onChange={e => setFatData(e.target.value)} />
                        </div>
                        <div className="gf-field">
                            <label>Taxa IVA</label>
                            <select value={fatTaxaIva} onChange={e => setFatTaxaIva(parseFloat(e.target.value))}>
                                <option value={23}>23%</option>
                                <option value={16}>16%</option>
                                <option value={13}>13%</option>
                                <option value={6}>6%</option>
                                <option value={4}>4%</option>
                                <option value={0}>0% (Isento)</option>
                            </select>
                        </div>
                    </div>
                    {!ef.despesa_id && (
                        <div style={{marginTop:8, fontSize:12, color:'#6b7280'}}>
                            Total calculado da fatura: <strong style={{color:'#111827'}}>{fmtMoney(efTotalComIva)}</strong>
                            {' '}(base {fmtMoney(efBaseNum)} + IVA {fatTaxaIva}%)
                        </div>
                    )}
                    {saveMsg === 'ok' && <div className="gf-save-ok">Fatura registada com sucesso. Digitalização pendente.</div>}
                    {saveMsg && saveMsg !== 'ok' && <div className="gf-save-err">{saveMsg}</div>}
                </div>
                <div className="gf-modal-footer">
                    {jaTemFatura && (
                        <button className="gf-modal-btn-desassoc" onClick={handleDesassociar} disabled={saving || savingEF}>
                            Desassociar Fatura
                        </button>
                    )}
                    <button className="btn-gf-clear" onClick={onClose}>Cancelar</button>
                    {ef.orcamento_id && (
                        <button className="gf-modal-btn-orc" onClick={() => { onClose(); navigate(`/orcamentos/${ef.orcamento_id}`); }}>
                            Ver Orç. #{ef.orcamento_id}
                        </button>
                    )}
                    <button className="btn-gf-search" style={{background:'#6b7280'}} onClick={handleSaveEF} disabled={savingEF || saving}>
                        {savingEF ? 'A guardar...' : 'Guardar Encomenda'}
                    </button>
                    <button className="btn-gf-search" onClick={handleSave} disabled={saving || savingEF || !fatNumero.trim()}>
                        {saving ? 'A guardar...' : 'Registar Fatura'}
                    </button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ============================================================
   TAB: ENCOMENDAS FORNECEDOR (EFs)
   ============================================================ */
const PAGE_SIZE_EF = 200;

function TabEncomendasFornecedor() {
    const [lista, setLista] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filtroEstado, setFiltroEstado] = useState('');
    const [filtroForn, setFiltroForn] = useState('');
    const [filtroNumero, setFiltroNumero] = useState('');
    const [modalEF, setModalEF] = useState(null);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [page, setPage] = useState(1);

    // Só a resposta ao último pedido conta: a lista sem filtro é bastante mais
    // lenta que uma pesquisa, e se chegar atrasada apaga o resultado da
    // pesquisa que o utilizador tem à frente.
    const pedidoRef = useRef(0);
    const fetchLista = useCallback(async (silent = false) => {
        const meu = ++pedidoRef.current;
        if (!silent) setLoading(true);
        try {
            const params = { page, pageSize: PAGE_SIZE_EF };
            if (filtroForn) params.fornecedor = filtroForn;
            if (filtroNumero) params.numero = filtroNumero;
            if (filtroEstado === 'associada') params.tem_fatura = 'true';
            else if (filtroEstado === 'aguarda') params.tem_fatura = 'false';
            const r = await api.get('/gestao-financeira/encomendas-fornecedor', { params });
            if (meu !== pedidoRef.current) return;   // resposta atrasada: ignora-se
            setLista(r.data.rows || []);
            setTotal(r.data.total || 0);
        } catch (e) {} finally { if (!silent && meu === pedidoRef.current) setLoading(false); }
    }, [filtroForn, filtroNumero, filtroEstado, page]);

    // Reset para página 1 quando os filtros mudam
    useEffect(() => { setPage(1); }, [filtroForn, filtroNumero, filtroEstado]);

    useEffect(() => { fetchLista(); }, [fetchLista]);

    // Auto-refresh quando a janela volta a ficar visível
    useEffect(() => {
        const onVisible = () => { if (document.visibilityState === 'visible') fetchLista(true); };
        const onFocus = () => fetchLista(true);
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onFocus);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onFocus);
        };
    }, [fetchLista]);

    const totalPages = Math.ceil(total / PAGE_SIZE_EF);

    return (
        <div>
            <div className="gf-section-header">
                <h3>Encomendas a Fornecedor (EFs)</h3>
                <span className="gf-count">{total} registos</span>
            </div>
            <div className="gf-filtros">
                <input type="text" placeholder="Pesquisar por fornecedor ou nº encomenda (ex: 0055)..." value={filtroForn}
                    onChange={e => setFiltroForn(e.target.value)} className="gf-input-f" style={{flex:3}} />
                <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} className="gf-select">
                    <option value="">Todos os estados</option>
                    <option value="aguarda">Aguarda Fatura</option>
                    <option value="associada">Associada</option>
                </select>
            </div>
            <div className="gf-table-wrap">
                {loading ? <div className="gf-loading">A carregar...</div> : (
                    <table className="gf-table standard-table">
                        <thead>
                            <tr>
                                <th>Número</th><th>Orç.</th><th>Fornecedor</th><th>Descrição</th>
                                <th>Qtd</th><th>Total</th><th>Estado</th><th>Fatura Forn.</th><th>Criada em</th><th>Projeto</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(() => {
                                if (lista.length === 0) return <tr><td colSpan={10} className="gf-empty">Nenhuma encomenda</td></tr>;
                                return lista.map(ef => {
                                const isExpanded = expandedRows.has(ef.id);
                                return (
                                <tr key={ef.id} className="gf-row gf-row-click" onClick={() => setModalEF(ef)}>
                                    <td><span className="gf-num">{ef.numero}</span></td>
                                    <td>{ef.orcamento_id && <span className="gf-orc-badge">ORC-{ef.orcamento_id}</span>}</td>
                                    <td style={{ maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ef.fornecedor}</td>
                                    <td style={{ maxWidth: 260 }} onClick={e => { e.stopPropagation(); setExpandedRows(prev => { const s = new Set(prev); s.has(ef.id) ? s.delete(ef.id) : s.add(ef.id); return s; }); }}>
                                        <span style={{ display: 'block', whiteSpace: isExpanded ? 'normal' : 'nowrap', overflow: 'hidden', textOverflow: isExpanded ? 'clip' : 'ellipsis', cursor: 'pointer' }} title={ef.descricao}>{ef.descricao}</span>
                                    </td>
                                    <td className="tc">{ef.qtd}</td>
                                    <td className="tr">{fmtMoney(ef.valor_total)}</td>
                                    <td><span className={`ef-estado ${ef.tem_fatura_ligada ? 'ef-estado-associada' : 'ef-estado-aguarda'}`}>{ef.tem_fatura_ligada ? 'Associada' : 'Aguarda Fatura'}</span></td>
                                    <td>
                                        {(ef.numero_fatura_fornecedor_ef || ef.numero_fatura_fornecedor) ? (
                                            <span className="ef-fatura-tag" title={ef.data_fatura_fornecedor ? fmtDate(ef.data_fatura_fornecedor) : ''}>
                                                <OrcamentoIcon name="check" fallback="✓" /> {ef.numero_fatura_fornecedor_ef || ef.numero_fatura_fornecedor}
                                            </span>
                                        ) : ef.tem_fatura_ligada ? (
                                            <span className="ef-fatura-tag" style={{background:'#fef3c7',color:'#92400e'}}><OrcamentoIcon name="check" fallback="✓" /> Fatura</span>
                                        ) : <span className="ef-fatura-pendente">—</span>}
                                    </td>
                                    <td>{fmtDate(ef.criado_em)}</td>
                                    <td>{ef.projeto}</td>
                                </tr>
                            );});
                            })()}
                        </tbody>
                    </table>
                )}
            </div>

            {totalPages > 1 && (
                <div className="gf-pagination">
                    <button className="gf-pag-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                    <span className="gf-pag-info">Pág. {page} / {totalPages}</span>
                    <button className="gf-pag-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                </div>
            )}

            {modalEF && (
                <ModalEF ef={modalEF} onClose={() => setModalEF(null)} onSaved={fetchLista} />
            )}
        </div>
    );
}

/* Mostra uma EF de cada vez com setas < > quando há várias.
   Clicar no chip abre o modal da EF. */
function ReferenciaEF({ numero }) {
    return <span className="gf-ef-reference-text">{String(numero || '').split(/(\d+)/).map((parte, i) =>
        /^\d+$/.test(parte) ? <span className="gf-ef-digits" key={i}>{parte}</span> : parte
    )}</span>;
}

function EFChipsCarousel({ efs, onOpen }) {
    const [idx, setIdx] = useState(0);
    if (!Array.isArray(efs) || efs.length === 0) return null;
    const safeIdx = Math.min(idx, efs.length - 1);
    const ae = efs[safeIdx];
    const multi = efs.length > 1;
    const stop = (fn) => (e) => { e.stopPropagation(); fn(e); };
    return (
        <div className="gf-ef-carousel" onClick={e => e.stopPropagation()}>
            {multi && (
                <button
                    className="gf-ef-carousel-nav"
                    onClick={stop(() => setIdx(i => (i - 1 + efs.length) % efs.length))}
                    title="EF anterior">
                    <OrcamentoIcon name="back" fallback="‹" />
                </button>
            )}
            <span
                className="gf-ef-tag gf-ef-tag-ok gf-ef-tag-clickable"
                title={`${ae.ef_fornecedor || ''} — alocado ${fmtMoney(ae.valor_alocado)}\nClique para abrir EF`}
                onClick={stop(() => onOpen && onOpen(ae.ef_id))}>
                <ReferenciaEF numero={ae.ef_numero} />
            </span>
            {multi && (
                <>
                    <button
                        className="gf-ef-carousel-nav"
                        onClick={stop(() => setIdx(i => (i + 1) % efs.length))}
                        title="EF seguinte">
                        <span className="gf-ef-next-icon"><OrcamentoIcon name="back" fallback="‹" /></span>
                    </button>
                    <span className="gf-ef-carousel-count">{safeIdx + 1}/{efs.length}</span>
                </>
            )}
        </div>
    );
}

/* ============================================================
   COL FILTER (estilo Excel) — usado nos cabeçalhos da tabela de faturas.
   Lista de valores únicos com pesquisa, multi-seleção e sort asc/desc.
   ============================================================ */
function ColFilterFat({ label, field, getValue, data, colFilters, setColFilters, sortCol, setSortCol, sortDir, setSortDir }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
    const wrapRef = useRef(null);
    const btnRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (!wrapRef.current) return;
            // Check both the wrap and the fixed dropdown (which is a DOM child of wrap)
            if (!wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleOpen = (e) => {
        e.stopPropagation();
        if (!open && btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect();
            setDropPos({ top: rect.bottom + 4, left: rect.left });
        }
        setOpen(o => !o);
    };

    const uniqueVals = useMemo(() => {
        const vals = new Set(data.map(row => String(getValue(row) ?? '')));
        return [...vals].filter(v => v !== '').sort((a, b) => a.localeCompare(b, 'pt', { numeric: true }));
    }, [data, getValue]);

    const selected = colFilters[field] || new Set();
    const allSelected = selected.size === 0;
    const filteredVals = uniqueVals.filter(v => v.toLowerCase().includes(search.toLowerCase()));

    const toggle = (val) => {
        setColFilters(prev => {
            const cur = new Set(prev[field] || []);
            if (cur.has(val)) cur.delete(val); else cur.add(val);
            if (cur.size === 0) {
                const next = { ...prev }; delete next[field]; return next;
            }
            return { ...prev, [field]: cur };
        });
        // Stays open for multi-select
    };

    const clearFilter = () => {
        setColFilters(prev => { const next = { ...prev }; delete next[field]; return next; });
        setSearch('');
    };

    const handleSort = (dir) => { setSortCol(field); setSortDir(dir); setOpen(false); };

    const isFiltered = !allSelected;
    const isSorted = sortCol === field;

    return (
        <div className="col-filter-wrap" ref={wrapRef}>
            <button
                ref={btnRef}
                className={`col-filter-btn${isFiltered ? ' filtered' : ''}${isSorted ? ' sorted' : ''}`}
                onClick={handleOpen}
                title={isFiltered ? 'Filtro activo' : 'Filtrar / ordenar'}>
                <LiquidText>{label}</LiquidText>
                <span className="col-filter-arrow">{isFiltered ? '▼' : isSorted ? (sortDir === 'asc' ? '↑' : '↓') : '⌄'}</span>
            </button>
            {open && (
                <div className="col-filter-dropdown col-filter-dropdown-fixed"
                     style={{ top: dropPos.top, left: dropPos.left }}
                     onClick={e => e.stopPropagation()}>
                    <div className="col-filter-sort">
                        <button onClick={() => handleSort('asc')} className={`col-filter-sort-btn${sortCol===field&&sortDir==='asc'?' active':''}`}>↑ Crescente</button>
                        <button onClick={() => handleSort('desc')} className={`col-filter-sort-btn${sortCol===field&&sortDir==='desc'?' active':''}`}>↓ Decrescente</button>
                    </div>
                    <input className="col-filter-search" autoFocus placeholder="Pesquisar..." value={search} onChange={e => setSearch(e.target.value)} />
                    <div className="col-filter-list">
                        <label className="col-filter-item">
                            <input type="checkbox" checked={allSelected} onChange={clearFilter} />
                            <span>(Todos)</span>
                        </label>
                        {filteredVals.map(v => (
                            <label key={v} className="col-filter-item">
                                <input type="checkbox" checked={selected.has(v)} onChange={() => toggle(v)} />
                                <span>{v || '(vazio)'}</span>
                            </label>
                        ))}
                    </div>
                    {isFiltered && (
                        <div className="col-filter-footer">
                            <button className="col-filter-clear-btn" onClick={clearFilter}>Limpar filtro</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/* ============================================================
   TAB: FATURAS FORNECEDOR (Real)
   ============================================================ */
// Fornecedores cujas faturas resultam de acordo comercial: não têm — nem vão
// ter — encomenda associada. Mostrá-las como "Não associada" dava a entender
// trabalho por fazer, quando não há nada a associar.
// A comparação ignora acentos, maiúsculas e pontuação, para aguentar variações
// como "Just2Partners, Lda", "JUST2PARTNERS LDA" ou "Brandcode".
const FORNECEDORES_ACORDO = ['brandcode', 'just2partners'];
const normalizarNomeForn = (s) => (s || '')
    .normalize('NFD')                 // "ã" passa a "a" + acento combinante
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');       // cai tudo o resto: acentos, espaços, pontuação
// Fator de IVA da própria fatura, para converter o valor líquido das encomendas
// no valor com IVA que a fatura apresenta. Estava 1.23 fixo no código, o que
// inflacionava a encomenda em ~16% nas faturas a 6% e fazia aparecer divergências
// que não existiam — numa coluna cuja função é precisamente assinalar divergências.
const fatorIva = (f) => 1 + (parseFloat(f.taxa_iva ?? 23) / 100);

// Porção da encomenda atribuída a ESTA fatura, em líquido.
// Usa-se valor_alocado e não o valor total da linha: uma encomenda pode ser
// consumida por várias faturas (entregas sucessivas, por exemplo), e comparar
// cada uma contra o total da linha fazia todas parecer muito abaixo do encomendado.
// Remendo para o histórico: até há pouco uma das rotas gravava aqui o valor COM
// IVA; essas linhas reconhecem-se pela assinatura e são lidas em líquido.
const porcaoEfLiquida = (e, f) => {
    const alocado = parseFloat(e.valor_alocado);
    const efBase = parseFloat(e.ef_valor_total);
    if (!Number.isFinite(alocado)) return Number.isFinite(efBase) ? efBase : 0;
    if (Number.isFinite(efBase) && efBase > 0
        && Math.abs(alocado - efBase * fatorIva(f)) <= 0.02) return efBase;
    return alocado;
};

// Base da fatura, e se a decomposição fecha. Quando base + IVA não dá o total, o
// QR só trouxe a parcela de uma taxa e a base não serve para comparar com nada.
const baseFatura = (f) => {
    const total = parseFloat(f.valor_total) || 0;
    const base = parseFloat(f.subtotal);
    return Number.isFinite(base) ? base : total / fatorIva(f);
};

// Fatura nova com EF escolhidas: as EF têm de cobrir o valor (a mesma regra que
// o servidor aplica ao gravar a ficha e ao marcar como verificada).
// Até 1 € (sem IVA) de diferença a fatura grava-se na mesma.
const TOLERANCIA_EXCESSO_EF = 1;
const msgExcessoEfNova = (excesso, nEfs = 1) =>
    `O valor da fatura é superior ao valor ${nEfs > 1 ? 'das EF escolhidas' : 'da EF escolhida'} em ${fmtMoney(excesso)} (sem IVA). `
    + 'Escolha as EF que faltam, ou grave a fatura sem EF e associe-as depois.';

// Tolerância de 2 cêntimos: o valor da encomenda é guardado sem IVA e a
// multiplicação arredonda, pelo que um desvio de um cêntimo não é divergência.
const TOL_EF = 0.02;

const eAcordoComercial = (f) => {
    const nome = normalizarNomeForn(f.fornecedor);
    return !!nome && FORNECEDORES_ACORDO.some(n => nome.includes(n));
};

/* Caixa de confirmação da própria aplicação.
   O window.confirm do browser não se consegue estilizar: mostra sempre o
   domínio no cabeçalho e alinha o texto à esquerda. Esta é a mesma pergunta,
   com o aviso destacado e tudo centrado.
   Mantém o Escape a cancelar, como fazia a caixa nativa. */
function ConfirmarApagar({ mensagem, onConfirmar, onCancelar }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onCancelar(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onCancelar]);

    return (
        <LiquidDialog onClose={onCancelar} className="gf-modal-overlay" onClick={onCancelar}>
            <div className="gf-conf-box" onClick={e => e.stopPropagation()}>
                <div className="gf-conf-titulo">ATENÇÃO !!</div>
                <div className="gf-conf-texto">{mensagem}</div>
                <div className="gf-conf-botoes">
                    <button className="gf-conf-btn-cancelar" onClick={onCancelar}>Cancelar</button>
                    <button className="gf-conf-btn-ok" onClick={onConfirmar} autoFocus><DeleteIcon /> Apagar</button>
                </div>
            </div>
        </LiquidDialog>
    );
}

function TabFaturasFornecedor() {
    const associacoesDiretas = useRef(new Set());
    const [efsAOcupar, setEfsAOcupar] = useState({});
    const liquidFeedback = useLiquidFeedback();
    // Helpers para persistir estado em localStorage (sobrevive a refresh)
    const LS = 'gf_fat_';
    const readLS = (key, fallback) => {
        try {
            const v = localStorage.getItem(LS + key);
            return v == null ? fallback : JSON.parse(v);
        } catch { return fallback; }
    };
    const writeLS = (key, value) => {
        try { localStorage.setItem(LS + key, JSON.stringify(value)); } catch {}
    };

    const [lista, setLista] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalEF, setModalEF] = useState(null);
    const [modalEditFatura, setModalEditFatura] = useState(null);
    useEffect(() => {
        setModalEditFatura(atual => {
            if (!atual) return atual;
            const linha = lista.find(f => f.id === atual.id);
            if (!linha) return atual;
            return { ...atual, pago: linha.pago, valor_pago: linha.valor_pago,
                data_pagamento: linha.data_pagamento, pagamentos_historico: linha.pagamentos_historico };
        });
    }, [lista]);

    const [avisoDup, setAvisoDup] = useState(null);   // duplicação detetada ao gravar
    const [filtro, setFiltro] = useState(() => readLS('filtro', 'todas')); // 'todas', 'pendentes', 'associadas'
    const [filtroSearch, setFiltroSearch] = useState('');
    // Texto escrito na caixa de pesquisa. Em modo automático é aplicado a cada
    // tecla; em modo manual só passa para filtroSearch com Enter ou o botão.
    const [searchInput, setSearchInput] = useState('');
    const [pesquisaAuto, setPesquisaAuto] = useState(() => readLS('pesquisaAuto', true));
    const [filtroDataInicio, setFiltroDataInicio] = useState('');
    const [filtroDataFim, setFiltroDataFim] = useState('');
    const [filtroRegInicio, setFiltroRegInicio] = useState('');
    const [filtroRegFim, setFiltroRegFim] = useState('');

    // Filtros acumuláveis melhorados — persistidos em localStorage
    const [filtrosDataAtivos, setFiltrosDataAtivos] = useState(() => new Set(['pistola']));
    const [mesesSelecionados, setMesesSelecionados] = useState(() => new Set(readLS('meses', [new Date().getMonth()])));
    const [anoSelecionado, setAnoSelecionado] = useState(() => readLS('ano', new Date().getFullYear()));
    const [todosAtivo, setTodosAtivo] = useState(() => readLS('todos', false));

    // Filtros estilo Excel por coluna + ordenação (persistidos em localStorage)
    const [colFilters, setColFilters] = useState(() => {
        const saved = readLS('colFilters', {});
        // Sets não serializam em JSON — converter arrays de volta a Sets
        return Object.fromEntries(Object.entries(saved).map(([k, v]) => [k, new Set(v)]));
    });
    const [sortCol, setSortCol] = useState(() => readLS('sortCol', null));
    const [sortDir, setSortDir] = useState(() => readLS('sortDir', 'asc'));

    // Modal de data de pagamento — usado quando o utilizador marca uma fatura como paga
    const [pagamentoModal, setPagamentoModal] = useState(null);
    const dataPagamentoRef = useRef(null);
    const pagamentoAbertoId = pagamentoModal?.id;
    useEffect(() => {
        if (pagamentoAbertoId == null) return;
        // O diálogo foca primeiro o painel; focar a data após essa montagem.
        const frame = requestAnimationFrame(() => {
            dataPagamentoRef.current?.focus({ preventScroll: true });
        });
        return () => cancelAnimationFrame(frame);
    }, [pagamentoAbertoId]);

    const pagamentoEmCurso = useRef(false);
    const [aGuardarPagamento, setAGuardarPagamento] = useState(false); // { id, dataPagamento, valorPago, valorTotal }

    // Pagamento em lote: filtrar por fornecedor, marcar as que estão em aberto e
    // liquidar todas com uma única data, em vez de as abrir uma a uma.
    const [selecionadas, setSelecionadas] = useState(() => new Set());
    const [loteModal, setLoteModal] = useState(null);   // { dataPagamento } | null
    const [loteAPagar, setLoteAPagar] = useState(false);
    const [filtroPag, setFiltroPag] = useState(() => readLS('filtroPag', 'todos')); // 'todos' | 'porpagar' | 'pagas'

    // Persistir cada filtro quando muda
    useEffect(() => { writeLS('filtro', filtro); }, [filtro]);
    useEffect(() => { writeLS('filtroPag', filtroPag); }, [filtroPag]);
    useEffect(() => { writeLS('pesquisaAuto', pesquisaAuto); }, [pesquisaAuto]);
    // Ao entrar, filtrar pela data de entrada; a opção Data Fatura é voluntária.
    useEffect(() => { writeLS('meses', Array.from(mesesSelecionados)); }, [mesesSelecionados]);
    useEffect(() => { writeLS('ano', anoSelecionado); }, [anoSelecionado]);
    useEffect(() => { writeLS('todos', todosAtivo); }, [todosAtivo]);
    useEffect(() => {
        const toSave = Object.fromEntries(Object.entries(colFilters).map(([k, v]) => [k, [...v]]));
        writeLS('colFilters', toSave);
    }, [colFilters]);
    useEffect(() => { writeLS('sortCol', sortCol); }, [sortCol]);
    useEffect(() => { writeLS('sortDir', sortDir); }, [sortDir]);

    const applyQuickFilter = useCallback((type) => {
        if (type === 'todos') {
            setTodosAtivo(true);
            setMesesSelecionados(new Set());
            setFiltroDataInicio(''); setFiltroDataFim('');
            setFiltroRegInicio(''); setFiltroRegFim('');
        } else if (type === 'ano') {
            setTodosAtivo(false);
            setMesesSelecionados(new Set([0,1,2,3,4,5,6,7,8,9,10,11]));
        }
    }, []);

    const toggleFiltroFonte = (fonte) => {
        setFiltrosDataAtivos(prev => {
            const next = new Set(prev);
            if (next.has(fonte)) {
                if (next.size > 1) next.delete(fonte); // Garantir pelo menos 1 ativo
            } else { next.add(fonte); }
            return next;
        });
    };

    const toggleMonth = (idx) => {
        setTodosAtivo(false);
        setMesesSelecionados(prev => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx);
            else next.add(idx);
            return next;
        });
    };

    const pedidoListaEf = useRef(null);
    const versaoListaEf = useRef(0);
    const carregarListaEf = useCallback((forcar = false) => {
        // Eventos de foco partilham o pedido; após gravar é obrigatório pedir dados novos.
        if (!forcar && pedidoListaEf.current) return pedidoListaEf.current;
        const versao = ++versaoListaEf.current;
        const pedido = api.get('/gestao-financeira/faturas-reais')
            .then(r => { if (versao === versaoListaEf.current) setLista(r.data); })
            .finally(() => { if (pedidoListaEf.current === pedido) pedidoListaEf.current = null; });
        pedidoListaEf.current = pedido;
        return pedido;
    }, []);
    const fetch = useCallback(async () => {
        setLoading(true);
        try { await carregarListaEf(true); }
        catch (e) { console.error('Erro ao buscar faturas reais:', e); }
        finally { setLoading(false); }
    }, [carregarListaEf]);

    const fetchSilent = useCallback(async () => {
        try { await carregarListaEf(); } catch (e) { /* silencioso */ }
    }, [carregarListaEf]);

    useEffect(() => { fetch(); }, [fetch]);
    const listaAtualEf = useRef(lista);
    listaAtualEf.current = lista;
    const idsPendentesEf = idsLeituraPendente(lista).join(',');
    useEffect(() => {
        if (!idsPendentesEf) return;
        let cancelado = false, emCurso = false, indisponivel = false;
        const timer = setInterval(async () => {
            if (cancelado || emCurso || indisponivel || document.visibilityState !== 'visible') return;
            emCurso = true;
            const originais = new Map(listaAtualEf.current.map(f => [f.id, f]));
            try {
                const resultados = await carregarSugestoesPendentes(api, idsPendentesEf.split(',').map(Number));
                if (!cancelado) setLista(atual => fundirSugestoes(atual, resultados, originais));
            } catch (e) {
                // Compatibilidade durante a atualização do servidor: evitar pedidos repetidos.
                if ([404, 405].includes(e.response?.status)) indisponivel = true;
            } finally { emCurso = false; }
        }, 3000);
        return () => { cancelado = true; clearInterval(timer); };
    }, [idsPendentesEf]);


    // Auto-refresh: refetch quando o tab volta a ficar visível ou a janela ganha foco.
    // Lightweight — zero polling, só reage a eventos do browser.
    useEffect(() => {
        const onVisible = () => { if (document.visibilityState === 'visible') fetchSilent(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', fetchSilent);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', fetchSilent);
        };
    }, [fetchSilent]);

    // Guarda o id enquanto a caixa de confirmação está aberta.
    const [confirmarApagar, setConfirmarApagar] = useState(null);

    const handleDeleteFatura = (id) => setConfirmarApagar(id);

    const apagarFaturaConfirmada = async () => {
        const id = confirmarApagar;
        setConfirmarApagar(null);
        try {
            await api.delete(`/gestao-financeira/faturas-reais/${id}`);
            fetch();
        } catch (e) {
            liquidFeedback.notify('Erro ao apagar: ' + (e.response?.data?.error || e.message));
        }
    };

    // As notas de crédito têm valor_total negativo, e regularizá-las é registar
    // um valor também negativo. Toda a aritmética de pagamento corre no sinal do
    // documento, para que "atingiu o total" signifique liquidado tanto numa
    // fatura de +100 € como numa NC de -100 €.
    const sinalDoc = (v) => ((parseFloat(v) || 0) < 0 ? -1 : 1);

    // Helper: determina estado de pagamento de uma fatura
    const pagoStatus = useCallback((f) => {
        if (!f.pago) return 'nao-pago';
        if (f.valor_pago == null) return 'pago';
        const s = (parseFloat(f.valor_total) || 0) < 0 ? -1 : 1;
        if (parseFloat(f.valor_pago) * s < parseFloat(f.valor_total || 0) * s - 0.005) return 'parc-pago';
        return 'pago';
    }, []);

    // Clicar no pill de pagamento:
    // - "Não pago" → abre modal para registar novo pagamento
    // - "Pago" / "Parc. Pago" → abre modal de gestão (com opção de adicionar mais ou anular)
    const handleTogglePago = (f) => {
        const hoje = new Date().toISOString().slice(0, 10);
        const vTotal = parseFloat(f.valor_total || 0);
        const historico = f.pagamentos_historico || [];
        if (!f.pago) {
            setPagamentoModal({ id: f.id, dataPagamento: hoje, valorPago: vTotal.toFixed(2), valorTotal: vTotal, valorJaPago: 0, historico: [] });
        } else {
            const s = sinalDoc(vTotal);
            const vJaPago = f.valor_pago == null ? vTotal : (parseFloat(f.valor_pago) || 0);
            const falta = vTotal - vJaPago;                  // conserva o sinal do documento
            const emFalta = falta * s > 0.005 ? falta : 0;   // o Math.max(0,…) anterior zerava sempre as NC
            setPagamentoModal({ id: f.id, dataPagamento: hoje, valorPago: '', valorTotal: vTotal, valorJaPago: vJaPago, gerir: true, historico, descontoProntoPagamento: descontoLiquidacao(f) });
        }
    };

    const anularPagamento = async () => {
        if (!pagamentoModal) return;
        if (!await liquidFeedback.confirm('Anular todos os pagamentos registados nesta fatura?')) return;
        try {
            const r = await api.patch(`/gestao-financeira/faturas-reais/${pagamentoModal.id}/pago`, { pago: false });
            setLista(prev => prev.map(x => x.id === pagamentoModal.id ? { ...x, pago: r.data.pago, data_pagamento: r.data.data_pagamento, valor_pago: r.data.valor_pago } : x));
            setPagamentoModal(null);
        } catch (e) { liquidFeedback.notify('Erro ao anular pagamento: ' + (e.response?.data?.error || e.message)); }
    };

    const confirmarPagamento = async () => {
        if (!pagamentoModal || pagamentoEmCurso.current) return;
        const { id, dataPagamento, valorPago } = pagamentoModal;
        const vNovoPagamento = parseFloat(valorPago) || 0;
        // numa NC o valor é negativo; o que não se aceita é zero, ou um valor
        // com o sinal contrário ao do documento
        if (vNovoPagamento * sinalDoc(pagamentoModal.valorTotal) <= 0) return;
        pagamentoEmCurso.current = true;
        setAGuardarPagamento(true);
        try {
            const r = await api.patch(`/gestao-financeira/faturas-reais/${id}/pago`, {
                pago: true,
                data_pagamento: dataPagamento || null,
                valor_pago: vNovoPagamento,
            });
            setLista(prev => prev.map(x => x.id === id
                ? { ...x, pago: r.data.pago, data_pagamento: r.data.data_pagamento, valor_pago: r.data.valor_pago, pagamentos_historico: r.data.pagamentos_historico || [] }
                : x));
            // Actualizar o modal para reflectir o novo estado (não fechar se ainda há em falta)
            const novoHistorico = r.data.pagamentos_historico || [];
            const vTotal = pagamentoModal.valorTotal;
            const totalPago = novoHistorico.reduce((s, p) => s + (parseFloat(p.valor) || 0), 0);
            const emFalta = (vTotal - totalPago) * sinalDoc(vTotal);
            if (emFalta > 0.005) {
                setPagamentoModal(prev => ({
                    ...prev,
                    valorPago: '',
                    mensagem: 'Pagamento de ' + fmtMoney(vNovoPagamento) + ' registado. Pode fechar esta janela.',
                    valorJaPago: totalPago,
                    historico: novoHistorico,
                    gerir: true,
                }));
            } else {
                setPagamentoModal(null);
            }
        } catch (e) {
            liquidFeedback.notify('Erro ao registar pagamento: ' + (e.response?.data?.error || e.message));
        } finally {
            pagamentoEmCurso.current = false;
            setAGuardarPagamento(false);
        }
    };

    // Helpers de extracção dos valores por coluna — usados pelo ColFilter e pelo sort
    const colGetters = useMemo(() => ({
        data_registo: f => f.criado_em ? f.criado_em.slice(0, 10) : '',
        data_fatura: f => f.data_documento ? f.data_documento.slice(0, 10) : '',
        fornecedor: f => f.fornecedor || '',
        numero: f => f.numero_documento_completo || '',
        valor: f => parseFloat(f.valor_total || 0).toFixed(2),
        verificacao: f => f.estado_fatura === 'verificada' ? 'Verificada' : 'Pendente',
        digitalizacao: f => f.estado_digitalizacao === 'concluida' ? 'Concluída' : 'Pendente',
        associacao: f => Array.isArray(f.associated_efs) && f.associated_efs.length > 0
            ? f.associated_efs.map(e => e.ef_numero).join(', ')
            : f.motivo_sem_ef ? f.motivo_sem_ef
            : eAcordoComercial(f) ? 'Acordo comercial' : f.tipo_documento === 'NC' ? 'Nota crédito' : 'Não associada',
        pago: f => {
            if (!f.pago) return 'Não pago';
            if (f.valor_pago == null) return 'Pago';
            const s = (parseFloat(f.valor_total) || 0) < 0 ? -1 : 1;   // NC: aritmética invertida
            if (parseFloat(f.valor_pago) * s < parseFloat(f.valor_total || 0) * s - 0.005) return 'Parc. Pago';
            return 'Pago';
        },
        data_pagamento: f => f.pago && f.data_pagamento ? new Date(f.data_pagamento).toLocaleDateString('pt-PT') : '',
        em_falta: f => {
            if (!f.pago || f.valor_pago == null) return '';
            const s = (parseFloat(f.valor_total) || 0) < 0 ? -1 : 1;   // NC: aritmética invertida
            if (parseFloat(f.valor_pago) * s < parseFloat(f.valor_total || 0) * s - 0.005)
                return parseFloat((parseFloat(f.valor_total || 0) - parseFloat(f.valor_pago || 0)).toFixed(2)).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            return '';
        },
    }), []);

    const filtradaSemColFilters = useMemo(() => {
        return lista.filter(f => {
            if (filtro === 'pendentes' && f.encomenda_fornecedor_id) return false;
            if (filtro === 'associadas' && !f.encomenda_fornecedor_id) return false;

            // Estado de pagamento. "Por pagar" inclui as parcialmente pagas —
            // continuam com valor em aberto. (valor_pago a null = liquidada.)
            const pagoTotal = !!f.pago && f.valor_pago == null;
            if (filtroPag === 'porpagar' && pagoTotal) return false;
            if (filtroPag === 'pagas' && !pagoTotal) return false;

            // Filtro Acumulável de Meses (Lógica OU entre Fatura e Pistola)
            if (!todosAtivo && mesesSelecionados.size > 0) {
                const df = f.data_documento ? new Date(f.data_documento) : null;
                const dp = f.criado_em ? new Date(f.criado_em) : null;

                const matchFatura = filtrosDataAtivos.has('fatura') && df &&
                                    df.getFullYear() === anoSelecionado && mesesSelecionados.has(df.getMonth());

                const matchPistola = filtrosDataAtivos.has('pistola') && dp &&
                                     dp.getFullYear() === anoSelecionado && mesesSelecionados.has(dp.getMonth());

                if (!matchFatura && !matchPistola) return false;
            }

            // Pesquisa Texto
            if (filtroSearch) {
                const s = filtroSearch.toLowerCase();
                const match = (f.fornecedor || '').toLowerCase().includes(s) ||
                              (f.nif_emissor || '').includes(s) ||
                              (f.numero_documento_completo || '').toLowerCase().includes(s);
                if (!match) return false;
            }

            // Filtros Manuais de Data (se preenchidos, têm prioridade ou somam)
            if (filtroDataInicio && f.data_documento < filtroDataInicio) return false;
            if (filtroDataFim && f.data_documento > filtroDataFim) return false;
            if (filtroRegInicio && f.criado_em.split('T')[0] < filtroRegInicio) return false;
            if (filtroRegFim && f.criado_em.split('T')[0] > filtroRegFim) return false;

            return true;
        });
    }, [lista, filtro, filtroPag, filtroSearch, filtroDataInicio, filtroDataFim, filtroRegInicio, filtroRegFim, filtrosDataAtivos, mesesSelecionados, anoSelecionado, todosAtivo]);

    // Aplicar filtros de coluna (Excel) e ordenação por cima do filtrado base
    const filtrada = useMemo(() => {
        let rows = [...filtradaSemColFilters];
        for (const [field, vals] of Object.entries(colFilters)) {
            if (!vals || vals.size === 0) continue;
            const getter = colGetters[field];
            if (!getter) continue;
            rows = rows.filter(r => vals.has(String(getter(r) ?? '')));
        }
        if (sortCol && colGetters[sortCol]) {
            const getter = colGetters[sortCol];
            const isNumeric = sortCol === 'valor';
            rows.sort((a, b) => {
                let va = getter(a), vb = getter(b);
                if (isNumeric) { va = parseFloat(va) || 0; vb = parseFloat(vb) || 0; return sortDir === 'asc' ? va - vb : vb - va; }
                va = String(va || ''); vb = String(vb || '');
                return sortDir === 'asc' ? va.localeCompare(vb, 'pt', { numeric: true }) : vb.localeCompare(va, 'pt', { numeric: true });
            });
        }
        return rows;
    }, [filtradaSemColFilters, colFilters, sortCol, sortDir, colGetters]);

    const totalValor = useMemo(() => filtrada.reduce((acc, f) => acc + parseFloat(f.valor_total || 0), 0), [filtrada]);
    const totalBase = useMemo(() => filtrada.reduce((acc, f) => {
        if (f.subtotal) return acc + parseFloat(f.subtotal);
        const taxa = parseFloat(f.taxa_iva ?? 23);
        return acc + parseFloat(f.valor_total || 0) / (1 + taxa / 100);
    }, 0), [filtrada]);

    // ---- Pagamento em lote --------------------------------------------------
    // Selecionável = está visível E ainda tem valor em aberto. Se um filtro
    // esconder uma fatura selecionada, ela deixa de contar para a operação —
    // assim nunca se marca nada que não esteja à vista ao confirmar.
    const selecionaveis = useMemo(
        () => filtrada.filter(f => !(f.pago && f.valor_pago == null)), [filtrada]);
    const selVisiveis = useMemo(
        () => selecionaveis.filter(f => selecionadas.has(f.id)), [selecionaveis, selecionadas]);
    const todasSelecionadas = selecionaveis.length > 0 && selVisiveis.length === selecionaveis.length;
    const emAbertoDe = (f) => parseFloat(
        (parseFloat(f.valor_total || 0) - (f.valor_pago != null ? parseFloat(f.valor_pago) : 0)).toFixed(2));
    const totalLote = useMemo(
        () => selVisiveis.reduce((s, f) => s + emAbertoDe(f), 0), [selVisiveis]);

    const toggleSelecionada = (id) => setSelecionadas(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const toggleSelecionarTodas = () => setSelecionadas(prev => {
        const next = new Set(prev);
        if (selecionaveis.length > 0 && selecionaveis.every(f => prev.has(f.id))) {
            selecionaveis.forEach(f => next.delete(f.id));
        } else {
            selecionaveis.forEach(f => next.add(f.id));
        }
        return next;
    });

    // Sequencial de propósito: são poucas faturas, e assim uma falha a meio fica
    // identificada por documento, em vez de se perder num Promise.all.
    const confirmarPagamentoLote = async () => {
        if (!loteModal || selVisiveis.length === 0) return;
        const data = loteModal.dataPagamento;
        setLoteAPagar(true);
        const falhas = [];
        for (const f of selVisiveis) {
            const emAberto = emAbertoDe(f);
            if (emAberto === 0) continue;
            try {
                const r = await api.patch(`/gestao-financeira/faturas-reais/${f.id}/pago`, {
                    pago: true, data_pagamento: data || null, valor_pago: emAberto,
                });
                setLista(prev => prev.map(x => x.id === f.id
                    ? { ...x, pago: r.data.pago, data_pagamento: r.data.data_pagamento, valor_pago: r.data.valor_pago, pagamentos_historico: r.data.pagamentos_historico || [] }
                    : x));
            } catch {
                falhas.push(`${f.fornecedor || f.nif_emissor || ''} ${f.numero_documento_completo || ''}`.trim());
            }
        }
        setLoteAPagar(false);
        setLoteModal(null);
        setSelecionadas(new Set());
        if (falhas.length) liquidFeedback.notify(`Não foi possível marcar ${falhas.length} fatura(s):\n\n${falhas.join('\n')}`);
    };

    // ---- Limpar filtros -----------------------------------------------------
    // O ecrã tem sete grupos de filtro e todos persistem em localStorage, pelo
    // que sobrevivem a um refresh. Sem forma de os repor de uma vez, é fácil
    // ficar com um filtro esquecido de outra sessão e concluir que
    // "desapareceram faturas".
    const MES_ATUAL = new Date().getMonth();
    const ANO_ATUAL = new Date().getFullYear();
    const haFiltrosAtivos =
        !!filtroSearch || !!filtroDataInicio || !!filtroDataFim ||
        !!filtroRegInicio || !!filtroRegFim ||
        filtro !== 'todas' || filtroPag !== 'todos' ||
        todosAtivo || anoSelecionado !== ANO_ATUAL ||
        !(mesesSelecionados.size === 1 && mesesSelecionados.has(MES_ATUAL)) ||
        (filtrosDataAtivos.size !== 1 || !filtrosDataAtivos.has('pistola')) ||
        Object.keys(colFilters).length > 0 ||
        sortCol !== null;

    const limparFiltros = () => {
        setFiltroSearch(''); setSearchInput(''); setFiltroDataInicio(''); setFiltroDataFim('');
        setFiltroRegInicio(''); setFiltroRegFim('');
        setFiltro('todas');
        setFiltroPag('todos');
        setFiltrosDataAtivos(new Set(['pistola']));
        setMesesSelecionados(new Set([MES_ATUAL]));
        setAnoSelecionado(ANO_ATUAL);
        setTodosAtivo(false);
        setColFilters({});
        setSortCol(null);
        setSortDir('asc');
        setSelecionadas(new Set());   // a seleção referia-se ao que estava filtrado
    };

    const MONTHS = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
    const curYear = new Date().getFullYear();

    return (
        <div className="gf-faturas">
            <AvisoDuplicacao aviso={avisoDup} onFechar={() => setAvisoDup(null)} />
            <div className="gf-quick-filter-container">
                <div className="gf-quick-row gf-mode-row">
                    <button 
                        className={`gf-mode-btn-s ${filtrosDataAtivos.has('fatura') ? 'active' : ''}`} aria-pressed={filtrosDataAtivos.has('fatura')}
                        onClick={() => toggleFiltroFonte('fatura')}
                    >
                        Data Fatura
                    </button>
                    <button 
                        className={`gf-mode-btn-s ${filtrosDataAtivos.has('pistola') ? 'active' : ''}`} aria-pressed={filtrosDataAtivos.has('pistola')}
                        onClick={() => toggleFiltroFonte('pistola')}
                    >
                        Data Entrada
                    </button>
                </div>

                <div className="gf-quick-row">
                    <button 
                        className={`orc-ano-btn ${todosAtivo ? 'active' : ''}`}
                        onClick={() => applyQuickFilter('todos')}
                    >
                        Todos
                    </button>
                    <button 
                        className={`orc-ano-btn ${!todosAtivo && mesesSelecionados.size === 12 ? 'active' : ''}`}
                        onClick={() => applyQuickFilter('ano')}
                    >
                        Ano Corrente ({curYear})
                    </button>
                </div>
                
                <div className="gf-quick-row gf-months-grid">
                    {MONTHS.map((m, idx) => {
                        const isActive = mesesSelecionados.has(idx);
                        return (
                            <button 
                                key={m} 
                                className={`gf-month-btn ${isActive ? 'active' : ''}`} aria-pressed={isActive}
                                onClick={() => toggleMonth(idx)}
                            >
                                {m}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="gf-dashboard-mini">
                <div className="gf-btn-group">
                    <button className={`gf-btn-s ${filtro === 'todas' ? 'active' : ''}`} onClick={() => setFiltro('todas')}>Todas</button>
                    <button className={`gf-btn-s ${filtro === 'pendentes' ? 'active' : ''}`} onClick={() => setFiltro('pendentes')}>Pendentes</button>
                    <button className={`gf-btn-s ${filtro === 'associadas' ? 'active' : ''}`} onClick={() => setFiltro('associadas')}>Associadas</button>
                </div>

                {/* Estado de pagamento — separado do grupo acima, que diz
                    respeito à associação a encomendas e não a pagamentos. */}
                <div className="gf-btn-group" title="Estado de pagamento">
                    <button className={`gf-btn-s gf-btn-pag ${filtroPag === 'todos' ? 'active' : ''}`} onClick={() => setFiltroPag('todos')}>Todas as Faturas</button>
                    <button className={`gf-btn-s gf-btn-pag ${filtroPag === 'porpagar' ? 'active' : ''}`} onClick={() => setFiltroPag('porpagar')}>Faturas por Pagar</button>
                    <button className={`gf-btn-s gf-btn-pag ${filtroPag === 'pagas' ? 'active' : ''}`} onClick={() => setFiltroPag('pagas')}>Faturas Pagas</button>
                </div>

                <div className="gf-summary-cards">
                    <div className="gf-card-mini">
                        <span className="gf-card-label">TOTAL S/ IVA</span>
                        <span className="gf-card-val">{fmtMoney(totalBase)}</span>
                    </div>
                    <div className="gf-card-mini">
                        <span className="gf-card-label">TOTAL C/ IVA</span>
                        <span className="gf-card-val">{fmtMoney(totalValor)}</span>
                    </div>
                    <div className="gf-card-mini">
                        <span className="gf-card-label">TOTAL FATURAS</span>
                        <span className="gf-card-val">{filtrada.length}</span>
                    </div>
                    <button className="gf-btn-refresh" onClick={fetch} disabled={loading} title="Atualizar listagem">
                        <OrcamentoIcon name={loading ? "clock" : "refresh"} fallback={loading ? '⏳' : '🔄'} />
                    </button>
                </div>
            </div>

            <div className="orc-filtros" style={{ marginTop: '20px' }}>
                <input type="text" placeholder="Pesquisar fornecedor, NIF, documento..."
                    value={searchInput} className="orc-input-search"
                    onChange={e => {
                        setSearchInput(e.target.value);
                        if (pesquisaAuto) setFiltroSearch(e.target.value);
                    }}
                    onKeyDown={e => { if (e.key === 'Enter' && !pesquisaAuto) setFiltroSearch(searchInput); }} />

                {!pesquisaAuto && (
                    <button className={`btn-gf-search gf-search-btn ${searchInput !== filtroSearch ? 'pendente' : ''}`}
                        onClick={() => setFiltroSearch(searchInput)}
                        title={searchInput !== filtroSearch ? 'Há texto por pesquisar — clique ou carregue em Enter' : 'Pesquisar'}>
                        <OrcamentoIcon name="search" fallback="🔍" /> Pesquisar
                    </button>
                )}

                <button type="button" role="switch" aria-checked={pesquisaAuto} aria-label="Pesquisa automática"
                    className={`gf-search-auto ${pesquisaAuto ? 'on' : ''}`}
                    onClick={() => {
                        // Ao ligar o automático, aplica de imediato o que já está escrito.
                        if (!pesquisaAuto) setFiltroSearch(searchInput);
                        setPesquisaAuto(v => !v);
                    }}
                    title={pesquisaAuto
                        ? 'Pesquisa automática: filtra enquanto escreve. Clique para passar a pesquisar só com Enter ou o botão.'
                        : 'Pesquisa manual: filtra com Enter ou o botão Pesquisar. Clique para filtrar enquanto escreve.'}>
                    <span className="gf-search-auto-track"><span className="gf-search-auto-thumb" /></span>
                </button>
                
                <div className="gf-filter-date-group">
                    <label>Fatura:</label>
                    <input type="date" value={filtroDataInicio} onChange={e => setFiltroDataInicio(e.target.value)} className="orc-input-date" />
                    <span className="gf-dash">-</span>
                    <input type="date" value={filtroDataFim} onChange={e => setFiltroDataFim(e.target.value)} className="orc-input-date" />
                </div>

                <div className="gf-filter-date-group">
                    <label>Registo:</label>
                    <input type="date" value={filtroRegInicio} onChange={e => setFiltroRegInicio(e.target.value)} className="orc-input-date" />
                    <span className="gf-dash">-</span>
                    <input type="date" value={filtroRegFim} onChange={e => setFiltroRegFim(e.target.value)} className="orc-input-date" />
                </div>

                {haFiltrosAtivos && (
                    <button className="btn-limpar" onClick={limparFiltros}
                        title="Repõe todos os filtros: pesquisa, datas, associação, pagamento, meses/ano, filtros de coluna e ordenação">
                        <OrcamentoIcon name="clear" fallback="✕" /> Limpar Filtros
                    </button>
                )}
            </div>

            {selVisiveis.length > 0 && (
                <div className="gf-lote-bar">
                    <span className="gf-lote-count">{selVisiveis.length} fatura(s) selecionada(s)</span>
                    <span className="gf-lote-total">Em aberto: <strong>{fmtMoney(totalLote)}</strong></span>
                    <button className="btn-gf-search"
                        onClick={() => setLoteModal({ dataPagamento: new Date().toISOString().slice(0, 10) })}>
                        Marcar como pagas
                    </button>
                    <button className="btn-gf-clear" onClick={() => setSelecionadas(new Set())}>Limpar seleção</button>
                </div>
            )}

            <div className="gf-table-wrap">
                {loading ? <div className="gf-loading">A carregar faturas...</div> : (
                    <table className="gf-table standard-table gf-table-faturas">
                        <thead>
                            <tr>
                                <th className="gf-col-tight" style={{width:28,textAlign:'center'}}>
                                    <input type="checkbox" title="Selecionar todas as que estão em aberto"
                                        checked={todasSelecionadas}
                                        onChange={toggleSelecionarTodas}
                                        disabled={selecionaveis.length === 0} />
                                </th>
                                <th className="gf-col-tight"><ColFilterFat label="Data Registo" field="data_registo" getValue={colGetters.data_registo} data={filtradaSemColFilters} colFilters={colFilters} setColFilters={setColFilters} sortCol={sortCol} setSortCol={setSortCol} sortDir={sortDir} setSortDir={setSortDir} /></th>
                                <th className="gf-col-tight"><ColFilterFat label="Data Fatura" field="data_fatura" getValue={colGetters.data_fatura} data={filtradaSemColFilters} colFilters={colFilters} setColFilters={setColFilters} sortCol={sortCol} setSortCol={setSortCol} sortDir={sortDir} setSortDir={setSortDir} /></th>
                                <th><ColFilterFat label="Fornecedor" field="fornecedor" getValue={colGetters.fornecedor} data={filtradaSemColFilters} colFilters={colFilters} setColFilters={setColFilters} sortCol={sortCol} setSortCol={setSortCol} sortDir={sortDir} setSortDir={setSortDir} /></th>
                                <th className="gf-col-tight"><ColFilterFat label="N.º Documento" field="numero" getValue={colGetters.numero} data={filtradaSemColFilters} colFilters={colFilters} setColFilters={setColFilters} sortCol={sortCol} setSortCol={setSortCol} sortDir={sortDir} setSortDir={setSortDir} /></th>
                                <th className="gf-th-valor"><ColFilterFat label="Valor" field="valor" getValue={colGetters.valor} data={filtradaSemColFilters} colFilters={colFilters} setColFilters={setColFilters} sortCol={sortCol} setSortCol={setSortCol} sortDir={sortDir} setSortDir={setSortDir} /></th>
                                <th className="gf-col-tight tc"><span className="gf-doc-titulo">Doc.</span></th>
                                <th className="gf-col-tight tc"><ColFilterFat label="Verificação" field="verificacao" getValue={colGetters.verificacao} data={filtradaSemColFilters} colFilters={colFilters} setColFilters={setColFilters} sortCol={sortCol} setSortCol={setSortCol} sortDir={sortDir} setSortDir={setSortDir} /></th>
                                <th className="gf-col-tight tc"><ColFilterFat label="Digitalização" field="digitalizacao" getValue={colGetters.digitalizacao} data={filtradaSemColFilters} colFilters={colFilters} setColFilters={setColFilters} sortCol={sortCol} setSortCol={setSortCol} sortDir={sortDir} setSortDir={setSortDir} /></th>
                                <th className="gf-col-tight gf-th-assoc"><div className="gf-assoc-layout"><ColFilterFat label="Associação" field="associacao" getValue={colGetters.associacao} data={filtradaSemColFilters} colFilters={colFilters} setColFilters={setColFilters} sortCol={sortCol} setSortCol={setSortCol} sortDir={sortDir} setSortDir={setSortDir} /></div></th>
                                <th className="gf-col-tight gf-th-ef-valor" title="Diferença entre o valor encomendado e o faturado">€/EF</th>
                                <th className="gf-col-tight"><ColFilterFat label="Ações" field="pago" getValue={colGetters.pago} data={filtradaSemColFilters} colFilters={colFilters} setColFilters={setColFilters} sortCol={sortCol} setSortCol={setSortCol} sortDir={sortDir} setSortDir={setSortDir} /></th>
                                <th className="gf-col-tight"><ColFilterFat label="Pago Em" field="data_pagamento" getValue={colGetters.data_pagamento} data={filtradaSemColFilters} colFilters={colFilters} setColFilters={setColFilters} sortCol={sortCol} setSortCol={setSortCol} sortDir={sortDir} setSortDir={setSortDir} /></th>
                                <th className="gf-col-tight"><ColFilterFat label="Em Falta" field="em_falta" getValue={colGetters.em_falta} data={filtradaSemColFilters} colFilters={colFilters} setColFilters={setColFilters} sortCol={sortCol} setSortCol={setSortCol} sortDir={sortDir} setSortDir={setSortDir} /></th>
                                <th style={{ position: 'sticky', right: 0, background: '#f8fafc', zIndex: 1 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtrada.length === 0 ? (
                                <tr><td colSpan={15} className="gf-empty">Nenhuma fatura encontrada</td></tr>
                            ) : filtrada.map(f => (
                                <tr key={f.id} className={`gf-row gf-row-clickable${pagoStatus(f) === 'pago' ? ' gf-row-pago' : pagoStatus(f) === 'parc-pago' ? ' gf-row-parc-pago' : ''}${eAcordoComercial(f) ? '' : f.ef_sugerida_id ? ' gf-row-sugerida' : (!f.encomenda_fornecedor_id ? ' gf-row-pendente' : '')}`}
                                    onClick={() => setModalEditFatura(f)}>
                                    {/* stopPropagation: a linha inteira abre a ficha da fatura */}
                                    <td className="tc gf-col-tight" onClick={e => e.stopPropagation()}>
                                        {!(f.pago && f.valor_pago == null) && (
                                            <input type="checkbox"
                                                checked={selecionadas.has(f.id)}
                                                onChange={() => toggleSelecionada(f.id)} />
                                        )}
                                    </td>
                                    <td className="gf-cell-date gf-col-tight">
                                        {new Date(f.criado_em).toLocaleDateString('pt-PT')}
                                    </td>
                                    <td className="gf-cell-date gf-col-tight">{fmtDate(f.data_documento)}</td>
                                    <td className="gf-fornecedor-cell">
                                        {f.fornecedor
                                            ? <span className="gf-fornecedor-nome" title={f.fornecedor}>{f.fornecedor}</span>
                                            : <span className="gf-fornecedor-pendente">Nome Pendente</span>}
                                        <span className="gf-nif-tag">NIF: {f.nif_emissor}</span>
                                    </td>
                                    <td className="gf-cell-doc gf-col-tight"><span className="gf-numero-texto">{f.numero_documento_completo}</span></td>
                                    <td className="tr gf-cell-valor">
                                        {f.tipo_documento === 'NC' && (
                                            <span className="gf-credit-badge" style={{display:'inline-block', background:'#fee2e2', color:'#dc2626', border:'1px solid #fca5a5', borderRadius:4, fontSize:10, fontWeight:700, padding:'1px 5px', marginRight:5, verticalAlign:'middle'}}>NC</span>
                                        )}
                                        <span className={f.tipo_documento === 'NC' ? 'gf-numero-texto gf-credit-amount' : 'gf-numero-texto'} style={f.tipo_documento === 'NC' ? {color:'#dc2626', fontWeight:700} : {}}>
                                            {fmtMoney(f.tipo_documento === 'NC' ? -Math.abs(parseFloat(f.valor_total||0)) : parseFloat(f.valor_total||0))}
                                        </span>
                                    </td>
                                    <td className="tc gf-col-tight">
                                        {f.caminho_foto ? (
                                            <button className="gf-doc-link" title="Ver Fatura Original"
                                                onClick={async e => {
                                                    e.stopPropagation();
                                                    try {
                                                        const res = await api.get(`/gestao-financeira/documento/${f.id}`, { responseType: 'blob' });
                                                        const blobUrl = URL.createObjectURL(res.data);
                                                        window.open(blobUrl, '_blank');
                                                    } catch { liquidFeedback.notify('Erro ao abrir documento'); }
                                                }}>
                                                <OrcamentoIcon name="document" fallback="🖼️" />
                                            </button>
                                        ) : '—'}
                                    </td>
                                    <td className="tc gf-col-tight">
                                        {f.estado_fatura === 'verificada' ? (
                                            <span className="gf-badge badge-green">Verificada</span>
                                        ) : (
                                            <span
                                                className="gf-badge badge-yellow gf-badge-clickable"
                                                title="Clique para marcar como Verificada"
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    try {
                                                        const fd = new FormData();
                                                        fd.append('estado_fatura', 'verificada');
                                                        await api.patch(`/gestao-financeira/faturas-reais/${f.id}`, fd);
                                                        fetchSilent();
                                                    } catch (err) {
                                                        // p.ex. a fatura é superior às EF associadas
                                                        liquidFeedback.notify(err.response?.data?.error || err.message);
                                                    }
                                                }}
                                            >
                                                Pendente
                                            </span>
                                        )}
                                    </td>
                                    <td className="tc gf-col-tight">
                                        <span className={`gf-badge ${f.estado_digitalizacao === 'concluida' ? 'badge-green' : 'badge-red'}`}>
                                            {f.estado_digitalizacao === 'concluida' ? 'Concluída' : 'Pendente'}
                                        </span>
                                    </td>
                                    <td className="gf-col-tight gf-td-assoc">
                                        <div className="gf-assoc-layout">
                                        {Array.isArray(f.associated_efs) && f.associated_efs.length > 0 ? (
                                            <EFChipsCarousel efs={f.associated_efs} onOpen={async (efId) => {
                                                try {
                                                    const r = await api.get(`/gestao-financeira/encomendas-fornecedor/${efId}`);
                                                    setModalEF(r.data);
                                                } catch (e) { liquidFeedback.notify('Erro ao carregar EF'); }
                                            }} />
                                        ) : eAcordoComercial(f) ? (
                                            // verde como as EF associadas: é um caso resolvido,
                                            // não uma associação por fazer
                                            <span className="gf-ef-tag gf-ef-tag-ok"
                                                title="Fornecedor com acordo comercial — não há encomenda a associar">
                                                Acordo comercial
                                            </span>
                                        ) : f.sugestao_ef && !['associada', 'dispensada'].includes(f.sugestao_ef.estado) ? (
                                            <button type="button" className="gf-btn-associar-ef" disabled={!!efsAOcupar[f.id]} title={f.sugestao_ef.leitura?.aviso || f.sugestao_ef.motivo}
                                                onClick={async e => {
                                                    e.stopPropagation();
                                                    const candidata = f.sugestao_ef.candidatos?.find(c => c.chave === f.sugestao_ef.recomendada);
                                                    // O clique em ASSOCIAR confirma a referência apresentada.
                                                    const confirmarReferencia = f.sugestao_ef.estado === 'referencia' && candidata?.pode_associar;
                                                    if (!f.sugestao_ef.associacao_direta && !confirmarReferencia) { setModalEditFatura(f); return; }
                                                    if (associacoesDiretas.current.has(f.id)) return;
                                                    associacoesDiretas.current.add(f.id); setEfsAOcupar(v => ({ ...v, [f.id]: true }));
                                                    try {
                                                        await api.post("/gestao-financeira/faturas-reais/" + f.id + "/associar-sugestao", { chave: f.sugestao_ef.recomendada, direta: !!f.sugestao_ef.associacao_direta });
                                                        await fetch();
                                                    } catch (err) { liquidFeedback.notify(err.response?.data?.error || "Não foi possível associar. Volte a consultar a fatura."); }
                                                    finally { associacoesDiretas.current.delete(f.id); setEfsAOcupar(v => ({ ...v, [f.id]: false })); }
                                                }}>
                                                {efsAOcupar[f.id] ? 'A ASSOCIAR…' : f.sugestao_ef.leitura?.estado === 'indisponivel' ? 'LEITURA INDISPONÍVEL' : f.sugestao_ef.estado === 'pendente' ? 'A LER DOCUMENTO' : f.sugestao_ef.estado === 'referencia' ? <ReferenciaEF numero={f.ef_sugerida_numero} /> : f.sugestao_ef.estado === 'provavel' ? 'SUGESTÃO PROVÁVEL' : 'POR CONFIRMAR'}
                                            </button>
                                        ) : f.ef_sugerida_id ? (
                                            /* Sem confirmação: associar é reversível (remover a EF na ficha
                                               da fatura desfaz por completo) e a confirmação obrigava a
                                               dois cliques por cada EF. O caminho destrutivo do backend
                                               — fundir dois registos — exige ?fundir=true, que este botão
                                               não envia: em caso de conflito o servidor devolve 409 e não
                                               altera nada. */
                                            <button className="gf-btn-associar-ef"
                                                title={`Associar à ${f.ef_sugerida_numero} (reversível na ficha da fatura)`}
                                                onClick={async e => {
                                                e.stopPropagation();
                                                try {
                                                    await api.post(`/gestao-financeira/faturas-reais/${f.id}/associar-ef/${f.ef_sugerida_id}`);
                                                    fetch();
                                                } catch (err) {
                                                    // o 409 traz {conflict, existente} e não um campo error,
                                                    // pelo que sem este ramo aparecia "Erro: undefined"
                                                    const ex = err.response?.data?.existente;
                                                    if (err.response?.status === 409 && ex) {
                                                        liquidFeedback.notify(`A ${f.ef_sugerida_numero} já tem outro registo associado:\n\n`
                                                            + `  ${ex.fornecedor || 'Sem nome'}\n`
                                                            + `  ${ex.numero_documento_completo || 'sem nº'}\n\n`
                                                            + 'Abra a ficha da fatura para decidir o que fazer.');
                                                    } else {
                                                        liquidFeedback.notify('Erro: ' + (err.response?.data?.error || err.message));
                                                    }
                                                }
                                            }}><ReferenciaEF numero={f.ef_sugerida_numero} /></button>
                                        ) : (
                                            /* laranja, não verde: o verde é o estado resolvido
                                               (EF associada ou acordo comercial), e igualar as
                                               cores tornava os dois indistinguíveis de relance */
                                            f.motivo_sem_ef ? (
                                                /* mesma etiqueta das EF: é um caso resolvido */
                                                <span className="gf-ef-tag gf-ef-tag-ok" title="Compra sem encomenda a fornecedor, para consumo da empresa">{f.motivo_sem_ef}</span>
                                            ) : (
                                                <span className={f.tipo_documento === 'NC' ? 'gf-ef-tag gf-ef-tag-ok' : 'gf-badge badge-orange'}>{f.tipo_documento === 'NC' ? 'Nota crédito' : 'Não associada'}</span>
                                            )
                                        )}
                                    </div>
                                    </td>
                                    <td className="tr gf-cell-ef-valor gf-col-tight" onClick={e => e.stopPropagation()}>
                                        {(() => {
                                            // Relatório de exceções. Mostrar o valor da encomenda ao lado do
                                            // da fatura obrigava a compará-los a olho, linha a linha, e como
                                            // quase sempre coincidem a coluna parecia não servir para nada.
                                            // Agora: ✓ quando bate certo, e só o desvio quando não bate.
                                            if (eAcordoComercial(f)) {
                                                return <span className="gf-ef-ok"
                                                    title="Acordo comercial — não há encomenda a comparar"><OrcamentoIcon name="check" fallback="✓" /></span>;
                                            }
                                            const temEfs = Array.isArray(f.associated_efs) && f.associated_efs.length > 0;
                                            if (!temEfs) {
                                                // EF apenas sugerida: ainda não há associação, logo nada a cruzar
                                                if (!f.ef_sugerida_valor) return null;
                                                return <span className="gf-ef-sug"
                                                    title="Valor da encomenda sugerida (s/ IVA) — ainda não associada">
                                                    {fmtMoney(parseFloat(f.ef_sugerida_valor))}
                                                </span>;
                                            }
                                            // A base é usada tal como está no registo, mesmo quando
                                            // não fecha com o total. Havia aqui um ⚠ para esse caso,
                                            // mas a correção do IVA é feita à mão na ficha e o aviso
                                            // só tirava o lugar ao número. Corrigida a base, a coluna
                                            // acerta sozinha. O modal mantém a explicação, que é onde
                                            // a correção se faz.
                                            // Tudo em LÍQUIDO: é a unidade da encomenda, e evita o
                                            // cêntimo de arredondamento que aparecia ao reconstruir
                                            // o valor com IVA a partir do líquido.
                                            const efBase = f.associated_efs.reduce(
                                                (s, e) => s + porcaoEfLiquida(e, f), 0);
                                            const base = f.tipo_documento === 'NC'
                                                ? -Math.abs(baseFatura(f))
                                                : baseFatura(f);
                                            const dif = parseFloat((base - efBase).toFixed(2));
                                            if (Math.abs(dif) <= TOL_EF) {
                                                return <span className="gf-ef-ok"
                                                    title={`Coincide com o alocado da encomenda (${fmtMoney(efBase)} s/ IVA)`}><OrcamentoIcon name="check" fallback="✓" /></span>;
                                            }
                                            return <span className="gf-ef-dif"
                                                title={`Encomenda atribuída a esta fatura: ${fmtMoney(efBase)} · Base da fatura: ${fmtMoney(base)} (valores s/ IVA)`}>
                                                {dif > 0 ? '+' : ''}{fmtMoney(dif)}
                                            </span>;
                                        })()}
                                    </td>
                                    <td className="gf-td-acoes gf-col-tight" onClick={e => e.stopPropagation()}>
                                        <button className={`btn-pago btn-pago-fixed ${pagoStatus(f)}`}
                                            onClick={() => handleTogglePago(f)}
                                            title={descontoLiquidacao(f) ? 'Liquidada com desconto de pronto pagamento de ' + fmtMoney(descontoLiquidacao(f)) : f.pago ? 'Gerir pagamento' : 'Registar pagamento'}>
                                            {pagoStatus(f) === 'pago' ? 'Pago' : pagoStatus(f) === 'parc-pago' ? 'Parc. Pago' : 'Não pago'}
                                        </button>
                                    </td>
                                    <td className="gf-td-pagoed gf-col-tight" onClick={e => e.stopPropagation()}>
                                        {f.pago && f.data_pagamento && (
                                            <span className="gf-pago-date" title="Data de pagamento">
                                                {new Date(f.data_pagamento).toLocaleDateString('pt-PT')}
                                            </span>
                                        )}
                                    </td>
                                    <td className="gf-td-emfalta gf-col-tight" onClick={e => e.stopPropagation()}>
                                        {pagoStatus(f) === 'parc-pago' && (
                                            <span className="gf-em-falta parc" title="Valor em falta">
                                                {fmtMoney(parseFloat(f.valor_total || 0) - parseFloat(f.valor_pago || 0))}
                                            </span>
                                        )}
                                    </td>
                                    <td className="gf-td-del" onClick={e => e.stopPropagation()}>
                                        {pagoStatus(f) === 'nao-pago' && (
                                            <button className="btn-delete-icon" onClick={() => handleDeleteFatura(f.id)} title="Apagar Registo"><DeleteIcon /></button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {modalEF && (
                <ModalEF ef={modalEF} onClose={() => setModalEF(null)} onSaved={fetch} />
            )}

            {loteModal && (
                <LiquidDialog onClose={() => { if (!loteAPagar) setLoteModal(null); }} className="gf-modal-overlay" onClick={() => { if (!loteAPagar) setLoteModal(null); }}>
                    <div className="gf-modal" onClick={e => e.stopPropagation()} style={{maxWidth: 420}}>
                        <div className="gf-modal-header">
                            <h3>Marcar {selVisiveis.length} fatura(s) como pagas</h3>
                            <button className="gf-modal-close" onClick={() => setLoteModal(null)} disabled={loteAPagar}><OrcamentoIcon name="close" fallback="×" /></button>
                        </div>
                        <div className="gf-modal-body">
                            <div className="gf-field">
                                <label>Data do pagamento</label>
                                <input type="date" value={loteModal.dataPagamento} autoFocus
                                    onChange={e => setLoteModal(prev => ({ ...prev, dataPagamento: e.target.value }))} />
                            </div>
                            <div className="gf-parc-falta-row">
                                <span className="gf-parc-falta-label">Total a liquidar</span>
                                <span className="gf-parc-falta-val">{fmtMoney(totalLote)}</span>
                            </div>
                            <p style={{fontSize:12,color:'#6b7280',margin:'10px 0 0'}}>
                                Cada fatura é liquidada pelo seu valor ainda em aberto, com esta data.
                            </p>
                        </div>
                        <div className="gf-modal-footer">
                            <button className="btn-gf-clear" onClick={() => setLoteModal(null)} disabled={loteAPagar}>Cancelar</button>
                            <button className="btn-gf-search" onClick={confirmarPagamentoLote}
                                disabled={loteAPagar || selVisiveis.length === 0}>
                                {loteAPagar ? 'A marcar…' : 'Confirmar'}
                            </button>
                        </div>
                    </div>
                </LiquidDialog>
            )}
            {modalEditFatura && (
                <ModalEditFatura
                    fatura={modalEditFatura}
                    listaFaturas={lista}
                    onPagamento={handleTogglePago}
                    onClose={() => setModalEditFatura(null)}
                    onSaved={(opts) => {
                        fetch();
                        if (opts?.aviso) setAvisoDup(opts.aviso);
                        if (!opts?.keepOpen) setModalEditFatura(null);
                    }}
                />
            )}
            {pagamentoModal && (() => {
                const vNovoInput = parseFloat(pagamentoModal.valorPago) || 0;
                const vTotal = pagamentoModal.valorTotal || 0;
                const vJaPago = pagamentoModal.valorJaPago || 0;
                const descontoPronto = pagamentoModal.descontoProntoPagamento || 0;
                const historico = pagamentoModal.historico || [];
                const isAdicional = Math.abs(vJaPago) > 0.005;
                const isGerir = !!pagamentoModal.gerir;
                const s = sinalDoc(vTotal);
                const vTotalFinal = vJaPago + vNovoInput;
                const emFaltaFinal = vTotal - vTotalFinal;
                const ficaPago = vTotalFinal * s >= vTotal * s - 0.005;
                const temNovoPagamento = vNovoInput * s > 0.005;
                return (
                <LiquidDialog onClose={() => setPagamentoModal(null)} className="gf-modal-overlay" onClick={() => setPagamentoModal(null)}>
                    <div className="gf-modal" onClick={e => e.stopPropagation()} style={{maxWidth: 420}}>
                        <div className="gf-modal-header">
                            <h3>{isGerir ? 'Gerir Pagamento' : isAdicional ? 'Adicionar Pagamento' : 'Registar Pagamento'}</h3>
                            <button className="gf-modal-close" onClick={() => setPagamentoModal(null)}><OrcamentoIcon name="close" fallback="×" /></button>
                        </div>
                        <div className="gf-modal-body">
                            {historico.length > 0 && (
                                <div className="gf-pag-historico">
                                    <div className="gf-pag-historico-title">Histórico de pagamentos</div>
                                    {historico.map((p, i) => (
                                        <div key={i} className="gf-pag-historico-row">
                                            <span className="gf-pag-hist-n">#{i+1}</span>
                                            <span className="gf-pag-hist-data">{new Date(p.data).toLocaleDateString('pt-PT')}</span>
                                            <span className="gf-pag-hist-valor">{fmtMoney(p.valor)}</span>
                                            <button className="gf-pag-hist-del" title="Remover este pagamento" onClick={async () => {
                                                const novoHist = historico.filter((_, j) => j !== i);
                                                try {
                                                    const r = await api.patch(`/gestao-financeira/faturas-reais/${pagamentoModal.id}/pago-historico`, { historico: novoHist });
                                                    setLista(prev => prev.map(x => x.id === pagamentoModal.id ? { ...x, pago: r.data.pago, data_pagamento: r.data.data_pagamento, valor_pago: r.data.valor_pago, pagamentos_historico: r.data.pagamentos_historico || [] } : x));
                                                    if (novoHist.length === 0) { setPagamentoModal(null); return; }
                                                    const vTotal2 = pagamentoModal.valorTotal;
                                                    const totalPago2 = novoHist.reduce((s, p) => s + (parseFloat(p.valor) || 0), 0);
                                                    const emFalta2 = Math.max(0, vTotal2 - totalPago2);
                                                    setPagamentoModal(prev => ({ ...prev, historico: novoHist, valorJaPago: totalPago2, valorPago: '', mensagem: '', descontoProntoPagamento: 0 }));
                                                } catch(e) { liquidFeedback.notify('Erro: ' + (e.response?.data?.error || e.message)); }
                                            }}><DeleteIcon /></button>
                                        </div>
                                    ))}
                                    {isAdicional && <div className="gf-pag-historico-total">
                                        <span>Total pago</span><span>{fmtMoney(vJaPago - descontoPronto)}</span>
                                        {(vTotal - vJaPago) * s > 0.005 && <><span style={{marginLeft:8}}>Em falta</span><span style={{color:'#dc2626'}}>{fmtMoney(vTotal - vJaPago)}</span></>}
                                    </div>}
                                </div>
                            )}
                            {descontoPronto > 0 && <div className="gf-pag-historico" role="status">Liquidada com desconto de pronto pagamento de <strong>{fmtMoney(descontoPronto)}</strong>, sem nota de crédito. Em falta: <strong>0,00 €</strong>.</div>}
                            <div className="gf-field">
                                <label>Data do Pagamento</label>
                                <input type="date"
                                    ref={dataPagamentoRef}
                                    value={pagamentoModal.dataPagamento}
                                    onChange={e => setPagamentoModal(prev => ({ ...prev, dataPagamento: e.target.value }))}
                                    autoFocus />
                            </div>
                            <div className="gf-field">
                                <label>{isAdicional ? 'Novo pagamento a adicionar (€)' : 'Valor Pago (€)'} <span style={{fontWeight:400,color:'#6b7280'}}>— total fatura: {fmtMoney(vTotal)}</span></label>
                                <input type="number" step="0.01"
                                    min={s < 0 ? undefined : 0} max={s < 0 ? 0 : undefined}
                                    value={pagamentoModal.valorPago}
                                    disabled={aGuardarPagamento || descontoPronto > 0}
                                    onChange={e => setPagamentoModal(prev => ({ ...prev, valorPago: e.target.value, mensagem: '' }))} />
                            </div>
                            {pagamentoModal.mensagem && <p role="status" style={{color:'#206644',fontSize:13}}>{pagamentoModal.mensagem}</p>}
                            {temNovoPagamento && (
                                <div><p style={{fontSize:12,margin:'0 0 6px'}}>Após confirmar este novo pagamento:</p>
                                <div className="gf-parc-falta-row">
                                    {ficaPago ? (
                                        <><span className="gf-parc-falta-label">Total pago</span><span className="gf-parc-falta-val" style={{color:'#065f46'}}>{fmtMoney(vTotalFinal)}</span><span className="gf-parc-falta-badge" style={{background:'#d1fae5',color:'#065f46'}}>Pago</span></>
                                    ) : (
                                        <><span className="gf-parc-falta-label">Total pago</span><span className="gf-parc-falta-val">{fmtMoney(vTotalFinal)}</span><span className="gf-parc-falta-label" style={{marginLeft:8}}>Em falta</span><span className="gf-parc-falta-val">{fmtMoney(emFaltaFinal)}</span><span className="gf-parc-falta-badge">Parc. Pago</span></>
                                    )}
                                </div>
                                </div>
                            )}
                        </div>
                        <div className="gf-modal-footer" style={{justifyContent:'space-between'}}>
                            <button className="btn-gf-clear" style={{color:'#991b1b',borderColor:'#fca5a5'}} onClick={anularPagamento} title="Anular todos os pagamentos">
                                <DeleteIcon /> Anular pagamento
                            </button>
                            <div data-liquid-actions="true" style={{display:'flex',gap:8}}>
                                <button className="btn-gf-clear" onClick={() => setPagamentoModal(null)}>Cancelar</button>
                                <button className="btn-gf-search" onClick={confirmarPagamento} disabled={!temNovoPagamento || aGuardarPagamento}>{aGuardarPagamento ? 'A guardar…' : 'Confirmar'}</button>
                            </div>
                        </div>
                    </div>
                </LiquidDialog>
                );
            })()}
            {confirmarApagar !== null && (
                <ConfirmarApagar
                    mensagem="Tem a certeza que deseja apagar este registo e o ficheiro anexado?"
                    onConfirmar={apagarFaturaConfirmada}
                    onCancelar={() => setConfirmarApagar(null)}
                />
            )}
        </div>
    );
}

/* ============================================================
   MODAL EDIÇÃO DE FATURA FORNECEDOR
   ============================================================ */
const extractNifFromAtcud = (atcud) => {
    if (!atcud) return '';
    const m = atcud.match(/(?:^|\*)A:([^*]+)/);
    return m ? m[1] : '';
};

function ModalEditFatura({ fatura, listaFaturas, onClose, onSaved, onPagamento, isNew, initialPreEfs }) {
    const valorInputId = useId();
    const liquidFeedback = useLiquidFeedback();
    const [fornecedor, setFornecedor] = useState(fatura.fornecedor || '');
    const [nif, setNif] = useState(fatura.nif_emissor || extractNifFromAtcud(fatura.atcud) || '');
    const [valor, setValor] = useState(fatura.valor_total ? parseFloat(fatura.valor_total).toFixed(2) : '');
    const initTaxa = fatura.taxa_iva != null ? parseFloat(fatura.taxa_iva) : 23;
    const initTotal = parseFloat(fatura.valor_total) || 0;
    const initBase = fatura.subtotal ? parseFloat(fatura.subtotal) : (initTaxa > 0 ? parseFloat((initTotal / (1 + initTaxa / 100)).toFixed(2)) : initTotal);
    const initIva = fatura.valor_iva ? parseFloat(fatura.valor_iva) : parseFloat((initTotal - initBase).toFixed(2));
    const [taxaIva, setTaxaIva] = useState(initTaxa);
    const [subtotal, setSubtotal] = useState(initBase ? initBase.toFixed(2) : '');
    const [valorIva, setValorIva] = useState(initIva ? initIva.toFixed(2) : '');
    // Faturas com mais do que uma taxa — transportes com portagens isentas ao
    // lado do serviço a 23%, tipicamente. Vazio significa taxa única, e é o
    // estado de tudo o que foi lançado até aqui: essas faturas continuam a usar
    // os campos de sempre, sem passarem por nada disto.
    const [linhasIva, setLinhasIva] = useState(
        Array.isArray(fatura.decomposicao_iva) && fatura.decomposicao_iva.length > 1
            ? fatura.decomposicao_iva.map(l => ({
                taxa: String(l.taxa ?? 23),
                base: l.base != null ? parseFloat(l.base).toFixed(2) : '',
                iva: l.iva != null ? parseFloat(l.iva).toFixed(2) : '',
            }))
            : []);
    const multiIva = linhasIva.length > 0;
    const [numDoc, setNumDoc] = useState(fatura.numero_documento_completo || '');
    // Motivo de não haver encomenda (consumo interno, etc.)
    const [motivoSemEf, setMotivoSemEf] = useState(fatura.motivo_sem_ef || '');
    const [dataDoc, setDataDoc] = useState(fatura.data_documento ? fatura.data_documento.substring(0, 10) : '');
    const [dataRegisto, setDataRegisto] = useState(fatura.criado_em ? fatura.criado_em.substring(0, 10) : '');
    const temAtcud = !!fatura.atcud;

    /* ---- Sugestão de fornecedor (só quando a fatura NÃO tem QR) ----------
       Com QR, o NIF e o nome vêm lidos do documento e não há nada a escolher.
       Sem QR — as faturas estrangeiras, tipicamente — tudo é escrito à mão, e
       é aí que a lista poupa o trabalho e evita o NIF mal copiado.

       Estrangeiro = NIF que não seja 9 dígitos depois de tirar o "PT" e os
       separadores. Assim "502 044 403" continua a contar como português, e o
       "B04267225" da Makito como estrangeiro. A classificação só decide o que
       aparece PRIMEIRO: quem escreve procura em todos, porque o campo pais da
       tabela está vazio em dois terços dos registos e não é de confiança. */
    const [fornecedoresBD, setFornecedoresBD] = useState([]);
    const [sugestoesForn, setSugestoesForn] = useState(false);
    const refForn = useRef(null);
    useEffect(() => {
        if (temAtcud) return;
        api.get('/fornecedores')
            .then(r => setFornecedoresBD(Array.isArray(r.data) ? r.data : []))
            .catch(() => {});
    }, [temAtcud]);
    useEffect(() => {
        if (!sugestoesForn) return;
        const fora = (e) => { if (refForn.current && !refForn.current.contains(e.target)) setSugestoesForn(false); };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [sugestoesForn]);

    const eEstrangeiro = (f) => {
        const n = String(f.nif || '').replace(/^PT/i, '').replace(/[^0-9A-Za-z]/g, '');
        return !!n && !/^[0-9]{9}$/.test(n);
    };
    const fornecedoresSugeridos = useMemo(() => {
        const limpar = (t) => (t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const alvo = limpar((fornecedor || '').trim());
        const ativos = fornecedoresBD.filter(f => f.ativo !== false && (f.nome || '').trim());
        if (!alvo) return ativos.filter(eEstrangeiro).sort((a, b) => a.nome.localeCompare(b.nome, 'pt'));
        // a escrever: procura em todos, por nome ou por NIF
        return ativos
            .filter(f => limpar(f.nome).includes(alvo) || limpar(f.nif).includes(alvo))
            .sort((a, b) => a.nome.localeCompare(b.nome, 'pt'))
            .slice(0, 40);
    }, [fornecedoresBD, fornecedor]);

    const escolherFornecedor = (f) => {
        setFornecedor(f.nome || '');
        if (f.nif) setNif(String(f.nif).replace(/\s+/g, ''));
        setSugestoesForn(false);
    };

    const [estadoFatura, setEstadoFatura] = useState(fatura.estado_fatura || 'pendente');
    const [isNC, setIsNC] = useState(fatura.tipo_documento === 'NC');
    // Ficheiros a carregar: array para suportar multi-página (vários PDFs/imagens fundidos num só PDF no backend)
    const [fotoFiles, setFotoFiles] = useState([]);
    const [previewIdx, setPreviewIdx] = useState(0);
    const fotoFile = fotoFiles[Math.min(previewIdx, Math.max(0, fotoFiles.length - 1))] || null;
    const [saving, setSaving] = useState(false);
    const [erro, setErro] = useState('');
    const [openingDoc, setOpeningDoc] = useState(false);
    // Blob URL para preview do documento existente (PDF ou imagem) — evita carregar SPA dentro do iframe
    const [docBlobUrl, setDocBlobUrl] = useState(null);
    // docRev: incrementa após ações que alteram o ficheiro no servidor (ex: append-pages)
    // para forçar o useEffect abaixo a re-fetch sem depender da mudança de caminho_foto
    const [docRev, setDocRev] = useState(0);
    useEffect(() => {
        let cancelled = false;
        let createdUrl = null;
        if (fatura.caminho_foto && fatura.id) {
            api.get(`/gestao-financeira/documento/${fatura.id}`, { responseType: 'blob' })
                .then(r => {
                    if (cancelled) return;
                    createdUrl = URL.createObjectURL(r.data);
                    setDocBlobUrl(createdUrl);
                })
                .catch(() => { if (!cancelled) setDocBlobUrl(null); });
        }
        return () => {
            cancelled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [fatura.id, fatura.caminho_foto, docRev]);

    // Auto-cálculo IVA quando muda valor total ou taxa (apenas manual, não ATCUD)
    const handleValorChange = (v) => {
        setValor(v);
        setValorSincEfsSync(false); // utilizador editou manualmente — desligar auto-sync
        if (!temAtcud && v && taxaIva != null) {
            const total = parseFloat(v) || 0;
            const taxa = parseFloat(taxaIva) || 0;
            const base = taxa > 0 ? parseFloat((total / (1 + taxa / 100)).toFixed(2)) : total;
            const iva = parseFloat((total - base).toFixed(2));
            setSubtotal(base.toFixed(2));
            setValorIva(iva.toFixed(2));
        }
    };
    const handleTaxaIvaChange = (t) => {
        setTaxaIva(parseFloat(t));
        if (valor) {
            const total = parseFloat(valor) || 0;
            const taxa = parseFloat(t) || 0;
            const base = taxa > 0 ? parseFloat((total / (1 + taxa / 100)).toFixed(2)) : total;
            const iva = parseFloat((total - base).toFixed(2));
            setSubtotal(base.toFixed(2));
            setValorIva(iva.toFixed(2));
        }
    };

    /* ---- Linhas de IVA (faturas com mais do que uma taxa) ----
       Manda a soma: a base e o IVA da fatura passam a ser o somatório das
       linhas, e o total é a soma dos dois. Ao contrário da taxa única, aqui é
       o utilizador que sabe os valores — não há conta que os deduza. */
    const recalcularPorLinhas = (linhas) => {
        const somaBase = linhas.reduce((s, l) => s + (parseFloat(l.base) || 0), 0);
        const somaIva = linhas.reduce((s, l) => s + (parseFloat(l.iva) || 0), 0);
        setSubtotal(somaBase ? somaBase.toFixed(2) : '');
        setValorIva(somaIva ? somaIva.toFixed(2) : '');
        if (somaBase || somaIva) setValor((somaBase + somaIva).toFixed(2));
        // resumo numa só taxa: a mais alta das que a fatura tem
        const taxas = linhas.map(l => parseFloat(l.taxa) || 0);
        if (taxas.length) setTaxaIva(Math.max(...taxas));
    };
    const alterarLinhaIva = (idx, campo, valorCampo) => {
        setLinhasIva(prev => {
            const novas = prev.map((l, i) => i === idx ? { ...l, [campo]: valorCampo } : l);
            // o IVA de cada linha calcula-se da base e da taxa; o utilizador
            // pode sempre corrigi-lo, para bater ao cêntimo com o papel
            if (campo === 'base' || campo === 'taxa') {
                const l = novas[idx];
                const b = parseFloat(l.base) || 0, t = parseFloat(l.taxa) || 0;
                novas[idx] = { ...l, iva: (b * t / 100).toFixed(2) };
            }
            recalcularPorLinhas(novas);
            return novas;
        });
    };
    const acrescentarLinhaIva = () => {
        setLinhasIva(prev => {
            // primeira vez: a linha existente entra com o que já está preenchido
            const novas = prev.length
                ? [...prev, { taxa: '0', base: '', iva: '' }]
                : [{ taxa: String(taxaIva), base: subtotal || '', iva: valorIva || '' },
                   { taxa: '0', base: '', iva: '' }];
            recalcularPorLinhas(novas);
            return novas;
        });
    };
    const removerLinhaIva = (idx) => {
        setLinhasIva(prev => {
            const novas = prev.filter((_, i) => i !== idx);
            // uma linha só deixa de ser "várias taxas": volta ao campo simples
            if (novas.length <= 1) {
                const l = novas[0];
                if (l) {
                    setTaxaIva(parseFloat(l.taxa) || 0);
                    setSubtotal(l.base || '');
                    setValorIva(l.iva || '');
                }
                return [];
            }
            recalcularPorLinhas(novas);
            return novas;
        });
    };

    // Fusão
    const [targetFusao, setTargetFusao] = useState(null);
    const [fundindo, setFundindo] = useState(false);
    // Zoom hover
    const [zoomPos, setZoomPos] = useState({ active: false, x: 50, y: 50 });
    // Pesquisa EF
    const [queryEF, setQueryEF] = useState('');
    const [resultadosEF, setResultadosEF] = useState([]);
    // Seleção múltipla: quando a mesma encomenda existe repetida (4 EF iguais
    // para a mesma fatura), marcam-se todas e associam-se de uma vez, em vez de
    // repetir pesquisa + associação uma a uma.
    const [efsSel, setEfsSel] = useState(() => new Set());
    const [associando, setAssociando] = useState(false);
    const efSearchRef = useRef(null);

    // NC — modo de entrada: 'original' (ref. fatura) ou 'fornecedor' (sem fatura associada)
    const [ncModo, setNcModo] = useState('original');
    // NC — pesquisa da fatura original
    const [ncOriginalQuery, setNcOriginalQuery] = useState('');
    const [ncOriginalResultados, setNcOriginalResultados] = useState([]);
    const [ncOriginalSelecionada, setNcOriginalSelecionada] = useState(null);
    // NC — pesquisa por fornecedor
    const [ncFornQuery, setNcFornQuery] = useState('');
    const [ncFornResultados, setNcFornResultados] = useState([]);

    // M:N — lista de EFs associadas (vem do backend) + UI state
    const [associatedEfs, setAssociatedEfs] = useState(Array.isArray(fatura.associated_efs) ? fatura.associated_efs : []);
    const [showAddEf, setShowAddEf] = useState(false);
    const [editingAloc, setEditingAloc] = useState({});
    // Auto-sync total com soma das EFs (desligado se utilizador editar manualmente)
    const [valorSincEfs, setValorSincEfs] = useState(!fatura.id); // inicia ligado em nova fatura
    const valorSincEfsRef = useRef(!fatura.id);
    const setValorSincEfsSync = (v) => { valorSincEfsRef.current = v; setValorSincEfs(v); };

    // Multi-EF pre-selection (apenas para nova fatura — isNew=true)
    const [preEfs, setPreEfs] = useState(Array.isArray(initialPreEfs) && initialPreEfs.length > 0 ? initialPreEfs : []); // [{id, numero, fornecedor, valor_total}]
    const [preQueryEF, setPreQueryEF] = useState('');
    const [preResultadosEF, setPreResultadosEF] = useState([]);
    const [valorManualOverride, setValorManualOverride] = useState(false); // true se o user editou o valor manualmente
    const valorManualOverrideRef = useRef(false);

    // Criar Nova EF inline
    const [showCriarEf, setShowCriarEf] = useState(false);
    const [criarEfOrc, setCriarEfOrc] = useState(() => localStorage.getItem('gf_lastOrcamentoId') || '');
    const [criarEfForn, setCriarEfForn] = useState(fatura.fornecedor || '');
    const [criarEfValor, setCriarEfValor] = useState(() => {
        const base = fatura.subtotal ? parseFloat(fatura.subtotal) : (fatura.taxa_iva > 0 ? parseFloat((parseFloat(fatura.valor_total||0) / (1 + parseFloat(fatura.taxa_iva||23)/100)).toFixed(2)) : parseFloat(fatura.valor_total||0));
        return base > 0 ? base.toFixed(2) : '';
    });
    const [criarEfDesc, setCriarEfDesc] = useState('');
    const [criandoEf, setCriandoEf] = useState(false);

    const handleCriarEf = async () => {
        if (!criarEfOrc || !criarEfForn || !criarEfValor) return;
        setCriandoEf(true);
        try {
            const ef = await api.post('/encomendas-fornecedor', {
                orcamento_id: parseInt(criarEfOrc),
                fornecedor: criarEfForn.trim(),
                valor_total: parseFloat(criarEfValor),
                descricao: criarEfDesc.trim() || null,
            });
            localStorage.setItem('gf_lastOrcamentoId', criarEfOrc);
            // Associar automaticamente à fatura — alocar o valor base (sem IVA) da EF
            await api.post(`/gestao-financeira/faturas-reais/${fatura.id}/efs`, {
                ef_id: ef.data.id,
                valor_alocado: parseFloat(criarEfValor) || 0,
            });
            await reloadAssociations();
            setShowCriarEf(false);
            setCriarEfDesc('');
            onSaved && onSaved({ keepOpen: true });
        } catch (e) {
            setErro(e.response?.data?.error || e.message);
        } finally { setCriandoEf(false); }
    };

    const valorTotalFatura = parseFloat(valor) || 0;
    // EFs são sempre sem IVA — a alocação compara com a base (subtotal)
    const valorBaseFatura = parseFloat(subtotal) || (taxaIva > 0 ? parseFloat((valorTotalFatura / (1 + taxaIva / 100)).toFixed(2)) : valorTotalFatura);
    // Usar ef_valor_total (valor real da EF, sem IVA) — valor_alocado pode estar incorreto.
    // O recurso ao valor_alocado só vale quando a EF NÃO TEM valor definido (null).
    // Antes era `parseFloat(ef_valor_total) || parseFloat(valor_alocado)`, e como em
    // JavaScript o 0 é falso, uma linha de encomenda a 0,00 € era substituída pelo valor
    // alocado — escondendo no modal a divergência que a tabela mostrava, e fazendo os
    // dois ecrãs discordarem sobre a mesma fatura.
    // A porção atribuída a ESTA fatura, e não o valor total das linhas: a mesma
    // encomenda pode ser consumida por várias faturas, e nesse caso somar o total
    // das linhas fazia cada uma parecer muito abaixo do encomendado.
    // Mesma regra da coluna €/EF da tabela, para os dois ecrãs não discordarem.
    const totalAlocado = associatedEfs.reduce(
        (s, ae) => s + porcaoEfLiquida(ae, { taxa_iva: taxaIva }), 0);
    const valorEmFalta = parseFloat((valorBaseFatura - totalAlocado).toFixed(2));

    // Factos da(s) encomenda(s), para o ecrã os poder afirmar em vez de mostrar
    // só a fatia: quanto valem, quanto já foi faturado por todas as faturas e
    // quanto resta por faturar. Uma encomenda de 271.000 € consumida às tranches
    // não pode aparecer como se valesse os 20.000 € desta fatura.
    const efValorEncomendas = associatedEfs.reduce(
        (s, ae) => s + (parseFloat(ae.ef_valor_total) || 0), 0);
    const efJaFaturado = associatedEfs.reduce(
        (s, ae) => s + (parseFloat(ae.ef_alocado_total) || 0), 0);
    const efPorFaturar = parseFloat((efValorEncomendas - efJaFaturado).toFixed(2));
    // O que a(s) encomenda(s) ainda podem cobrir desta fatura é o que já lhe está
    // alocado mais o que delas falta faturar. Acima disso, a fatura é maior do que
    // a EF. Não se bloqueia: associa-se EF a EF (decisão do Tiago, 17-09-2026).
    const excessoSobreEf = parseFloat((valorBaseFatura - totalAlocado - Math.max(efPorFaturar, 0)).toFixed(2));

    // A decomposição da fatura tem de fechar: base + IVA = total. Quando não fecha,
    // é porque o QR só trouxe a parcela de uma taxa (o leitor usa os campos I7/I8,
    // que são a taxa normal) ou porque os campos foram editados à mão. Nesse caso a
    // base não serve para comparar com a encomenda, e é preciso dizê-lo em vez de
    // apresentar um desvio calculado sobre um número errado.
    const ivaFatura = parseFloat(valorIva) || parseFloat((valorTotalFatura - valorBaseFatura).toFixed(2));
    const somaDecomposta = parseFloat((valorBaseFatura + ivaFatura).toFixed(2));
    const decomposicaoBate = Math.abs(somaDecomposta - valorTotalFatura) <= 0.02;

    const temFicheiro = !!fatura.caminho_foto;

    // Recarrega associações da fatura do backend (usado após add/remove/update)
    const reloadAssociations = async () => {
        try {
            const r = await api.get(`/gestao-financeira/faturas-reais`);
            const fresh = (r.data || []).find(x => x.id === fatura.id);
            if (fresh) {
                const efs = Array.isArray(fresh.associated_efs) ? fresh.associated_efs : [];
                setAssociatedEfs(efs);
                setEditingAloc({});
                return efs; // retorna para sincronização
            }
        } catch {}
        return null;
    };

    // Sincronizar valor total da fatura com soma das EFs (se auto-sync activo)
    const sincronizarTotalEfs = (efs) => {
        if (!valorSincEfsRef.current) return;
        if (!efs || efs.length === 0) return;
        const somaEfs = efs.reduce((s, ae) => s + (parseFloat(ae.ef_valor_total) || parseFloat(ae.valor_alocado) || 0), 0);
        const taxa = parseFloat(taxaIva) || 0;
        const total = parseFloat((somaEfs * (1 + taxa / 100)).toFixed(2));
        setValor(total.toFixed(2));
        setSubtotal(somaEfs.toFixed(2));
        setValorIva((total - somaEfs).toFixed(2));
    };

    // Associa uma ou várias EF de uma só vez. O valor alocado a cada uma segue a
    // mesma regra de sempre (o natural da EF, limitado ao que falta alocar na
    // fatura), mas com o "em falta" a ser descontado ao longo do lote — senão
    // todas as EF do lote veriam o mesmo valor inicial e alocavam a mais.
    const handleAddEfs = async (lista) => {
        if (!lista || lista.length === 0) return;
        setAssociando(true);
        setErro('');
        const falhas = [];
        let restante = valorEmFalta;
        try {
            for (const ef of lista) {
                const efNatural = parseFloat((parseFloat(ef.valor_total) || 0).toFixed(2));
                const valorSugerido = (restante > 0.01 && efNatural > restante) ? restante : efNatural;
                try {
                    await api.post(`/gestao-financeira/faturas-reais/${fatura.id}/efs`, {
                        ef_id: ef.id,
                        valor_alocado: valorSugerido,
                    });
                    restante = parseFloat((restante - valorSugerido).toFixed(2));
                } catch (e) {
                    falhas.push(`${ef.numero}: ${e.response?.data?.error || e.message}`);
                }
            }
            const efs = await reloadAssociations();
            sincronizarTotalEfs(efs);
            if (falhas.length) {
                setErro(`${falhas.length} EF não foram associadas — ${falhas.join(' | ')}`);
            } else {
                setShowAddEf(false);
                setQueryEF('');
            }
            setEfsSel(new Set());
            onSaved && onSaved({ keepOpen: true });
        } catch (e) {
            setErro(e.response?.data?.error || e.message);
        } finally { setAssociando(false); }
    };

    // Sem confirmação, pelo mesmo motivo do botão de associar: a operação é
    // reversível — voltar a associar repõe a ligação e os campos de fatura da EF —
    // e a confirmação obrigava a dois cliques por cada linha a remover.
    const handleRemoveEf = async (efId) => {
        setAssociando(true);
        setErro('');
        try {
            await api.delete(`/gestao-financeira/faturas-reais/${fatura.id}/efs/${efId}`);
            const efs = await reloadAssociations();
            sincronizarTotalEfs(efs);
            onSaved && onSaved({ keepOpen: true });
        } catch (e) {
            setErro(e.response?.data?.error || e.message);
        } finally { setAssociando(false); }
    };

    const handleUpdateAloc = async (efId) => {
        const raw = editingAloc[efId];
        if (raw == null || raw === '') return;
        const novoValor = parseFloat(raw);
        if (isNaN(novoValor) || novoValor < 0) {
            setErro('Valor alocado inválido');
            return;
        }
        const current = associatedEfs.find(a => a.ef_id === efId);
        if (current && Math.abs((parseFloat(current.valor_alocado) || 0) - novoValor) < 0.005) {
            // Sem alteração
            setEditingAloc(prev => { const n = { ...prev }; delete n[efId]; return n; });
            return;
        }
        setAssociando(true);
        setErro('');
        try {
            await api.patch(`/gestao-financeira/faturas-reais/${fatura.id}/efs/${efId}`, {
                valor_alocado: novoValor,
            });
            await reloadAssociations();
            onSaved && onSaved({ keepOpen: true });
        } catch (e) {
            setErro(e.response?.data?.error || e.message);
        } finally { setAssociando(false); }
    };

    // Abrir documento via fetch+blob — evita React Router e problemas de proxy
    const openDoc = async (e) => {
        e.stopPropagation();
        if (openingDoc) return;
        setOpeningDoc(true);
        try {
            const r = await api.get(`/gestao-financeira/documento/${fatura.id}`, { responseType: 'blob' });
            const blobUrl = URL.createObjectURL(r.data);
            const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
            if (!win) {
                // Popup bloqueado — fallback: download
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = fatura.numero_documento_completo || `fatura-${fatura.id}`;
                a.click();
            }
            setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
        } catch (e2) {
            liquidFeedback.notify('Erro ao abrir documento: ' + (e2.response?.data?.error || e2.message));
        } finally { setOpeningDoc(false); }
    };

    // Auto-match: quando NIF + valor preenchidos, procura faturas pendentes de digitalização
    const autoMatch = useMemo(() => {
        const v = parseFloat(valor);
        if ((!nif || nif.length < 5) && !v) return [];
        const TOL = 0.03;
        return (listaFaturas || []).filter(f => {
            if (f.id === fatura.id) return false;
            if (f.estado_digitalizacao === 'concluida') return false;
            const fv = parseFloat(f.valor_total || 0);
            const valorMatch = v && (
                (fv >= v * (1 - TOL) && fv <= v * (1 + TOL)) ||
                (fv >= (v / 1.23) * (1 - TOL) && fv <= (v / 1.23) * (1 + TOL))
            );
            const nifMatch = nif && (
                (f.nif_emissor || '').includes(nif.replace(/\D/g,'')) ||
                !f.nif_emissor
            );
            if (v && nif) return valorMatch && nifMatch;
            if (v) return valorMatch;
            if (nif) return nifMatch;
            return false;
        }).slice(0, 5);
    }, [nif, valor, listaFaturas, fatura.id]);

    // Faturas bulk sem data_documento: se o user preencher, guarda; senão deixa fechar na mesma.
    const requerData = fatura.auto_processada && !fatura.data_documento;

    const handleClose = () => {
        if (requerData && dataDoc) {
            // User preencheu — guardar antes de fechar
            api.patch(`/gestao-financeira/faturas-reais/${fatura.id}`, { data_documento: dataDoc })
                .then(() => onSaved())
                .catch(() => onClose());
            return;
        }
        // Sem data ou não requerida — fechar diretamente
        onClose();
    };

    // { aviso, resolver } — o resolver destranca o handleSave com a resposta.
    const [confirmarDup, setConfirmarDup] = useState(null);

    /* Pergunta ao servidor se este lançamento colide com uma fatura já existente.
       Devolve 'seguir' (não há colisão), 'confirmado' (há, e o utilizador mandou
       lançar) ou 'cancelar'. Se a verificação falhar, deixa passar: uma falha a
       verificar não pode impedir de trabalhar. */
    const decidirSeDuplicado = async () => {
        const num = (numDoc || '').trim();
        if (!num) return 'seguir';
        try {
            const r = await api.post('/gestao-financeira/verificar-duplicado', {
                nif_emissor: nif,
                fornecedor,
                numero_documento_completo: num,
                valor_total: isNC ? -Math.abs(parseFloat(valor) || 0) : Math.abs(parseFloat(valor) || 0),
                data_documento: dataDoc || null,
                tipo_documento: isNC ? 'NC' : 'FT',
                ignorar_id: fatura.id || null,
            });
            const aviso = r.data?.aviso;
            if (!aviso || aviso.nivel !== 'duplicado') return 'seguir';
            return await new Promise(resolver => setConfirmarDup({ aviso, resolver }));
        } catch {
            return 'seguir';
        }
    };

    const handleSave = async () => {
        // Fatura nova com EF escolhidas: gravar é o fecho, por isso as EF têm de a
        // cobrir. Numa fatura já existente é o servidor que o verifica (fecho=1).
        if (!fatura.id && preEfs.length > 0 && !isNC && decomposicaoBate) {
            const livre = preEfs.reduce((s, ef) => s + Math.max(0,
                (parseFloat(ef.valor_total) || 0) - (parseFloat(ef.ef_alocado_total) || 0)), 0);
            const excesso = parseFloat((valorBaseFatura - livre).toFixed(2));
            if (excesso > TOLERANCIA_EXCESSO_EF) { setErro(msgExcessoEfNova(excesso, preEfs.length)); return; }
        }
        // Perguntar ANTES de gravar seja o que for.
        const decisao = await decidirSeDuplicado();
        if (decisao === 'cancelar') return;
        setSaving(true);
        setErro('');
        let avisoDuplicado = null;   // possível duplicação detetada ao gravar
        try {
            const fd = new FormData();
            fd.append('fornecedor', fornecedor);
            fd.append('nif_emissor', nif);
            // NC: guardar valor como negativo
            const valorFinal = isNC ? -Math.abs(parseFloat(valor) || 0) : Math.abs(parseFloat(valor) || 0);
            const subtotalFinal = isNC ? -Math.abs(parseFloat(subtotal) || 0) : Math.abs(parseFloat(subtotal) || 0);
            const valorIvaFinal = isNC ? -Math.abs(parseFloat(valorIva) || 0) : Math.abs(parseFloat(valorIva) || 0);
            fd.append('valor_total', valorFinal);
            fd.append('subtotal', subtotalFinal);
            fd.append('valor_iva', valorIvaFinal);
            fd.append('taxa_iva', taxaIva);
            // Linhas de IVA: só seguem quando há mais do que uma taxa. Uma
            // string vazia limpa o campo no servidor, devolvendo a fatura ao
            // comportamento de taxa única.
            fd.append('decomposicao_iva', multiIva
                ? JSON.stringify(linhasIva.map(l => ({
                    taxa: parseFloat(l.taxa) || 0,
                    base: parseFloat(l.base) || 0,
                    iva: parseFloat(l.iva) || 0,
                  })))
                : '');
            fd.append('numero_documento_completo', numDoc);
            fd.append('motivo_sem_ef', motivoSemEf.trim());
            fd.append('data_documento', dataDoc);
            fd.append('estado_fatura', estadoFatura);
            fd.append('tipo_documento', isNC ? 'NC' : 'FT');
            if (isNC && ncOriginalSelecionada) fd.append('fatura_original_id', ncOriginalSelecionada.id);
            if (dataRegisto) fd.append('criado_em', dataRegisto);
            // Anexar todos os ficheiros — o backend funde-os num PDF único se forem vários
            // Se carregou novo documento, marcar como verificada automaticamente
            if (fotoFiles.length > 0 && estadoFatura !== 'verificada') fd.set('estado_fatura', 'verificada');
            fotoFiles.forEach(f => fd.append('foto', f));
            if (!fatura.id) {
                // Nova fatura — POST (pode devolver fatura existente com merged:true se NIF+numDoc já existe)
                fd.append('auto_processada', 'false');
                const novaFatura = await api.post('/gestao-financeira/faturas-reais', fd);
                const fatId = novaFatura.data?.id;
                const isMerged = !!novaFatura.data?.merged;
                avisoDuplicado = novaFatura.data?.aviso_duplicado || null;
                // Associar EFs pré-selecionadas (multi-EF na criação)
                if (preEfs.length > 0 && fatId) {
                    for (const ef of preEfs) {
                        try {
                            await api.post(`/gestao-financeira/faturas-reais/${fatId}/efs`, {
                                ef_id: ef.id,
                                valor_alocado: parseFloat(ef.valor_total) || 0,
                            });
                        } catch {}
                    }
                    // Se fundiu com fatura existente: recalcular total pela soma de TODAS as EFs agora associadas
                    if (isMerged) {
                        try {
                            const fRes = await api.get(`/gestao-financeira/faturas-reais`);
                            const faturaAtual = (fRes.data || []).find(x => x.id === fatId);
                            if (faturaAtual?.associated_efs?.length) {
                                const somaEfs = faturaAtual.associated_efs.reduce(
                                    (s, ae) => s + (parseFloat(ae.ef_valor_total) || parseFloat(ae.valor_alocado) || 0), 0
                                );
                                const taxa = parseFloat(taxaIva) || 0;
                                const novoTotal = parseFloat((somaEfs * (1 + taxa / 100)).toFixed(2));
                                const fd2 = new FormData();
                                fd2.append('valor_total', novoTotal);
                                fd2.append('subtotal', somaEfs.toFixed(2));
                                fd2.append('valor_iva', (novoTotal - somaEfs).toFixed(2));
                                await api.patch(`/gestao-financeira/faturas-reais/${fatId}`, fd2);
                            }
                        } catch {}
                    }
                }
            } else {
                // Gravar a ficha é o fecho: o servidor recusa se a fatura for superior
                // às EF associadas. Sem base fiável (decomposição que não fecha) não se pede.
                if (decomposicaoBate) fd.append('fecho', '1');
                const r = await api.patch(`/gestao-financeira/faturas-reais/${fatura.id}`, fd);
                avisoDuplicado = r.data?.aviso_duplicado || null;
            }
            // O aviso sobe com o onSaved: o modal fecha-se a seguir, e um aviso
            // que desaparece com ele não seria lido.
            // Se ele já disse "lançar mesmo assim", repetir o aviso agora era ruído.
            onSaved({ aviso: decisao === 'confirmado' ? null : avisoDuplicado });
        } catch (e) {
            setErro(e.response?.data?.error || e.message);
        } finally { setSaving(false); }
    };

    // Zoom hover handlers
    const handleImgMouseMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setZoomPos({ active: true, x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
    };
    const handleImgMouseLeave = () => setZoomPos({ active: false, x: 50, y: 50 });

    // Pesquisa EF
    useEffect(() => {
        if (!queryEF || queryEF.trim().length < 2) { setResultadosEF([]); return; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get(`/gestao-financeira/search-ef?q=${encodeURIComponent(queryEF.trim())}`);
                setResultadosEF(r.data);
            } catch { setResultadosEF([]); }
        }, 300);
        return () => clearTimeout(t);
    }, [queryEF]);

    // Pesquisa de EFs para pre-seleção (nova fatura)
    useEffect(() => {
        if (!isNew) return;
        if (!preQueryEF || preQueryEF.trim().length < 2) { setPreResultadosEF([]); return; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get(`/gestao-financeira/search-ef?q=${encodeURIComponent(preQueryEF.trim())}`);
                setPreResultadosEF(r.data || []);
            } catch { setPreResultadosEF([]); }
        }, 250);
        return () => clearTimeout(t);
    }, [preQueryEF, isNew]);

    // Pesquisa da fatura original para NCs
    useEffect(() => {
        if (!isNC || ncModo !== 'original' || !ncOriginalQuery || ncOriginalQuery.trim().length < 2) { setNcOriginalResultados([]); return; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get(`/gestao-financeira/buscar-fatura?q=${encodeURIComponent(ncOriginalQuery.trim())}`);
                setNcOriginalResultados(r.data || []);
            } catch { setNcOriginalResultados([]); }
        }, 250);
        return () => clearTimeout(t);
    }, [ncOriginalQuery, isNC, ncModo]);

    // Pesquisa de fornecedor para NCs no modo "por fornecedor"
    useEffect(() => {
        if (!isNC || ncModo !== 'fornecedor' || !ncFornQuery || ncFornQuery.trim().length < 2) { setNcFornResultados([]); return; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get(`/gestao-financeira/buscar-fornecedor?q=${encodeURIComponent(ncFornQuery.trim())}`);
                setNcFornResultados(r.data || []);
            } catch { setNcFornResultados([]); }
        }, 250);
        return () => clearTimeout(t);
    }, [ncFornQuery, isNC, ncModo]);

    // Auto-sum: quando mudam EFs pré-selecionadas e o valor não foi editado manualmente, actualizar total
    useEffect(() => {
        if (!isNew || !valorSincEfsRef.current) return;
        if (preEfs.length === 0) return;
        const soma = preEfs.reduce((s, ef) => s + (parseFloat(ef.valor_total) || 0), 0);
        // As EFs têm valor sem IVA — calcular total com IVA usando taxa actual
        const taxa = parseFloat(taxaIva) || 0;
        const total = parseFloat((soma * (1 + taxa / 100)).toFixed(2));
        setValor(total.toFixed(2));
        setSubtotal(soma.toFixed(2));
        setValorIva((total - soma).toFixed(2));
    }, [preEfs, isNew, valorSincEfs, taxaIva]);

    const handleAssociarEF = async (ef, forceFundir = false) => {
        if (!forceFundir) {
            if (!await liquidFeedback.confirm(`Associar esta fatura à encomenda ${ef.numero} (${ef.fornecedor})?`)) return;
        }
        setAssociando(true);
        setErro('');
        try {
            const url = `/gestao-financeira/faturas-reais/${fatura.id}/associar-ef/${ef.id}${forceFundir ? '?fundir=true' : ''}`;
            await api.post(url, { numero_fatura: numDoc || null, data_fatura: dataDoc || null });
            onSaved();
        } catch (e) {
            if (e.response?.status === 409 && e.response.data?.conflict) {
                // NC nunca devia chegar aqui (o backend deixa passar), mas por segurança:
                if (isNC) {
                    setErro('Não foi possível associar a nota de crédito. Tente novamente.');
                    setAssociando(false);
                    return;
                }
                // EF já tem outro registo — perguntar se quer fundir
                const ex = e.response.data.existente;
                const msg = `A EF ${ef.numero} já tem um registo associado:\n`
                    + `  Fornecedor: ${ex.fornecedor || 'Sem nome'}\n`
                    + `  Valor: ${parseFloat(ex.valor_total || 0).toFixed(2)} €\n`
                    + `  Digitalização: ${ex.estado_digitalizacao === 'concluida' ? 'Concluída' : 'Pendente'}\n\n`
                    + (temFicheiro
                        ? `Este registo tem o documento digitalizado.\nFundir: mover o documento para o registo existente e apagar este duplicado?`
                        : `Associar mesmo assim (ficam dois registos para a mesma EF)?`);
                if (await liquidFeedback.confirm(msg)) {
                    setAssociando(false);
                    await handleAssociarEF(ef, true);
                } else {
                    setAssociando(false);
                }
            } else {
                setErro(e.response?.data?.error || e.message);
                setAssociando(false);
            }
        }
    };

    const handleApagarDigitalizacao = async () => {
        if (!await liquidFeedback.confirm('Apagar a digitalização (ficheiro) desta fatura?\nO registo mantém-se, apenas o documento é removido.')) return;
        try {
            await api.delete(`/gestao-financeira/faturas-reais/${fatura.id}/documento`);
            onSaved();
        } catch (e) {
            setErro(e.response?.data?.error || e.message);
        }
    };

    const handleFundir = async (target) => {
        const t = target || targetFusao;
        if (!t) return;
        if (!await liquidFeedback.confirm(
            `Transferir o documento digitalizado para "${t.fornecedor || 'registo #' + t.id}" e apagar este duplicado?\n\nEsta ação não pode ser revertida.`
        )) return;
        setFundindo(true);
        setErro('');
        try {
            await api.post(`/gestao-financeira/faturas-reais/${fatura.id}/fundir/${t.id}`);
            onSaved();
        } catch (e) {
            setErro(e.response?.data?.error || e.message);
            setFundindo(false);
        }
    };

    return (
        <LiquidDialog onClose={handleClose} className="gf-modal-overlay" onClick={handleClose}>
            {confirmarDup && (
                <ConfirmarDuplicacao
                    aviso={confirmarDup.aviso}
                    onLancar={() => { confirmarDup.resolver('confirmado'); setConfirmarDup(null); }}
                    onApagar={async () => {
                        confirmarDup.resolver('cancelar');
                        setConfirmarDup(null);
                        // Apagar age sobre a fatura que está aberta — a repetida.
                        // Se ainda não existe registo, basta descartá-la; se já
                        // existe, tem mesmo de sair da base de dados.
                        if (!fatura.id) { onClose(); return; }
                        try {
                            await api.delete(`/gestao-financeira/faturas-reais/${fatura.id}`);
                            onSaved({});
                        } catch (e) {
                            setErro('Não foi possível apagar: ' + (e.response?.data?.error || e.message));
                        }
                    }}
                />
            )}
            <div className="gef-modal-wide gef-modal-associacao" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="gf-modal-header">
                    <h3>{isNC ? (isNew ? 'Nova Nota de Crédito' : 'Editar Nota de Crédito') : (isNew ? 'Nova Fatura Fornecedor' : 'Editar Fatura')}</h3>
                    <button className="gf-modal-close" onClick={handleClose}><OrcamentoIcon name="close" fallback="×" /></button>
                </div>

                {/* Documento, dados da fatura e associação de encomendas */}
                <div className="gef-modal-body-split">
                    {/* ─── Coluna esquerda: preview do documento ─── */}
                    <div className="gef-col-preview">
                        {temFicheiro || (fotoFile && fotoFile.type?.startsWith('image/')) || (fotoFile && fotoFile.type === 'application/pdf') ? (
                            <>
                                {(() => {
                                    // Determinar se é PDF
                                    const isFotoPdf = fotoFile && fotoFile.type === 'application/pdf';
                                    const isExistingPdf = temFicheiro && /\.pdf$/i.test(fatura.caminho_foto || '');
                                    const isPdf = isFotoPdf || isExistingPdf;
                                    const src = fotoFile
                                        ? URL.createObjectURL(fotoFile)
                                        : docBlobUrl;
                                    if (isPdf) {
                                        if (!src) {
                                            return <div className="gef-no-doc"><span><OrcamentoIcon name="document" fallback="📄" /></span><p>A carregar PDF...</p></div>;
                                        }
                                        return (
                                            <div className="gef-pdf-wrap">
                                                <iframe src={src} title="Fatura PDF" className="gef-pdf-iframe" />
                                            </div>
                                        );
                                    }
                                    return (
                                        <div className="gef-img-zoom-wrap"
                                            onMouseMove={handleImgMouseMove}
                                            onMouseLeave={handleImgMouseLeave}>
                                            <img
                                                src={src}
                                                alt="Fatura"
                                                className="gef-img-large"
                                                style={{
                                                    transform: zoomPos.active ? 'scale(2.6)' : 'scale(1)',
                                                    transformOrigin: `${zoomPos.x}% ${zoomPos.y}%`,
                                                    transition: zoomPos.active ? 'transform 0.1s ease' : 'transform 0.25s ease',
                                                    cursor: zoomPos.active ? 'zoom-in' : 'default',
                                                }}
                                                onError={e => { e.currentTarget.parentElement.innerHTML = '<div class="gef-pdf-placeholder">📄<br/>PDF</div>'; }}
                                            />
                                        </div>
                                    );
                                })()}
                                {temFicheiro && (
                                    <button className="gef-open-doc-btn" onClick={openDoc} disabled={openingDoc}>
                                        <OrcamentoIcon name="eye" fallback="🔍" /> {openingDoc ? 'A abrir...' : 'Abrir em tamanho completo'}
                                    </button>
                                )}
                                {!temFicheiro && fotoFile && (
                                    <p className="gef-preview-hint">Pré-visualização — clique Guardar para gravar</p>
                                )}
                            </>
                        ) : fotoFile ? (
                            <div className="gef-no-doc">
                                <span><OrcamentoIcon name="document" fallback="📄" /></span>
                                <p>{fotoFile.name}</p>
                                <p className="gef-preview-hint">Ficheiro PDF — clique Guardar para gravar</p>
                            </div>
                        ) : (
                            <div className="gef-no-doc">
                                <span><OrcamentoIcon name="document" fallback="📂" /></span>
                                <p>Sem documento</p>
                            </div>
                        )}
                        {/* Lista de ficheiros pendentes de upload (multi-página) */}
                        {fotoFiles.length > 0 && (
                            <div className="gef-pending-files">
                                <div className="gef-pending-header">
                                    <span>{fotoFiles.length} ficheiro{fotoFiles.length > 1 ? 's' : ''} a carregar{fotoFiles.length > 1 ? ' (serão fundidos num PDF único)' : ''}</span>
                                </div>
                                <div className="gef-pending-list">
                                    {fotoFiles.map((f, idx) => (
                                        <div
                                            key={idx}
                                            className={`gef-pending-item${idx === previewIdx ? ' selected' : ''}`}
                                            onClick={() => setPreviewIdx(idx)}
                                            title={f.name}>
                                            <span className="gef-pending-num">{idx + 1}</span>
                                            <span className="gef-pending-name">{f.name}</span>
                                            <button
                                                className="gef-pending-remove"
                                                title="Remover este ficheiro"
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    setFotoFiles(prev => prev.filter((_, i) => i !== idx));
                                                    setPreviewIdx(i => Math.max(0, i >= idx ? i - 1 : i));
                                                }}><DeleteIcon /></button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="gef-replace-file">
                            <label className="gef-replace-label">
                                <input type="file" accept="image/*,application/pdf" hidden multiple
                                    onChange={e => {
                                        const newFiles = Array.from(e.target.files || []);
                                        if (newFiles.length === 0) return;
                                        setFotoFiles(prev => {
                                            const combined = [...prev, ...newFiles];
                                            setPreviewIdx(prev.length); // saltar para a primeira nova
                                            return combined;
                                        });
                                        e.target.value = ''; // permite re-selecionar o mesmo ficheiro
                                    }} />
                                <OrcamentoIcon name={fotoFiles.length > 0 ? "plus" : temFicheiro ? "refresh" : "upload"} fallback={fotoFiles.length > 0 ? "+" : temFicheiro ? "↩" : "📎"} /> {fotoFiles.length > 0 ? "Adicionar mais páginas" : temFicheiro ? "Substituir documento" : "Carregar documento"}
                            </label>
                            {temFicheiro && fotoFiles.length === 0 && (
                                <label className="gef-replace-label gef-append-label" title="Acrescentar páginas ao PDF existente">
                                    <input type="file" accept="image/*,application/pdf" hidden multiple
                                        onChange={async e => {
                                            const newFiles = Array.from(e.target.files || []);
                                            e.target.value = '';
                                            if (newFiles.length === 0) return;
                                            setSaving(true);
                                            setErro('');
                                            try {
                                                const fd = new FormData();
                                                newFiles.forEach(f => fd.append('foto', f));
                                                await api.post(`/gestao-financeira/faturas-reais/${fatura.id}/append-pages`, fd);
                                                setDocRev(r => r + 1);
                                                onSaved && onSaved({ keepOpen: true });
                                            } catch (err) {
                                                setErro(err.response?.data?.error || err.message);
                                            } finally { setSaving(false); }
                                        }} />
                                    <OrcamentoIcon name="plus" fallback="➕" /> Adicionar páginas
                                </label>
                            )}
                            {temFicheiro && (
                                <button className="gef-btn-apagar-doc" onClick={handleApagarDigitalizacao}
                                    title="Apagar digitalização"><OrcamentoIcon name="trash" fallback="🗑️" /> Apagar</button>
                            )}
                        </div>
                    </div>

                    {/* ─── Coluna direita: formulário ─── */}
                    <div className="gef-col-form">
                        {/* ── NC picker: toggle Fatura Original / Por Fornecedor ── */}
                        {isNew && isNC && (
                            <div className="gef-pre-ef-box gf-credit-picker" style={{borderColor:'#fca5a5', background:'#fff5f5'}}>
                                {/* Toggle */}
                                <div style={{display:'flex', gap:6, marginBottom:8}}>
                                    <button type="button"
                                        aria-pressed={ncModo === 'original'} className="gf-credit-mode"
                                        onClick={() => { setNcModo('original'); setNcFornQuery(''); setNcFornResultados([]); }}
                                        style={{flex:1, padding:'4px 0', fontSize:11, fontWeight:700, borderRadius:4, cursor:'pointer', border:'1px solid',
                                            background: ncModo==='original' ? '#fee2e2' : '#f9fafb',
                                            color:      ncModo==='original' ? '#dc2626' : '#6b7280',
                                            borderColor:ncModo==='original' ? '#fca5a5' : '#e5e7eb'}}>
                                        Fatura Original
                                    </button>
                                    <button type="button"
                                        aria-pressed={ncModo === 'fornecedor'} className="gf-credit-mode"
                                        onClick={() => { setNcModo('fornecedor'); setNcOriginalSelecionada(null); setNcOriginalQuery(''); setNcOriginalResultados([]); }}
                                        style={{flex:1, padding:'4px 0', fontSize:11, fontWeight:700, borderRadius:4, cursor:'pointer', border:'1px solid',
                                            background: ncModo==='fornecedor' ? '#fee2e2' : '#f9fafb',
                                            color:      ncModo==='fornecedor' ? '#dc2626' : '#6b7280',
                                            borderColor:ncModo==='fornecedor' ? '#fca5a5' : '#e5e7eb'}}>
                                        Por Fornecedor
                                    </button>
                                </div>

                                {/* Modo: Fatura Original */}
                                {ncModo === 'original' && (
                                    <>
                                        <label className="gef-ef-search-label" style={{color:'#dc2626'}}>
                                            Fatura Original
                                            <span style={{fontWeight:400, color:'#6b7280', marginLeft:6, fontSize:12}}>(rectifica esta fatura — copia fornecedor e EF associada)</span>
                                        </label>
                                        {ncOriginalSelecionada ? (
                                            <div className="gef-pre-ef-item" style={{background:'#fee2e2', borderColor:'#fca5a5'}}>
                                                <span className="gef-ef-num" style={{color:'#dc2626'}}>{ncOriginalSelecionada.numero_documento_completo}</span>
                                                <span className="gef-ef-info" style={{flex:1}}>
                                                    {ncOriginalSelecionada.fornecedor}
                                                    {ncOriginalSelecionada.valor_total && <span className="gef-ef-val"> · {fmtMoney(ncOriginalSelecionada.valor_total)}</span>}
                                                    {ncOriginalSelecionada.data_documento && <span style={{color:'#9ca3af', fontSize:11}}> · {fmtDate(ncOriginalSelecionada.data_documento)}</span>}
                                                </span>
                                                <button type="button" className="gef-ef-assoc-remove"
                                                    onClick={() => { setNcOriginalSelecionada(null); setNcOriginalQuery(''); }} aria-label="Apagar" title="Apagar"><DeleteIcon /></button>
                                            </div>
                                        ) : (
                                            <>
                                                <input type="text" className="gef-ef-input"
                                                    placeholder="N.º da fatura original (ex: FT 2025/123)..."
                                                    value={ncOriginalQuery}
                                                    onChange={e => setNcOriginalQuery(e.target.value)}
                                                    autoFocus />
                                                {ncOriginalResultados.length > 0 && (
                                                    <div className="gef-ef-results" style={{maxHeight:180}}>
                                                        {ncOriginalResultados.map(f => (
                                                            <div key={f.id} className="gef-ef-item" style={{cursor:'pointer'}}
                                                                onClick={() => {
                                                                    setNcOriginalSelecionada(f);
                                                                    if (!fornecedor) setFornecedor(f.fornecedor || '');
                                                                    if (!nif && f.nif_emissor) setNif(f.nif_emissor);
                                                                    setNcOriginalQuery(''); setNcOriginalResultados([]);
                                                                }}>
                                                                <span className="gef-ef-num">{f.numero_documento_completo}</span>
                                                                <span className="gef-ef-info">
                                                                    {f.fornecedor}
                                                                    <span className="gef-ef-val"> · {fmtMoney(f.valor_total)}</span>
                                                                    {f.data_documento && <span style={{color:'#9ca3af', fontSize:11}}> · {fmtDate(f.data_documento)}</span>}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}

                                {/* Modo: Por Fornecedor */}
                                {ncModo === 'fornecedor' && (
                                    <>
                                        <label className="gef-ef-search-label" style={{color:'#dc2626'}}>
                                            Fornecedor
                                            <span style={{fontWeight:400, color:'#6b7280', marginLeft:6, fontSize:12}}>(NC sem fatura associada — fica registada para este fornecedor)</span>
                                        </label>
                                        {fornecedor && nif ? (
                                            <div className="gef-pre-ef-item" style={{background:'#fee2e2', borderColor:'#fca5a5'}}>
                                                <span className="gef-ef-num" style={{color:'#dc2626'}}>{fornecedor}</span>
                                                <span className="gef-ef-info" style={{flex:1, color:'#6b7280'}}>NIF {nif}</span>
                                                <button type="button" className="gef-ef-assoc-remove"
                                                    onClick={() => { setFornecedor(''); setNif(''); setNcFornQuery(''); }} aria-label="Apagar" title="Apagar"><DeleteIcon /></button>
                                            </div>
                                        ) : (
                                            <>
                                                <input type="text" className="gef-ef-input"
                                                    placeholder="Nome ou NIF do fornecedor..."
                                                    value={ncFornQuery}
                                                    onChange={e => setNcFornQuery(e.target.value)}
                                                    autoFocus />
                                                {ncFornResultados.length > 0 && (
                                                    <div className="gef-ef-results" style={{maxHeight:180}}>
                                                        {ncFornResultados.map(f => (
                                                            <div key={f.id} className="gef-ef-item" style={{cursor:'pointer'}}
                                                                onClick={() => {
                                                                    setFornecedor(f.nome);
                                                                    setNif(f.nif || '');
                                                                    setNcFornQuery(''); setNcFornResultados([]);
                                                                }}>
                                                                <span className="gef-ef-num">{f.nome}</span>
                                                                <span className="gef-ef-info" style={{color:'#6b7280'}}>NIF {f.nif}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* ── Multi-EF picker (apenas nova fatura não-NC) ── */}
                        {isNew && !isNC && (
                            <div className="gef-pre-ef-box">
                                <label className="gef-ef-search-label">
                                    Encomendas Fornecedor a associar
                                    <span style={{fontWeight:400, color:'#6b7280', marginLeft:6, fontSize:12}}>(opcional — valor soma automaticamente)</span>
                                </label>
                                {preEfs.length > 0 && (
                                    <div className="gef-pre-ef-list">
                                        {preEfs.map(ef => (
                                            <div key={ef.id} className="gef-pre-ef-item">
                                                <span className="gef-ef-num">{ef.numero}</span>
                                                <span className="gef-ef-info" style={{flex:1}}>
                                                    {ef.fornecedor}
                                                    {ef.projeto && <span className="gef-ef-proj"> · {ef.projeto}</span>}
                                                    <span className="gef-ef-val"> · {fmtMoney(ef.valor_total)}</span>
                                                </span>
                                                <button type="button" className="gef-ef-assoc-remove"
                                                    onClick={() => { setPreEfs(prev => prev.filter(x => x.id !== ef.id)); valorManualOverrideRef.current = false; setValorManualOverride(false); setValorSincEfsSync(true); }}
                                                    title="Remover"><DeleteIcon /></button>
                                            </div>
                                        ))}
                                        {preEfs.length > 1 && (
                                            <div style={{textAlign:'right', fontSize:12, color:'#1e40af', fontWeight:700, padding:'2px 6px'}}>
                                                Total EFs (s/ IVA): {fmtMoney(preEfs.reduce((s,e) => s + (parseFloat(e.valor_total)||0), 0))}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <input
                                    type="text"
                                    className="gef-ef-input"
                                    placeholder="N.º EF, fornecedor ou projeto..."
                                    value={preQueryEF}
                                    onChange={e => setPreQueryEF(e.target.value)}
                                    style={{marginTop: preEfs.length > 0 ? 6 : 0}}
                                />
                                {preResultadosEF.length > 0 && (
                                    <div className="gef-ef-results" style={{maxHeight:180}}>
                                        {preResultadosEF.map(ef => {
                                            const jaSel = preEfs.some(x => x.id === ef.id);
                                            return (
                                                <div key={ef.id}
                                                    className={`gef-ef-item${jaSel ? ' gef-ef-item-associada' : ''}`}
                                                    style={{cursor: jaSel ? 'default' : 'pointer'}}
                                                    onClick={() => {
                                                        if (jaSel) return;
                                                        setPreEfs(prev => [...prev, ef]);
                                                        valorManualOverrideRef.current = false; setValorManualOverride(false); setValorSincEfsSync(true); // recalcular soma
                                                        // se fornecedor/nif ainda vazio → auto-preencher
                                                        if (!fornecedor) setFornecedor(ef.fornecedor || '');
                                                        if (!nif && ef.nif_fornecedor) setNif(ef.nif_fornecedor);
                                                        setPreQueryEF('');
                                                        setPreResultadosEF([]);
                                                    }}>
                                                    <span className="gef-ef-num">{ef.numero}</span>
                                                    <span className="gef-ef-info">
                                                        {ef.fornecedor}
                                                        {ef.projeto && <span className="gef-ef-proj"> · {ef.projeto}</span>}
                                                        <span className="gef-ef-val"> · {fmtMoney(ef.valor_total)}</span>
                                                    </span>
                                                    {jaSel && <span className="gf-badge badge-yellow">SELECIONADA</span>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="gf-field gf-forn-campo" ref={refForn}>
                            <label>Fornecedor</label>
                            <input type="text" value={fornecedor} placeholder="Nome do fornecedor"
                                autoComplete="off"
                                onChange={e => { setFornecedor(e.target.value); if (!temAtcud) setSugestoesForn(true); }}
                                onFocus={() => { if (!temAtcud) setSugestoesForn(true); }} />
                            {/* Só sem QR: com QR o nome e o NIF vêm do documento. */}
                            {!temAtcud && sugestoesForn && fornecedoresSugeridos.length > 0 && (
                                <div className="gf-forn-lista">
                                    <div className="gf-forn-cab">
                                        {(fornecedor || '').trim()
                                            ? `${fornecedoresSugeridos.length} fornecedor(es) encontrado(s)`
                                            : `Estrangeiros registados (${fornecedoresSugeridos.length}) — escreva para procurar em todos`}
                                    </div>
                                    {fornecedoresSugeridos.map(f => (
                                        <button type="button" key={f.id} className="gf-forn-item"
                                            onClick={() => escolherFornecedor(f)}>
                                            <span className="gf-forn-nome">{f.nome}</span>
                                            <span className="gf-forn-nif">{f.nif || 'sem NIF'}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="gef-row-2">
                            <div className="gf-field">
                                <label>NIF</label>
                                <input type="text" value={nif} onChange={e => setNif(e.target.value)}
                                    placeholder="NIF emissor" />
                            </div>
                            <div className="gf-field">
                                <div className="gf-value-label" style={{display:'flex', alignItems:'center', gap:8}}>
                                    <label htmlFor={valorInputId}>Valor Total (€)</label>
                                    <label className="gf-nc-toggle" style={{display:'flex', alignItems:'center', gap:4, fontWeight:600, fontSize:11,
                                        color: isNC ? '#dc2626' : '#6b7280', cursor:'pointer', userSelect:'none',
                                        background: isNC ? '#fee2e2' : '#f1f5f9', border: `1px solid ${isNC ? '#fca5a5' : '#cbd5e1'}`,
                                        borderRadius:4, padding:'1px 7px', marginLeft:4}}>
                                        <input aria-label="Nota de Crédito" type="checkbox" checked={isNC} onChange={e => setIsNC(e.target.checked)}
                                            style={{cursor:'pointer', accentColor:'#dc2626'}} />
                                        NC
                                    </label>
                                    {valorSincEfs && (associatedEfs.length > 0 || preEfs.length > 0) && (
                                        <span style={{fontSize:10, color:'#2563eb', fontWeight:600, marginLeft:'auto', cursor:'default'}} title="Valor calculado automaticamente pela soma das EFs">∑ auto</span>
                                    )}
                                    {!valorSincEfs && (associatedEfs.length > 0 || preEfs.length > 0) && (
                                        <button type="button" onClick={() => {
                                            setValorSincEfsSync(true); valorManualOverrideRef.current = false; setValorManualOverride(false);
                                            // Forçar recálculo imediato
                                            const efsToSync = associatedEfs.length > 0 ? associatedEfs : null;
                                            const preToSync = preEfs.length > 0 ? preEfs : null;
                                            if (efsToSync) { setTimeout(() => sincronizarTotalEfs(efsToSync), 0); }
                                            else if (preToSync) { /* preEfs effect runs on next render */ }
                                        }}
                                            style={{fontSize:10, color:'#6b7280', fontWeight:600, marginLeft:'auto', background:'none', border:'1px solid #cbd5e1', borderRadius:3, padding:'1px 5px', cursor:'pointer'}}
                                            title="Repor cálculo automático pela soma das EFs">↺ auto</button>
                                    )}
                                </div>
                                <input id={valorInputId} type="number" value={valor}
                                    onChange={e => { valorManualOverrideRef.current = true; setValorManualOverride(true); setValorSincEfsSync(false); handleValorChange(e.target.value); }}
                                    step="0.01" min="0" placeholder="0.00" readOnly={temAtcud}
                                    style={valorSincEfs && (associatedEfs.length > 0 || preEfs.length > 0) ? {background:'#eff6ff', borderColor:'#93c5fd'} : {}} />
                            </div>
                        </div>
                        {!multiIva ? (
                            <div className="gef-row-3">
                                <div className="gf-field">
                                    <label>Taxa IVA</label>
                                    <select value={taxaIva} onChange={e => handleTaxaIvaChange(e.target.value)}
                                        disabled={temAtcud}>
                                        <option value={23}>23%</option>
                                        <option value={16}>16%</option>
                                        <option value={13}>13%</option>
                                        <option value={6}>6%</option>
                                        <option value={4}>4%</option>
                                        <option value={0}>0% (Isento)</option>
                                    </select>
                                </div>
                                <div className="gf-field">
                                    <label>Base (s/ IVA)</label>
                                    {/* readOnly: formata-se só o que é mostrado. O estado (subtotal,
                                        valorIva) fica intacto — é ele que segue para o backend.
                                        Vazio continua vazio, para não passar a mostrar "0,00 €". */}
                                    <input type="text" readOnly className="gef-field-readonly"
                                        value={subtotal === '' || subtotal == null ? '' : fmtMoney(subtotal)}
                                        placeholder="0,00 €" />
                                </div>
                                <div className="gf-field">
                                    <label>IVA (€)</label>
                                    <input type="text" readOnly className="gef-field-readonly"
                                        value={valorIva === '' || valorIva == null ? '' : fmtMoney(valorIva)}
                                        placeholder="0,00 €" />
                                </div>
                            </div>
                        ) : (
                            /* Uma linha por taxa. A base é escrita à mão, o IVA é calculado
                               mas continua editável — as faturas arredondam por linha e nem
                               sempre a conta bate ao cêntimo. */
                            <div className="gf-iva-linhas">
                                <div className="gf-iva-cab"><span>Taxa</span><span>Base (s/ IVA)</span><span>IVA (€)</span><span /></div>
                                {linhasIva.map((l, idx) => (
                                    <div className="gf-iva-linha" key={idx}>
                                        <select value={l.taxa} onChange={e => alterarLinhaIva(idx, 'taxa', e.target.value)}>
                                            <option value="23">23%</option>
                                            <option value="16">16%</option>
                                            <option value="13">13%</option>
                                            <option value="6">6%</option>
                                            <option value="4">4%</option>
                                            <option value="0">0% (Isento)</option>
                                        </select>
                                        <input type="number" step="0.01" min="0" placeholder="0.00"
                                            value={l.base} onChange={e => alterarLinhaIva(idx, 'base', e.target.value)} />
                                        <input type="number" step="0.01" min="0" placeholder="0.00"
                                            value={l.iva} onChange={e => alterarLinhaIva(idx, 'iva', e.target.value)} />
                                        <button type="button" className="gf-iva-x" title="Remover esta linha"
                                            onClick={() => removerLinhaIva(idx)}><DeleteIcon /></button>
                                    </div>
                                ))}
                                <div className="gf-iva-total">
                                    <span>Total</span>
                                    <span>{fmtMoney(subtotal || 0)}</span>
                                    <span>{fmtMoney(valorIva || 0)}</span>
                                    <span />
                                </div>
                                <button type="button" className="gf-iva-mais" onClick={acrescentarLinhaIva}>
                                    + acrescentar taxa
                                </button>
                            </div>
                        )}
                        {!multiIva && (
                            <button type="button" className="gf-iva-mais gf-iva-mais-solto" onClick={acrescentarLinhaIva}>
                                + acrescentar taxa de IVA
                            </button>
                        )}
                        <div style={{display:'flex', gap:10, alignItems:'flex-start'}}>
                            <div className="gf-field" style={{flex:2}}>
                                <label>N.º Documento</label>
                                <input type="text" value={numDoc} onChange={e => setNumDoc(e.target.value)}
                                    placeholder="ex: FT 2026/123" />
                            </div>
                            <div className="gf-field gf-editor-estado" style={{flex:'none'}}>
                                <label>Verificação</label>
                                {estadoFatura === 'verificada' ? (
                                    <span className="gf-badge badge-green" style={{display:'inline-block',lineHeight:'32px',padding:'0 10px'}}>Verificada</span>
                                ) : (
                                    <button
                                        title="Marcar manualmente como verificada"
                                        onClick={() => setEstadoFatura('verificada')}
                                        className="gf-badge badge-yellow"
                                        style={{display:'inline-block',lineHeight:'32px',padding:'0 10px',cursor:'pointer',border:'none'}}>
                                        Pendente <OrcamentoIcon name="check" fallback="✓" />
                                    </button>
                                )}
                            </div>
                            <div className="gf-field gf-editor-estado" style={{flex:'none'}}>
                                <label>Estado</label>
                                {(() => {
                                    const st = !fatura.pago ? 'nao-pago'
                                        : (fatura.valor_pago != null && parseFloat(fatura.valor_pago) < parseFloat(fatura.valor_total || 0) - 0.005 ? 'parc-pago' : 'pago');
                                    const label = st === 'pago' ? 'Pago' : st === 'parc-pago' ? 'Parc. Pago' : 'Não pago';
                                    return onPagamento && fatura.id ? <button type="button" className={`btn-pago btn-pago-fixed ${st}`} style={{minHeight:32}} title="Registar ou gerir pagamentos" onClick={() => { if (Math.abs((parseFloat(valor) || 0) - (parseFloat(fatura.valor_total) || 0)) > 0.005) { liquidFeedback.notify('Guarde primeiro a alteração do valor da fatura antes de registar o pagamento.'); return; } onPagamento(fatura); }}><LiquidText>{label}</LiquidText></button> : <span className={`btn-pago btn-pago-fixed ${st}`}><LiquidText>{label}</LiquidText></span>;
                                })()}
                            </div>
                            {fatura.pago && fatura.data_pagamento && (
                                <div className="gf-field gf-editor-estado" style={{flex:'none'}}>
                                    <label>Pago Em</label>
                                    <span className="gf-pago-date" style={{display:'inline-block', lineHeight:'32px', padding:'0 8px'}}>
                                        {new Date(fatura.data_pagamento).toLocaleDateString('pt-PT')}
                                    </span>
                                </div>
                            )}
                            {fatura.pago && fatura.valor_pago != null && parseFloat(fatura.valor_pago) < parseFloat(fatura.valor_total || 0) - 0.005 && (
                                <div className="gf-field gf-editor-estado" style={{flex:'none'}}>
                                    <label>Em Falta</label>
                                    <span className="gf-em-falta parc" style={{display:'inline-block', lineHeight:'32px', padding:'0 8px'}}>
                                        {fmtMoney(parseFloat(fatura.valor_total || 0) - parseFloat(fatura.valor_pago || 0))}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="gef-row-2">
                            <div className="gf-field">
                                <label>Data Fatura {requerData && !dataDoc && <span style={{color:'#d97706', fontWeight:700}} title="Em falta — recomendado preencher"><LiquidText>⚠</LiquidText></span>}</label>
                                <input type="date" value={dataDoc} onChange={e => { setDataDoc(e.target.value); setErro(''); }}
                                    style={requerData && !dataDoc ? {borderColor:'#f59e0b', background:'#fffbeb'} : {}} />
                            </div>
                            <div className="gf-field">
                                <label>Data Registo</label>
                                <input type="date" value={dataRegisto} onChange={e => setDataRegisto(e.target.value)} />
                            </div>
                        </div>

                        {/* Auto-match: faturas pendentes correspondentes */}
                        {autoMatch.length > 0 && (
                            <div className="gef-automatch-box">
                                <p className="gef-automatch-title">
                                    <OrcamentoIcon name="warning" fallback="⚠️" /> Possível duplicado — fatura{autoMatch.length > 1 ? 's' : ''} pendente{autoMatch.length > 1 ? 's' : ''} de digitalização com valores similares:
                                </p>
                                {autoMatch.map(m => (
                                    <div key={m.id} className={`gef-fusao-item${targetFusao?.id === m.id ? ' selected' : ''}`}
                                        onClick={() => setTargetFusao(prev => prev?.id === m.id ? null : m)}>
                                        <span className="gef-fusao-nome">{m.fornecedor || 'Nome Pendente'}</span>
                                        <span className="gef-fusao-meta">
                                            {m.numero_documento_completo && <span>{m.numero_documento_completo} · </span>}
                                            {fmtMoney(m.valor_total)}
                                            {m.ef_numero && <span className="gef-fusao-ef"> · {m.ef_numero}</span>}
                                            {m.nif_emissor && <span> · NIF {m.nif_emissor}</span>}
                                        </span>
                                        {targetFusao?.id === m.id && temFicheiro && (
                                            <button className="gef-btn-fundir-inline"
                                                onClick={e => { e.stopPropagation(); handleFundir(m); }}
                                                disabled={fundindo}>
                                                {fundindo ? 'A fundir...' : 'Fundir (transferir doc + apagar este)'}
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Encomendas associadas e resumo junto aos dados da fatura */}
                        <div className="gef-ef-search-box">
                            <label className="gef-ef-search-label">Encomendas Fornecedor associadas</label>
                            {associatedEfs.length > 0 && (
                                <div className="gef-ef-assoc-list">
                                    {associatedEfs.map(ae => (
                                        <div key={ae.ef_id} className="gef-ef-assoc-row">
                                            <div className="gef-ef-assoc-info">
                                                <span className="gef-ef-num">{ae.ef_numero}</span>
                                                <span className="gef-ef-info">
                                                    {ae.ef_fornecedor}
                                                    <span className="gef-ef-val"> · EF {fmtMoney(ae.ef_valor_total)}</span>
                                                    {/* consumo global: a EF é faturada às tranches e sem
                                                        isto o valor da fatia parecia ser o da encomenda */}
                                                    {parseFloat(ae.ef_alocado_total) > 0 && parseFloat(ae.ef_valor_total) > 0 && (
                                                        <span className="gef-ef-consumo">
                                                            {' · alocado '}{fmtMoney(ae.ef_alocado_total)}
                                                            {' ('}{Math.round(100 * parseFloat(ae.ef_alocado_total) / parseFloat(ae.ef_valor_total))}%)
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                            <div className="gef-ef-assoc-aloc">
                                                <label>Alocado</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={editingAloc[ae.ef_id] ?? (ae.valor_alocado != null ? parseFloat(ae.valor_alocado).toFixed(2) : '')}
                                                    onChange={e => setEditingAloc(prev => ({ ...prev, [ae.ef_id]: e.target.value }))}
                                                    onBlur={() => handleUpdateAloc(ae.ef_id)}
                                                    disabled={associando}
                                                />
                                                <span>€</span>
                                            </div>
                                            <button
                                                className="gef-ef-assoc-remove"
                                                onClick={() => handleRemoveEf(ae.ef_id)}
                                                disabled={associando}
                                                title="Remover esta EF desta fatura (reversível — basta voltar a associar)"><DeleteIcon /></button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* fundo neutro enquanto não houver encomenda associada: sem nada com
                                que comparar, alertar seria ruído */}
                            <div className={`gef-ef-falta ${associatedEfs.length === 0 || Math.abs(valorEmFalta) <= 0.01 ? 'gef-ef-falta-ok' : 'gef-ef-falta-pend'}`}>
                                {/* Ambos os valores SEM IVA. As encomendas são guardadas em valor
                                    líquido, pelo que comparar o total com IVA da fatura contra a
                                    encomenda daria sempre uma diferença falsa do tamanho do IVA.
                                    O total com IVA fica visível em pequeno por ser o que está
                                    impresso no documento. */}
                                {/* Grelha de 5 colunas para os números ficarem uns por baixo dos
                                    outros: rótulo | base | +IVA | valor do IVA | = | total.
                                    O IVA da EF é calculado à taxa da própria fatura — as encomendas
                                    são guardadas em valor líquido e não trazem taxa própria. */}
                                {/* Cada linha afirma um facto próprio, em vez de duas linhas
                                    com o mesmo número derivado. A fatura vale o que vale, a
                                    encomenda vale o que vale, e a associação é a terceira
                                    quantidade — antes o ecrã dizia "Valor EF: 24.600 €" numa
                                    encomenda de 271.000 €, que é simplesmente falso. */}
                                {/* Duas grelhas independentes, e não uma só: a linha da fatura tem
                                    seis células (base + IVA = total) e as da encomenda duas, e numa
                                    grelha partilhada o rótulo "Encomenda" ia ocupar a célula que
                                    sobrava ao fim da linha da fatura. Aqui cada bloco manda no seu
                                    alinhamento e os valores caem todos na mesma coluna à direita. */}
                                <div className="gef-ef-comparativo">
                                    {/* O IVA da fatura vem do campo IVA (€) acima, e não de uma conta
                                        própria: é o mesmo número que o operador tem à frente, e segue
                                        a Taxa IVA escolhida — o handleTaxaIvaChange recalcula base e
                                        IVA sempre que a taxa muda. */}
                                    <span className="gef-ef-lbl">Fatura</span>
                                    <span className="gef-ef-conta">
                                        {fmtMoney(valorBaseFatura)}
                                        <span className="gef-ef-op"> + </span>
                                        {fmtMoney(ivaFatura)}
                                        <span className="gef-ef-op"> = </span>
                                        {/* a soma da decomposição, e não o total gravado: quando os
                                            dois divergem, mostrar o total aqui produzia uma conta
                                            falsa do género "47,50 + 10,93 = 887,30". O aviso por
                                            baixo trata do caso. */}
                                        <strong>{fmtMoney(somaDecomposta)}</strong>
                                    </span>
                                </div>

                                {associatedEfs.length > 0 && (
                                    <div className="gef-ef-encomenda">
                                        <div className="gef-ef-enc-topo">
                                            <span className="gef-ef-lbl">
                                                {associatedEfs.length === 1
                                                    ? `Encomenda ${associatedEfs[0].ef_numero || ''}`.trim()
                                                    : `Encomendas (${associatedEfs.length})`}
                                            </span>
                                            <strong>{fmtMoney(efValorEncomendas)}</strong>
                                        </div>
                                        <div className="gef-ef-enc-linhas">
                                            <span>Alocado a esta fatura</span>
                                            <span>{fmtMoney(totalAlocado)}</span>

                                            {/* O que outras faturas já consumiram só aparece quando
                                                existe: numa encomenda faturada de uma vez seria uma
                                                linha a zero, a dizer nada. */}
                                            {efJaFaturado - totalAlocado > 0.01 && (<>
                                                <span>Já faturado por outras faturas</span>
                                                <span>{fmtMoney(efJaFaturado - totalAlocado)}</span>
                                            </>)}

                                            <span className="gef-ef-enc-resto">
                                                Por faturar
                                                {efValorEncomendas > 0 && (
                                                    <em> · {Math.round(100 * efJaFaturado / efValorEncomendas)}% da encomenda já faturado</em>
                                                )}
                                            </span>
                                            <span className="gef-ef-enc-resto">{fmtMoney(efPorFaturar)}</span>
                                        </div>
                                    </div>
                                )}

                                {/* Fora da grelha de propósito: dentro dela, este texto longo
                                    ocupava as primeiras colunas e alargava-as, empurrando o "+"
                                    e tudo o que vem a seguir para a direita. */}
                                <div className="gef-ef-conclusao">
                                    {!decomposicaoBate ? (
                                        /* Sem uma base fiável não há comparação possível: dá-la na
                                           mesma seria afirmar um desvio calculado sobre um número
                                           errado. Nos lançamentos novos isto deixou de acontecer —
                                           as taxas todas do QR são lidas e somadas — mas as faturas
                                           antigas guardaram só a taxa normal e continuam assim. */
                                        <span style={{color:'#b45309'}}><LiquidText>
                                            ⚠ A base e o IVA não somam o total da fatura
                                            (</LiquidText>{fmtMoney(valorTotalFatura)}).
                                            {' '}Se a fatura tem mais do que uma taxa, use
                                            {' '}<em>“acrescentar taxa de IVA”</em> acima para as repartir.
                                        </span>
                                    ) : associatedEfs.length === 0 ? (
                                        <span style={{color:'#6b7280'}}>
                                            Sem encomenda associada
                                            {/* Compras sem encomenda (consumo interno, por exemplo):
                                                aqui escreve-se do que se trata. */}
                                            <label style={{display:'flex', alignItems:'center', gap:6, marginTop:6, fontSize:13, color:'#374151', cursor:'pointer'}}
                                                title="Compra sem encomenda a fornecedor, para consumo da empresa">
                                                <input type="checkbox" checked={motivoSemEf === 'Consumo interno'}
                                                    onChange={e => setMotivoSemEf(e.target.checked ? 'Consumo interno' : '')} />
                                                Consumo interno
                                            </label>
                                        </span>
                                    ) : excessoSobreEf > 0.01 ? (
                                        /* As encomendas já não têm por onde cobrir o resto:
                                           a fatura é maior do que elas (falta associar EF). */
                                        <span style={{color:'#b91c1c'}}>
                                            O valor da fatura é superior ao valor {associatedEfs.length > 1 ? 'das EF' : 'da EF'} em
                                            <span className="gef-ef-conclusao-val">{fmtMoney(excessoSobreEf)}</span>
                                            <em style={{display:'block', fontStyle:'normal', fontWeight:400, fontSize:12, color:'#7f1d1d', marginTop:2}}>
                                                {fatura.excesso_ef_antigo
                                                    ? 'Fatura anterior à regra: pode ser gravada assim.'
                                                    : excessoSobreEf <= TOLERANCIA_EXCESSO_EF
                                                    ? 'Diferença até 1 €: a fatura pode ser gravada assim.'
                                                    : 'Associe as EF que faltam: a fatura só se grava quando as EF cobrirem o valor.'}
                                            </em>
                                        </span>
                                    ) : valorEmFalta > 0.01 ? (
                                        /* A encomenda ainda tem por faturar: falta só alocar. */
                                        <span style={{color:'#b91c1c'}}>
                                            A fatura excede o alocado — falta alocar:
                                            <span className="gef-ef-conclusao-val">{fmtMoney(valorEmFalta)}</span>
                                        </span>
                                    ) : valorEmFalta < -0.01 ? (
                                        <span style={{color:'#b91c1c'}}>
                                            Alocado acima da fatura — sobra:
                                            <span className="gef-ef-conclusao-val">{fmtMoney(-valorEmFalta)}</span>
                                        </span>
                                    ) : (
                                        <span style={{color:'#15803d'}}>
                                            <OrcamentoIcon name="check" fallback="✓" /> A fatura está totalmente alocada a esta encomenda
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <aside className="gef-col-associacao" aria-label="Associação de encomendas fornecedor">
                        <div className="gef-ef-search-box">
                            <label className="gef-ef-search-label">Adicionar Encomenda Fornecedor</label>
                            {fatura.id && associatedEfs.length === 0 && !isNC && !fatura.motivo_sem_ef && (
                                <SugestoesEf key={fatura.id + ':' + docRev} faturaId={fatura.id}
                                    alterada={Math.abs((parseFloat(valor) || 0) - (parseFloat(fatura.valor_total) || 0)) > .005 || fornecedor !== (fatura.fornecedor || '') || nif !== (fatura.nif_emissor || extractNifFromAtcud(fatura.atcud) || '') || Math.abs((parseFloat(subtotal) || 0) - initBase) > .005 || taxaIva !== initTaxa || Math.abs((parseFloat(valorIva) || 0) - initIva) > .005 || numDoc !== (fatura.numero_documento_completo || '') || dataDoc !== (fatura.data_documento ? fatura.data_documento.substring(0, 10) : '') || motivoSemEf !== (fatura.motivo_sem_ef || '') || fotoFiles.length > 0}
                                    onAssociada={async () => { await reloadAssociations(); onSaved && onSaved({ keepOpen: true }); }} />
                            )}

                                {associatedEfs.length === 0 && !showCriarEf && !showAddEf && (
                                    <button
                                        title="Criar nova EF para esta fatura"
                                        onClick={() => setShowCriarEf(true)}
                                        style={{marginLeft:'auto',background:'#fef3c7',color:'#92400e',border:'1px solid #fcd34d',borderRadius:5,padding:'2px 10px',fontWeight:700,fontSize:16,cursor:'pointer',lineHeight:1}}>
                                        +
                                    </button>
                                )}

                            {!showAddEf && !showCriarEf && (
                                <button
                                    className="gef-ef-add-btn"
                                    onClick={() => setShowAddEf(true)}>
                                    + Adicionar Encomenda Fornecedor
                                </button>
                            )}

                            {showCriarEf && (
                                <div style={{background:'#f0f9ff',border:'1px solid #bae6fd',borderRadius:8,padding:'12px',marginTop:8,display:'flex',flexDirection:'column',gap:10}}>
                                    <strong style={{fontSize:13,color:'#0369a1'}}>Nova Encomenda Fornecedor</strong>
                                    <div style={{display:'flex',gap:8,alignItems:'flex-end',flexWrap:'wrap'}}>
                                        <div style={{display:'flex',flexDirection:'column',gap:3,flex:'0 0 110px'}}>
                                            <label style={{fontSize:11,fontWeight:700,color:'#555'}}>N.º Orçamento *</label>
                                            <input type="number" value={criarEfOrc}
                                                onChange={e => setCriarEfOrc(e.target.value)}
                                                placeholder="ex: 16491"
                                                autoFocus
                                                style={{padding:'5px 8px',border:'1px solid #cbd5e1',borderRadius:6,fontSize:13,width:'100%'}} />
                                        </div>
                                        <div style={{display:'flex',flexDirection:'column',gap:3,flex:'1 1 160px'}}>
                                            <label style={{fontSize:11,fontWeight:700,color:'#555'}}>Fornecedor *</label>
                                            <input type="text" value={criarEfForn}
                                                onChange={e => setCriarEfForn(e.target.value)}
                                                style={{padding:'5px 8px',border:'1px solid #cbd5e1',borderRadius:6,fontSize:13,width:'100%'}} />
                                        </div>
                                        <div style={{display:'flex',flexDirection:'column',gap:3,flex:'0 0 100px'}}>
                                            <label style={{fontSize:11,fontWeight:700,color:'#555'}}>Valor s/ IVA *</label>
                                            <input type="number" value={criarEfValor} step="0.01"
                                                onChange={e => setCriarEfValor(e.target.value)}
                                                style={{padding:'5px 8px',border:'1px solid #cbd5e1',borderRadius:6,fontSize:13,width:'100%'}} />
                                        </div>
                                        <div style={{display:'flex',flexDirection:'column',gap:3,flex:'1 1 140px'}}>
                                            <label style={{fontSize:11,fontWeight:700,color:'#555'}}>Descrição</label>
                                            <input type="text" value={criarEfDesc}
                                                onChange={e => setCriarEfDesc(e.target.value)}
                                                placeholder="Opcional"
                                                style={{padding:'5px 8px',border:'1px solid #cbd5e1',borderRadius:6,fontSize:13,width:'100%'}} />
                                        </div>
                                    </div>
                                    <div data-liquid-actions="true" style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                                        <button className="btn-gf-clear" onClick={() => { setShowCriarEf(false); setErro(''); }}>Cancelar</button>
                                        <button className="btn-gf-search"
                                            onClick={handleCriarEf}
                                            disabled={criandoEf || !criarEfOrc || !criarEfForn || !criarEfValor}>
                                            {criandoEf ? 'A criar...' : 'Criar e Associar'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {showAddEf && (
                                <>
                                    <input
                                        ref={efSearchRef}
                                        type="text"
                                        className="gef-ef-input"
                                        placeholder="N.º EF, fornecedor ou projeto..."
                                        value={queryEF}
                                        onChange={e => { setQueryEF(e.target.value); setEfsSel(new Set()); }}
                                        autoFocus
                                    />
                                    {resultadosEF.length > 0 && (
                                        <div className="gef-ef-results">
                                            {resultadosEF.map(ef => {
                                                const jaAssociada = associatedEfs.some(ae => ae.ef_id === ef.id);
                                                const marcada = efsSel.has(ef.id);
                                                return (
                                                    <div key={ef.id}
                                                        className={`gef-ef-item${marcada ? ' selected' : ''}${jaAssociada ? ' gef-ef-item-associada' : ''}`}
                                                        onClick={() => {
                                                            if (jaAssociada) return;
                                                            setEfsSel(prev => {
                                                                const next = new Set(prev);
                                                                if (next.has(ef.id)) next.delete(ef.id); else next.add(ef.id);
                                                                return next;
                                                            });
                                                        }}>
                                                        <input type="checkbox" checked={marcada} disabled={jaAssociada}
                                                            readOnly style={{marginRight:6,flexShrink:0}} />
                                                        <span className="gef-ef-num">{ef.numero}</span>
                                                        <span className="gef-ef-info">
                                                            {ef.fornecedor}
                                                            {ef.projeto && <span className="gef-ef-proj"> · {ef.projeto}</span>}
                                                            <span className="gef-ef-val"> · {fmtMoney(ef.valor_total)}</span>
                                                        </span>
                                                        {jaAssociada && (
                                                            <span className="gf-badge badge-yellow">JÁ NESTA FATURA</span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {queryEF.trim().length >= 2 && resultadosEF.length === 0 && (
                                        <p className="gef-ef-vazio">Nenhuma EF encontrada.</p>
                                    )}
                                    <div data-liquid-actions="true" style={{display:'flex',gap:8,alignItems:'center',marginTop:8,flexWrap:'wrap'}}>
                                        <button
                                            className="gef-btn-fundir-inline"
                                            disabled={associando || efsSel.size === 0}
                                            onClick={() => handleAddEfs(resultadosEF.filter(ef => efsSel.has(ef.id)))}>
                                            {associando
                                                ? 'A associar…'
                                                : efsSel.size > 1
                                                ? `+ Associar ${efsSel.size} EF`
                                                : '+ Associar EF'}
                                        </button>
                                        {efsSel.size > 0 && (
                                            <span style={{fontSize:12,color:'#4b5563'}}>
                                                Total: {fmtMoney(resultadosEF.filter(ef => efsSel.has(ef.id))
                                                    .reduce((s, ef) => s + (parseFloat(ef.valor_total) || 0), 0))}
                                            </span>
                                        )}
                                        <button
                                            className="gef-ef-add-cancel"
                                            style={{marginLeft:'auto'}}
                                            onClick={() => { setShowAddEf(false); setQueryEF(''); setEfsSel(new Set()); }}>
                                            Cancelar
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>

                    </aside>
                </div>
                <div className="gef-associacao-rodape">
                    {erro && <div className="gf-save-err" role="alert">{erro}</div>}
                    <div className="gef-form-footer">
                        <button className="btn-gf-clear" onClick={handleClose}>Cancelar</button>
                        <button className="btn-gf-search" onClick={handleSave} disabled={saving}>
                            {saving ? 'A guardar...' : 'Guardar'}
                        </button>
                    </div>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ============================================================
   MODAL LISTAGENS
   ============================================================ */
/* Escolha de fornecedores para as listagens.

   O selector antigo era uma caixa de escolha única: ou um fornecedor, ou
   todos. Não havia como pedir "estes três". Aqui a escolha é um conjunto —
   conjunto vazio quer dizer todos, que é o comportamento por omissão e o mais
   usado. */
/* A caixa de escolha com caixinhas. Serve fornecedores e clientes: é a mesma
   coisa — uma lista comprida de nomes de que se escolhem alguns — e duplicá-la
   era garantir que as duas cópias divergiam à primeira melhoria. */
function SelectorFornecedores({ todos, selecionados, setSelecionados,
                                singular = 'fornecedor', plural = 'fornecedores' }) {
    const [aberto, setAberto] = useState(false);
    const [busca, setBusca] = useState('');
    const caixaRef = useRef(null);

    useEffect(() => {
        if (!aberto) return;
        const fora = (e) => {
            if (caixaRef.current && !caixaRef.current.contains(e.target)) {
                setAberto(false);
                setBusca('');
            }
        };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [aberto]);

    const visiveis = busca
        ? todos.filter(f => f.toLowerCase().includes(busca.toLowerCase()))
        : todos;

    const alternar = (f) => {
        const novo = new Set(selecionados);
        if (novo.has(f)) novo.delete(f); else novo.add(f);
        setSelecionados(novo);
    };

    const rotulo = selecionados.size === 0 ? `Todos os ${plural}`
                 : selecionados.size === 1 ? [...selecionados][0]
                 : `${selecionados.size} ${plural}`;

    return (
        <div className="lst-multi" ref={caixaRef}>
            <button type="button" className={`lst-multi-btn ${selecionados.size ? 'ativo' : ''}`}
                onClick={() => setAberto(a => !a)}
                aria-expanded={aberto} aria-haspopup="true"
                title={selecionados.size > 1 ? [...selecionados].join(', ') : undefined}>
                <span className="lst-multi-rotulo">{rotulo}</span>
                <span className="lst-multi-seta" aria-hidden="true">{aberto ? '▴' : '▾'}</span>
            </button>

            {aberto && (
                <div className="lst-multi-painel">
                    <input className="lst-multi-busca" type="text" autoFocus
                        placeholder={`Procurar ${singular}...`}
                        value={busca} onChange={e => setBusca(e.target.value)} />
                    <div className="lst-multi-acoes">
                        <button type="button" onClick={() => setSelecionados(new Set(visiveis))}>
                            Seleccionar {busca ? 'os encontrados' : 'todos'}
                        </button>
                        <button type="button" onClick={() => setSelecionados(new Set())}>
                            Limpar
                        </button>
                    </div>
                    <div className="lst-multi-lista">
                        {visiveis.length === 0 && <p className="lst-multi-vazio">Nenhum {singular} encontrado.</p>}
                        {visiveis.map(f => (
                            <label key={f} className="lst-multi-item">
                                <input type="checkbox" checked={selecionados.has(f)} onChange={() => alternar(f)} />
                                <span>{f}</span>
                            </label>
                        ))}
                    </div>
                    {selecionados.size === 0 && (
                        <p className="lst-multi-nota">Sem nada escolhido, entram todos os {plural}.</p>
                    )}
                </div>
            )}
        </div>
    );
}

/* Listagem do que foi faturado, por período e por cliente.

   A irmã desta — ModalListagens — lista o que se comprou; esta lista o que se
   vendeu. Partilham o desenho e as classes de estilo de propósito: são a mesma
   pergunta feita aos dois lados do negócio, e quem sabe usar uma sabe usar a
   outra.

   O período vai ao servidor em vez de se filtrar aqui: são ~15 mil orçamentos
   faturados, e trazê-los todos ao browser para deitar fora quase tudo demorava
   mais do que o pedido inteiro. */
function ModalListagensFaturado({ onClose }) {
    const hoje = new Date().toISOString().slice(0, 10);
    const inicioMes = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

    const [dataInicio, setDataInicio] = useState(inicioMes);
    const [dataFim, setDataFim]       = useState(hoje);
    // Conjunto vazio = todos os clientes.
    const [clientesSel, setClientesSel] = useState(() => new Set());
    const [clientes, setClientes]     = useState([]);
    const [lista, setLista]           = useState(null);
    const [loading, setLoading]       = useState(false);
    const [erro, setErro]             = useState('');

    useEffect(() => {
        api.get('/gestao-financeira/faturados/clientes')
            .then(r => setClientes(Array.isArray(r.data) ? r.data : []))
            .catch(() => {});
    }, []);

    const num = (v) => parseFloat(v || 0);
    const margemDe = (o) => num(o.total_venda) - num(o.total_compra);

    const gerarListagem = async () => {
        setLoading(true); setErro('');
        try {
            const r = await api.get('/gestao-financeira/faturados', {
                params: { ano: 'todos', de: dataInicio || undefined, ate: dataFim || undefined },
            });
            let rows = Array.isArray(r.data) ? r.data : [];
            // Conjunto vazio quer dizer todos, como na listagem de fornecedores.
            if (clientesSel.size > 0) rows = rows.filter(o => clientesSel.has(String(o.cliente || '').trim()));
            rows = [...rows].sort((a, b) => String(a.data_fatura || '').localeCompare(String(b.data_fatura || '')));
            setLista(rows);
        } catch (e) { setErro(e.response?.data?.error || e.message); }
        finally { setLoading(false); }
    };

    const totCompra = lista ? lista.reduce((t, o) => t + num(o.total_compra), 0) : 0;
    const totVenda  = lista ? lista.reduce((t, o) => t + num(o.total_venda), 0) : 0;
    const totMargem = totVenda - totCompra;

    const exportarCSV = () => {
        if (!lista || lista.length === 0) return;
        const sep = ';';
        const fmt = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const fmtN = v => String(num(v).toFixed(2)).replace('.', ',');
        const fmtD = v => v ? new Date(v).toLocaleDateString('pt-PT') : '';

        const headers = ['ID', 'Data Orçamento', 'Cliente', 'Projeto', 'N.º Fatura', 'Data Fatura',
                         'Total Compra', 'Total Venda', 'Margem', 'Margem %'];
        const linhas = lista.map(o => {
            const m = margemDe(o);
            /* Margem sobre a compra, como no ecrã. Sem compra não há
               percentagem: dividir por zero não diz nada a ninguém. */
            const pct = num(o.total_compra) > 0 ? (m / num(o.total_compra)) * 100 : null;
            return [
                fmt(`#${o.id}`),
                fmt(fmtD(o.data)),
                fmt(o.cliente || ''),
                fmt(o.projeto || ''),
                fmt(o.numero_fatura || ''),
                fmt(fmtD(o.data_fatura)),
                fmtN(o.total_compra),
                fmtN(o.total_venda),
                fmtN(m),
                pct == null ? '' : String(pct.toFixed(1)).replace('.', ','),
            ].join(sep);
        });
        const pctTot = totCompra > 0 ? (totMargem / totCompra) * 100 : null;
        const totRow = [fmt('TOTAIS'), '', '', fmt(`${lista.length} registos`), '', '',
                        fmtN(totCompra), fmtN(totVenda), fmtN(totMargem),
                        pctTot == null ? '' : String(pctTot.toFixed(1)).replace('.', ',')].join(sep);

        const bom = '\uFEFF';
        const csv = bom + [headers.map(fmt).join(sep), ...linhas, '', totRow].join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href = url;
        /* O nome diz o período: duas listagens na pasta das transferências têm
           de se distinguir sem se abrirem. */
        a.download = `faturado-${dataInicio || 'inicio'}-a-${dataFim || 'hoje'}.csv`;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a); URL.revokeObjectURL(url);
    };

    return (
        <LiquidDialog onClose={onClose} className="gf-modal-overlay" onClick={onClose}>
            <div className="gf-modal lst-modal" onClick={e => e.stopPropagation()}>
                <div className="gf-modal-header">
                    <h3>Listagens — Faturado</h3>
                    <button className="gf-modal-close" onClick={onClose}><OrcamentoIcon name="close" fallback="×" /></button>
                </div>
                <div className="gf-modal-body">
                    <div className="lst-filters">
                        <div className="lst-filter-group">
                            <label className="lst-label">Período (data da fatura)</label>
                            <div className="lst-date-row">
                                <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} className="lst-date-input" />
                                <span className="gf-dash">–</span>
                                <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} className="lst-date-input" />
                            </div>
                        </div>
                        <div className="lst-filter-group">
                            <label className="lst-label">Cliente</label>
                            <SelectorFornecedores todos={clientes} singular="cliente" plural="clientes"
                                selecionados={clientesSel} setSelecionados={setClientesSel} />
                        </div>
                        <button className="lst-btn-gerar" onClick={gerarListagem} disabled={loading}>
                            {loading ? 'A gerar...' : 'Gerar Listagem'}
                        </button>
                    </div>

                    {erro && <p className="lst-empty" style={{ color: '#b91c1c' }}>{erro}</p>}

                    {lista !== null && (
                        <>
                            <div className="lst-summary">
                                <span><strong>{lista.length}</strong> registos</span>
                                <span>Compra: <strong>{fmtMoney(totCompra)}</strong></span>
                                <span>Venda: <strong>{fmtMoney(totVenda)}</strong></span>
                                <span>Margem: <strong>{fmtMoney(totMargem)}</strong></span>
                                <button className="lst-btn-excel" onClick={exportarCSV} disabled={lista.length === 0} title="Exportar para Excel (CSV)">
                                    <OrcamentoIcon name="chart" fallback="📊" /> Excel
                                </button>
                            </div>
                            {lista.length === 0 ? (
                                <p className="lst-empty">Nenhum registo encontrado com os filtros seleccionados.</p>
                            ) : (
                                <div className="lst-table-wrap">
                                    <table className="gf-table lst-table">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Data Orç.</th>
                                                <th>Cliente</th>
                                                <th>Projeto</th>
                                                <th>N.º Fatura</th>
                                                <th>Data Fatura</th>
                                                <th className="tr">T. Compra</th>
                                                <th className="tr">T. Venda</th>
                                                <th className="tr">Margem</th>
                                                <th className="tr">Margem %</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {lista.map(o => {
                                                const m = margemDe(o);
                                                const pct = num(o.total_compra) > 0 ? (m / num(o.total_compra)) * 100 : null;
                                                return (
                                                    <tr key={o.id}>
                                                        <td>#{o.id}</td>
                                                        <td>{o.data ? new Date(o.data).toLocaleDateString('pt-PT') : ''}</td>
                                                        <td>{o.cliente}</td>
                                                        <td>{o.projeto}</td>
                                                        <td>{o.numero_fatura || ''}</td>
                                                        <td>{o.data_fatura ? new Date(o.data_fatura).toLocaleDateString('pt-PT') : ''}</td>
                                                        <td className="tr">{fmtMoney(o.total_compra)}</td>
                                                        <td className="tr">{fmtMoney(o.total_venda)}</td>
                                                        <td className="tr" style={{ color: m >= 0 ? '#166534' : '#b91c1c', fontWeight: 700 }}>
                                                            {fmtMoney(m)}
                                                        </td>
                                                        <td className="tr">{pct == null ? '' : `${pct.toFixed(1).replace('.', ',')}%`}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </LiquidDialog>
    );
}

function ModalListagens({ onClose }) {
    const liquidFeedback = useLiquidFeedback();
    const hoje = new Date().toISOString().slice(0, 10);
    const inicioMes = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

    const [dataInicio, setDataInicio] = useState(inicioMes);
    const [dataFim, setDataFim]       = useState(hoje);
    // 'data_documento' | 'criado_em' | 'data_pagamento'
    const [campData, setCampData]     = useState('data_documento');
    // Conjunto vazio = todos os fornecedores.
    const [fornecSel, setFornecSel]   = useState(() => new Set());
    const [estadoPag, setEstadoPag]   = useState('todos'); // 'todos' | 'pagos' | 'nao_pagos'
    const [lista, setLista]           = useState(null);
    const [loading, setLoading]       = useState(false);

    // Lista de fornecedores únicos (carregada uma vez)
    const [fornecedores, setFornecedores] = useState([]);
    useEffect(() => {
        api.get('/gestao-financeira/faturas-reais').then(r => {
            const uniq = [...new Set(r.data.map(f => f.fornecedor).filter(Boolean))].sort((a,b) => a.localeCompare(b, 'pt'));
            setFornecedores(uniq);
        }).catch(() => {});
    }, []);

    /* A data por que se lista. São três campos diferentes da mesma fatura, e a
       escolha muda o significado da listagem: quando entrou, quando foi
       emitida, ou quando foi paga. */
    const dataDe = (f) => ((campData === 'data_documento' ? f.data_documento
                          : campData === 'criado_em'      ? f.criado_em
                          :                                 f.data_pagamento) || '').slice(0, 10);

    const gerarListagem = async () => {
        setLoading(true);
        try {
            const r = await api.get('/gestao-financeira/faturas-reais');
            let rows = r.data;

            // Filtro data
            if (dataInicio || dataFim) {
                rows = rows.filter(f => {
                    const ds = dataDe(f);
                    // Sem data no campo escolhido, a fatura não pertence a dia
                    // nenhum. É o que exclui as faturas por pagar quando se
                    // lista por data de pagamento.
                    if (!ds) return false;
                    if (dataInicio && ds < dataInicio) return false;
                    if (dataFim   && ds > dataFim)   return false;
                    return true;
                });
            } else if (campData === 'data_pagamento') {
                rows = rows.filter(f => dataDe(f));
            }
            // Filtro fornecedor — conjunto vazio quer dizer todos
            if (fornecSel.size > 0) rows = rows.filter(f => fornecSel.has(f.fornecedor));
            // Filtro estado pagamento
            if (estadoPag === 'pagos')    rows = rows.filter(f => f.pago);
            if (estadoPag === 'nao_pagos') rows = rows.filter(f => !f.pago);

            rows = [...rows].sort((a, b) => dataDe(a).localeCompare(dataDe(b)));
            setLista(rows);
        } catch { liquidFeedback.notify('Erro ao carregar dados'); }
        finally { setLoading(false); }
    };

    const exportarCSV = () => {
        if (!lista || lista.length === 0) return;
        const sep = ';';
        const fmt = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const fmtN = v => String(parseFloat(v || 0).toFixed(2)).replace('.', ',');
        const fmtD = v => v ? new Date(v).toLocaleDateString('pt-PT') : '';

        const headers = ['Data Fatura', 'Data Entrada', 'Fornecedor', 'NIF', 'N.º Documento', 'Base S/ IVA', 'IVA', 'Total C/ IVA', 'Estado', 'Pago Em', 'Em Falta'];
        const csvRows = lista.map(f => {
            const st = pagoSt(f);
            const base = f.subtotal ? parseFloat(f.subtotal) : parseFloat(f.valor_total || 0) / (1 + (parseFloat(f.taxa_iva ?? 23) / 100));
            const iva  = parseFloat(f.valor_total || 0) - base;
            const emFalta = st === 'parc-pago' ? parseFloat(f.valor_total || 0) - parseFloat(f.valor_pago || 0)
                          : st === 'nao-pago'  ? parseFloat(f.valor_total || 0) : 0;
            return [
                fmt(fmtD(f.data_documento)),
                fmt(fmtD(f.criado_em)),
                fmt(f.fornecedor || ''),
                fmt(f.nif_emissor || ''),
                fmt(f.numero_documento_completo || ''),
                fmtN(base),
                fmtN(iva),
                fmtN(f.valor_total),
                fmt(pagoLabel(f)),
                fmt(f.pago && f.data_pagamento ? fmtD(f.data_pagamento) : ''),
                emFalta > 0.005 ? fmtN(emFalta) : '',
            ].join(sep);
        });

        // Linha de totais
        const tSemIva = lista.reduce((s, f) => s + (f.subtotal ? parseFloat(f.subtotal) : parseFloat(f.valor_total||0)/(1+(parseFloat(f.taxa_iva??23)/100))), 0);
        const tComIva = lista.reduce((s, f) => s + parseFloat(f.valor_total||0), 0);
        const tFalta  = lista.reduce((s, f) => {
            const st = pagoSt(f);
            if (st === 'parc-pago') return s + parseFloat(f.valor_total||0) - parseFloat(f.valor_pago||0);
            if (st === 'nao-pago')  return s + parseFloat(f.valor_total||0);
            return s;
        }, 0);
        const totRow = [fmt('TOTAIS'), '', '', '', fmt(`${lista.length} faturas`), fmtN(tSemIva), fmtN(tComIva-tSemIva), fmtN(tComIva), '', '', tFalta > 0.005 ? fmtN(tFalta) : ''].join(sep);

        const bom = '\uFEFF';
        const csv = bom + [headers.map(fmt).join(sep), ...csvRows, '', totRow].join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        const hoje = new Date().toISOString().slice(0, 10);
        // O nome do ficheiro diz o que lá está dentro: uma listagem de um dia de
        // pagamentos não se deve chamar o mesmo que uma listagem do mês.
        a.href = url;
        a.download = campData === 'data_pagamento' && dataInicio && dataInicio === dataFim
            ? `faturas-pagas-${dataInicio}.csv`
            : `faturas-fornecedor-${hoje}.csv`;
        a.click(); URL.revokeObjectURL(url);
    };

    const pagoSt = (f) => {
        if (!f.pago) return 'nao-pago';
        if (f.valor_pago != null && parseFloat(f.valor_pago) < parseFloat(f.valor_total || 0) - 0.005) return 'parc-pago';
        return 'pago';
    };
    const pagoLabel = (f) => ({ 'pago': 'Pago', 'parc-pago': 'Parc. Pago', 'nao-pago': 'Não pago' }[pagoSt(f)]);

    const totalSemIva  = lista ? lista.reduce((s, f) => s + (parseFloat(f.subtotal || 0) || parseFloat(f.valor_total || 0) / (1 + (parseFloat(f.taxa_iva ?? 23) / 100))), 0) : 0;
    const totalComIva  = lista ? lista.reduce((s, f) => s + parseFloat(f.valor_total || 0), 0) : 0;
    const totalEmFalta = lista ? lista.reduce((s, f) => {
        if (pagoSt(f) === 'parc-pago') return s + parseFloat(f.valor_total || 0) - parseFloat(f.valor_pago || 0);
        if (pagoSt(f) === 'nao-pago')  return s + parseFloat(f.valor_total || 0);
        return s;
    }, 0) : 0;

    return (
        <LiquidDialog onClose={onClose} className="gf-modal-overlay" onClick={onClose}>
            <div className="gf-modal lst-modal" onClick={e => e.stopPropagation()}>
                <div className="gf-modal-header">
                    <h3>Listagens — Faturas Fornecedor</h3>
                    <button className="gf-modal-close" onClick={onClose}><OrcamentoIcon name="close" fallback="×" /></button>
                </div>
                <div className="gf-modal-body">
                    {/* Filtros */}
                    <div className="lst-filters">
                        <div className="lst-filter-group">
                            <label className="lst-label">Tipo de Data</label>
                            <div className="lst-btn-group">
                                <button className={`lst-btn ${campData === 'data_documento' ? 'active' : ''}`} onClick={() => setCampData('data_documento')}>Data Fatura</button>
                                <button className={`lst-btn ${campData === 'criado_em' ? 'active' : ''}`} onClick={() => setCampData('criado_em')}>Data Entrada</button>
                                <button className={`lst-btn ${campData === 'data_pagamento' ? 'active' : ''}`}
                                    title="Lista as faturas pelo dia em que foram pagas"
                                    onClick={() => {
                                        setCampData('data_pagamento');
                                        // Por pagar e com data de pagamento é uma
                                        // combinação que não existe; sair dela.
                                        if (estadoPag === 'nao_pagos') setEstadoPag('todos');
                                    }}>Data Pagamento</button>
                            </div>
                        </div>
                        <div className="lst-filter-group">
                            <label className="lst-label">Período</label>
                            <div className="lst-date-row">
                                <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} className="lst-date-input" />
                                <span className="gf-dash">–</span>
                                <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} className="lst-date-input" />
                            </div>
                        </div>
                        <div className="lst-filter-group">
                            <label className="lst-label">Fornecedor</label>
                            <SelectorFornecedores todos={fornecedores}
                                selecionados={fornecSel} setSelecionados={setFornecSel} />
                        </div>
                        <div className="lst-filter-group">
                            <label className="lst-label">Estado Pagamento</label>
                            <div className="lst-btn-group">
                                <button className={`lst-btn ${estadoPag === 'todos' ? 'active' : ''}`}     onClick={() => setEstadoPag('todos')}>Todos</button>
                                <button className={`lst-btn ${estadoPag === 'pagos' ? 'active' : ''}`}     onClick={() => setEstadoPag('pagos')}>Pagos (incl. Parc.)</button>
                                <button className={`lst-btn ${estadoPag === 'nao_pagos' ? 'active' : ''}`}
                                    disabled={campData === 'data_pagamento'}
                                    title={campData === 'data_pagamento' ? 'Uma fatura por pagar não tem data de pagamento' : undefined}
                                    onClick={() => setEstadoPag('nao_pagos')}>Não pagos</button>
                            </div>
                        </div>
                        <button className="lst-btn-gerar" onClick={gerarListagem} disabled={loading}>
                            {loading ? 'A gerar...' : 'Gerar Listagem'}
                        </button>
                    </div>

                    {/* Tabela resultados */}
                    {lista !== null && (
                        <>
                            <div className="lst-summary">
                                <span><strong>{lista.length}</strong> faturas</span>
                                <span>S/ IVA: <strong>{fmtMoney(totalSemIva)}</strong></span>
                                <span>C/ IVA: <strong>{fmtMoney(totalComIva)}</strong></span>
                                {totalEmFalta > 0.005 && <span className="lst-em-falta-total">Em falta: <strong>{fmtMoney(totalEmFalta)}</strong></span>}
                                <button className="lst-btn-excel" onClick={exportarCSV} disabled={lista.length === 0} title="Exportar para Excel (CSV)">
                                    <OrcamentoIcon name="chart" fallback="📊" /> Excel
                                </button>
                            </div>
                            {lista.length === 0 ? (
                                <p className="lst-empty">Nenhum registo encontrado com os filtros seleccionados.</p>
                            ) : (
                                <div className="lst-table-wrap">
                                    <table className="gf-table lst-table">
                                        <thead>
                                            <tr>
                                                <th>Data Fatura</th>
                                                <th>Data Entrada</th>
                                                <th>Fornecedor</th>
                                                <th>N.º Documento</th>
                                                <th className="tr">Base S/ IVA</th>
                                                <th className="tr">IVA</th>
                                                <th className="tr">Total C/ IVA</th>
                                                <th className="tc">Estado</th>
                                                <th>Pago Em</th>
                                                <th className="tr">Em Falta</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {lista.map(f => {
                                                const st = pagoSt(f);
                                                const base = f.subtotal ? parseFloat(f.subtotal) : parseFloat(f.valor_total || 0) / (1 + (parseFloat(f.taxa_iva ?? 23) / 100));
                                                const iva  = parseFloat(f.valor_total || 0) - base;
                                                const emFalta = st === 'parc-pago' ? parseFloat(f.valor_total || 0) - parseFloat(f.valor_pago || 0)
                                                              : st === 'nao-pago'  ? parseFloat(f.valor_total || 0) : 0;
                                                return (
                                                    <tr key={f.id} className={st === 'pago' ? 'gf-row-pago' : st === 'parc-pago' ? 'gf-row-parc-pago' : ''}>
                                                        <td className="gf-cell-date">{f.data_documento ? new Date(f.data_documento).toLocaleDateString('pt-PT') : '—'}</td>
                                                        <td className="gf-cell-date">{f.criado_em ? new Date(f.criado_em).toLocaleDateString('pt-PT') : '—'}</td>
                                                        <td>
                                                            <span className="gf-fornecedor-nome" style={{fontSize:12}}>{f.fornecedor || '—'}</span>
                                                            {f.nif_emissor && <span className="gf-nif-tag">NIF: {f.nif_emissor}</span>}
                                                        </td>
                                                        <td className="gf-cell-doc">{f.numero_documento_completo || '—'}</td>
                                                        <td className="tr gf-cell-valor">{fmtMoney(base)}</td>
                                                        <td className="tr" style={{fontSize:12, color:'#64748b'}}>{fmtMoney(iva)}</td>
                                                        <td className="tr gf-cell-valor">{fmtMoney(f.valor_total)}</td>
                                                        <td className="tc">
                                                            <span className={`btn-pago btn-pago-fixed ${st}`} style={{cursor:'default'}}>{pagoLabel(f)}</span>
                                                        </td>
                                                        <td className="gf-cell-date">{f.pago && f.data_pagamento ? new Date(f.data_pagamento).toLocaleDateString('pt-PT') : ''}</td>
                                                        <td className="tr">
                                                            {emFalta > 0.005 && <span className="gf-em-falta parc">{fmtMoney(emFalta)}</span>}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot>
                                            <tr className="lst-tfoot">
                                                <td colSpan={4}><strong>TOTAIS ({lista.length} faturas)</strong></td>
                                                <td className="tr"><strong>{fmtMoney(totalSemIva)}</strong></td>
                                                <td className="tr"><strong>{fmtMoney(totalComIva - totalSemIva)}</strong></td>
                                                <td className="tr"><strong>{fmtMoney(totalComIva)}</strong></td>
                                                <td colSpan={2}></td>
                                                <td className="tr">{totalEmFalta > 0.005 && <strong className="lst-em-falta-total">{fmtMoney(totalEmFalta)}</strong>}</td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ============================================================
   PÁGINA PRINCIPAL
   ============================================================ */
export default function GestaoFinanceira() {
    const [rascunhoId, setRascunhoId] = useState(null);
    const [rascunhosRefresh, setRascunhosRefresh] = useState(0);
    const abrirRascunho = id => { setTab('faturacao'); setRascunhoId(id); setRascunhosRefresh(v => v + 1); };
    const [tab, setTab] = useState(() => localStorage.getItem('gf_tab') || 'scanner');
    useEffect(() => { localStorage.setItem('gf_tab', tab); }, [tab]);
    const [porFaturar, setPorFaturar] = useState(null);
    useEffect(() => {
        if (tab === 'faturar') return;
        let active = true;
        const refresh = () => api.get('/gestao-financeira/para-faturar')
            .then(r => { if (active && Array.isArray(r.data)) setPorFaturar(r.data.length); })
            .catch(() => {});
        const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
        refresh();
        window.addEventListener('focus', refresh);
        document.addEventListener('visibilitychange', onVisible);
        return () => { active = false; window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', onVisible); };
    }, [tab]);
    const [showListagens, setShowListagens] = useState(false);
    const [showListagensFat, setShowListagensFat] = useState(false);

    return (
        <div className="gf-page">
            <div className="top-bar">
                <div className="top-bar-left">
                    <div className="top-bar-title">
                        <h1><OrcamentoIcon name="finance" fallback="🏦" /> Gestão Financeira</h1>
                        <p>Leitura de faturas, orçamentos para faturar e encomendas a fornecedor</p>
                    </div>
                </div>
            </div>

            <div className="gf-body">
                {/* Sub-navbar */}
                <div className="gf-nav-row">
                    <div className="shared-subnav gf-subnav">
                        <div className="gf-subnav-left">
                            {TABS_GF_LEFT.map(t => (
                                <button key={t.key}
                                    className={`shared-subnav-btn ${tab === t.key ? 'active' : ''}`}
                                    onClick={() => setTab(t.key)}>
                                    <LiquidText>{t.label}</LiquidText>
                                </button>
                            ))}
                        </div>
                        <div className="gf-subnav-right">
                            {TABS_GF_RIGHT.map(t => (
                                <button key={t.key}
                                    className={`shared-subnav-btn gf-tab-faturar ${tab === t.key ? 'active' : ''}`}
                                    onClick={() => setTab(t.key)}>
                                    <LiquidText>{t.label}</LiquidText>
                                    {t.key === 'faturar' && porFaturar > 0 && <span className="gf-pending-count" title={`${porFaturar} orçamentos por faturar`} aria-label={`${porFaturar} por faturar`}>{porFaturar}</span>}
                                </button>
                            ))}
                        </div>
                    </div>
                    <button className="btn-listagens-action" onClick={() => setShowListagens(true)}>
                        Listagens Fornecedor
                    </button>
                    <button className="btn-listagens-action" onClick={() => setShowListagensFat(true)}>
                        Listagens Faturado
                    </button>
                </div>

                <div className="gf-content">
                    {tab === 'scanner'   && <TabScanner onAbrirFaturas={() => setTab('fat_forn')} />}
                    {tab === 'faturar'   && <TabParaFaturar onCountChange={setPorFaturar} onRascunho={abrirRascunho} />}
                    {tab === 'faturados' && <TabFaturados />}
                    {tab === 'faturacao' && <FaturacaoModulo onAbrir={setRascunhoId} onCriar={() => setTab('faturar')} refresh={rascunhosRefresh} />}
                    {tab === 'enc_forn'  && <TabEncomendasFornecedor />}
                    {tab === 'fat_forn'  && <TabFaturasFornecedor />}
                </div>
            </div>

            {rascunhoId && <EditorRascunho key={rascunhoId} id={rascunhoId} onClose={() => setRascunhoId(null)} onSaved={() => setRascunhosRefresh(v => v + 1)} />}
            {showListagens && <ModalListagens onClose={() => setShowListagens(false)} />}
            {showListagensFat && <ModalListagensFaturado onClose={() => setShowListagensFat(false)} />}
        </div>
    );
}
